import { getApiConfig } from "../config";
import {
  listOwnLedgerCashFlows,
  loadOwnLedgerCashMonthLedger,
  invalidateOwnLedgerGetCache,
  loadOwnLedgerImportStatus,
  ownLedgerReadRevision,
  loadOwnLedgerStockOpenSummary,
  loadOwnLedgerTransactionSnapshot,
  syncForecastTaxCashFlows,
  type OwnCapitalMonthCash,
  type OwnLedgerCashFlow,
} from "./ownLedgerClient";
import {
  accountScopeGeneration,
  paperAccountsEnabled,
  scopeAllowsAccount,
  scopeAllowsTicker,
  subscribeAccountScope,
} from "./accountScope";
import { dataTipParagraphs } from "../shared/formatDataTip";
import {
  fetchMarginCostMonthSummary,
  type MarginCostMonthSummary,
} from "../api/marginCostHistoryClient";
import { proxyFetch } from "../api/proxyFetch";
import {
  getLegacyAntonCutoffDate,
  getLegacyAntonStockSummary,
  getLegacyAntonTransactionRecords,
} from "../data/legacyAnton";
import { contentDialog } from "./confirmDialog";
import { syncResetButtons } from "./filterReset";
import {
  bindDetailsCollapsiblePersistence,
  isCollapsibleOpen,
  migrateLegacyCollapsedFlag,
  setCollapsibleOpen,
} from "./collapsibleSections";
import { buildStockLegAvgCostDetailIndex } from "./historyRelated";
import {
  historyAccountLabel,
  historySystemLabel,
  type HistoryLedgerMetaTxn,
} from "./historyLabels";
import {
  accountShortCellHtml,
  accountShortInnerHtml,
  brokerMarkCellHtml,
  brokerMarkInnerHtml,
  ledgerRowMark,
  type LedgerRowMarkSource,
} from "./ledgerMarks";
import {
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  loadTableColumns,
  renderTableColgroup,
  renderTableHeadRow,
  tableColumnTitle,
  visibleTableColumns,
  type ColumnState,
  type TableColumnId,
} from "./tableColumns";
import {
  cashFlowActionLabel,
  cashFlowCategoryLabel,
  cashFlowDisplayTimestamp,
  cashFlowToHistoryRow,
  isFactCashFlow,
} from "./historyCashFlows";
import { plannedOwnCapitalEom } from "./monthLedger";
import { showToast } from "./toast";
import { WHEEL_LEDGER_CHANGED_EVENT } from "./pageBridge";
import {
  ensureTxnNoteClickBinding,
  ledgerAttachedNote,
  renderTxnNoteBadge,
} from "./txnNote";
import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "../shared/tickerFilterInput";
import { isUserActiveInPanel, withPreservedPanelView } from "./preservePanelView";
import {
  buildForecastTaxCashFlowRows,
  estimatedTaxPaidForMonthKey,
  mergePredictedTaxWithdrawals,
  taxCashOutflowForMonthKey,
  TAX_PAYMENT_MONTH_FOCUS_EVENT,
  TAX_SETTINGS_CHANGED_EVENT,
  type ForecastOwnCapitalTaxPlan,
  type TaxPaymentMonthFocusDetail,
} from "./taxEstimatedPayments";
import {
  incomeChartSectionTip,
  mountIncomeByMonthChart,
  type IncomeChartMonthPoint,
} from "./analyticsIncomeChart";

const CASH_ADDED_KPI_ID = "sfe-cash-added-kpi";
const CASH_WITHDRAWN_KPI_ID = "sfe-cash-withdrawn-kpi";
const OWN_CAPITAL_KPI_ID = "sfe-own-capital-kpi";
const EXPENSES_KPI_ID = "sfe-expenses-kpi";
const TAXES_KPI_ID = "sfe-taxes-kpi";
const TOTAL_INCOME_KPI_ID = "sfe-total-income-kpi";
const TOTAL_INCOME_KPI_CLASS = "sfe-total-income-kpi";
const STOCK_HOLDINGS_KPI_ID = "sfe-stock-holdings-kpi";
const STOCK_COST_BASIS_KPI_ID = "sfe-stock-cost-basis-kpi";
const STOCK_MARKET_VALUE_KPI_ID = "sfe-stock-market-value-kpi";
const STOCK_PL_KPI_ID = "sfe-stock-pl-kpi";
const MARGIN_USED_KPI_ID = "sfe-margin-used-kpi";
const TOTAL_PERFORMANCE_SECTION_SEL = ".sfe-total-performance";
const TOTAL_PERFORMANCE_TIP = dataTipParagraphs(
  "Cumulative through the selected month (All = up to the current month): income components, own capital (EOM), cash in/out, expenses, open stock P/L, and margin.",
  "Same through-month window as Cash and Own Capital — not the selected month alone. Year chips only change the monthly section below.",
);
/** Annual rate for predicted margin financing cost in the Margin used KPI. */
const MARGIN_APR = 0.045;

/** Robinhood daily-interest summaries keyed by YYYY-MM. */
const marginCostHistoryByMonth = new Map<string, MarginCostMonthSummary>();

/** Legacy in-memory map for non–cash-ledger Analytics+ forecast (2027+). */
let predictedTaxWithdrawalsCache = new Map<string, number>();

let forecastTaxRefreshInFlight: Promise<void> | null = null;

/**
 * Refresh 2027+ tax forecast. Analytics(L) writes rows to cash_flows; classic
 * Analytics+ keeps the in-memory withdrawal map from the Tax payment log.
 */
async function refreshForecastTaxPlans(opts: { syncToCashLedger?: boolean } = {}): Promise<void> {
  if (forecastTaxRefreshInFlight) return forecastTaxRefreshInFlight;
  forecastTaxRefreshInFlight = (async () => {
    try {
      const taxMod = (await import("./tax")) as {
        loadForecastOwnCapitalTaxPlan: (
          year: number,
        ) => Promise<ForecastOwnCapitalTaxPlan | null>;
        getTaxYears: () => number[];
      };
      const plans = new Map<number, ForecastOwnCapitalTaxPlan>();
      const years = taxMod.getTaxYears().filter((y) => y >= 2027);
      await Promise.all(
        years.map(async (year) => {
          const plan = await taxMod.loadForecastOwnCapitalTaxPlan(year);
          if (plan && (plan.inYearNeed > 0 || plan.priorYearBalance > 0)) {
            plans.set(year, plan);
          }
        }),
      );
      if (opts.syncToCashLedger) {
        const rows = buildForecastTaxCashFlowRows(plans);
        await syncForecastTaxCashFlows(rows);
        predictedTaxWithdrawalsCache = new Map();
      } else {
        predictedTaxWithdrawalsCache = mergePredictedTaxWithdrawals(plans);
      }
    } catch {
      predictedTaxWithdrawalsCache = new Map();
    } finally {
      forecastTaxRefreshInFlight = null;
    }
  })();
  return forecastTaxRefreshInFlight;
}

function cachedMarginCostSummary(monthKey: string): MarginCostMonthSummary | null {
  return marginCostHistoryByMonth.get(monthKey) ?? null;
}

function storeMarginCostSummary(summary: MarginCostMonthSummary | null): void {
  if (summary?.monthKey) marginCostHistoryByMonth.set(summary.monthKey, summary);
}

function predictedMarginCostAtApr(marginUsed: number, apr = MARGIN_APR): number {
  return Math.max(0, marginUsed) * (apr / 12);
}

/** Same predicted monthly margin as the Margin used KPI (`cost basis − own capital EOM`). */
function predictedMarginCostFromOwnCapital(
  stockCostBasis: number,
  ownCapitalEom: number,
): number {
  return predictedMarginCostAtApr(stockCostBasis - ownCapitalEom);
}

/** Robinhood accrued margin interest through elapsed days in the month. */
function rhAccruedMarginCostForMonth(monthKey: string | null | undefined): number | null {
  if (!monthKey) return null;
  const summary = cachedMarginCostSummary(monthKey);
  if (!summary?.days.length) return null;
  return Math.max(0, summary.accrued);
}

/** Prefer Robinhood accrued+projected daily interest when history is loaded. */
function rhPredictedMarginCostForMonth(monthKey: string | null | undefined): number | null {
  if (!monthKey) return null;
  const summary = cachedMarginCostSummary(monthKey);
  if (!summary?.days.length) return null;
  return Math.max(0, summary.predictedMonthCost);
}

function predictedMarginCostForMonth(
  monthKey: string | null | undefined,
  stockCostBasis: number,
  ownCapitalEom: number,
): number {
  const rh = rhPredictedMarginCostForMonth(monthKey);
  if (rh != null) return rh;
  return predictedMarginCostFromOwnCapital(stockCostBasis, ownCapitalEom);
}

function daysInMonthKey(monthKey: string): number {
  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month) return 30;
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Full-month cost at the open month's latest daily Robinhood rate.
 *  Current and later months share this figure. Paper books do not. */
function forecastMarginCostFromCurrentDaily(): number | null {
  if (paperAccountsEnabled()) return null;
  const summary = cachedMarginCostSummary(currentMonthKey());
  if (!summary?.days.length) return null;
  const daily = Number(summary.currentDailyInterest);
  if (!Number.isFinite(daily)) return null;
  const dim = summary.daysInMonth > 0 ? summary.daysInMonth : daysInMonthKey(summary.monthKey);
  return Math.round(Math.max(0, daily) * dim * 100) / 100;
}

function monthUsesAutomaticMarginForecast(monthKey: string): boolean {
  return !isPastAnalyticsMonthKey(monthKey) && forecastMarginCostFromCurrentDaily() != null;
}

/** Own-ledger Analytics. Closed months stay on booked interest. The open month
 *  and every later month use the current daily rate for a full month. */
function localLedgerMarginCostForMonth(
  monthKey: string,
  entry: Pick<OwnCapitalEntry, "marginCost" | "usePredictedMarginCost">,
  stockCostBasis: number,
  ownCapitalEom: number,
  timeline?: Map<string, OwnCapitalPoint>,
  forecastMargin = true,
): number {
  if (monthUsesAutomaticMarginForecast(monthKey)) {
    return forecastMarginCostFromCurrentDaily() ?? 0;
  }
  // Paper and other books without a daily rate: closed months use interest
  // already booked. The open month forecasts from cost basis versus own capital
  // only while the forecast view is on.
  if (!forecastMargin && !isPastAnalyticsMonthKey(monthKey)) {
    return Number(entry.marginCost) || 0;
  }
  if (isPastAnalyticsMonthKey(monthKey)) {
    return Number(entry.marginCost) || 0;
  }
  const cm = currentMonthKey();
  if (monthKey > cm) {
    if (entry.usePredictedMarginCost === false) return Number(entry.marginCost) || 0;
    const current = timeline?.get(cm);
    if (current && Number.isFinite(current.effectiveMarginCost)) {
      return current.effectiveMarginCost ?? 0;
    }
    return predictedMarginCostFromOwnCapital(stockCostBasis, current?.ownCapital ?? 0);
  }
  if (entry.usePredictedMarginCost === false) {
    return Number(entry.marginCost) || 0;
  }
  return predictedMarginCostFromOwnCapital(stockCostBasis, ownCapitalEom);
}

/** Manual ledger margin, or RH accrued interest for elapsed days when history exists. */
function factualMarginCostForMonth(
  entry: Pick<OwnCapitalEntry, "marginCost">,
  monthKey: string,
): number {
  const rh = rhAccruedMarginCostForMonth(monthKey);
  if (rh != null) return rh;
  return Number(entry.marginCost) || 0;
}

function hasRhMarginCostHistory(monthKey?: string | null): boolean {
  const key = monthKey ?? currentMonthKey();
  return rhPredictedMarginCostForMonth(key) != null;
}

function ledgerUsesPredictedMarginCost(ledger: Record<string, OwnCapitalEntry>): boolean {
  return Object.values(ledger).some((entry) => entry?.usePredictedMarginCost === true);
}

/** Margin cost shown in month Cost card when Use Forecast is on — matches Margin used KPI. */
function displayEffectiveMarginCost(
  entry: Pick<OwnCapitalEntry, "marginCost" | "usePredictedMarginCost">,
  ownCapitalEom: number,
  stockCostBasis: number,
  timelineMargin?: number,
  monthKey?: string,
): number {
  const key = monthKey ?? currentMonthKey();
  if (entry.usePredictedMarginCost && stockCostBasis > 0) {
    return predictedMarginCostForMonth(key, stockCostBasis, ownCapitalEom);
  }
  const rh = rhAccruedMarginCostForMonth(key);
  if (rh != null) return rh;
  return timelineMargin ?? (Number(entry.marginCost) || 0);
}

/** Which month's detail panel is expanded in the month selector, and which
 * year is selected in the year selector — both persist across re-renders
 * (the panel is fully rebuilt on every refresh cycle) so the user's
 * selection doesn't keep jumping back to the default. */
let selectedIncomeMonthKey: string | null = null;

/** Transactions behind the current income render — powers the assign-cost trace. */
let lastIncomeTxnsForTrace: WheelTransaction[] = [];
/** Persists across re-renders; always a specific calendar year (no All-years mode). */
let selectedIncomeYear: number | "all" = currentCalendarYear();
/** When on, month chips include forecast months through {@link forecastMaxYear},
 * and the year selector lists years through that cap. */
let incomeForecastEnabled = false;

/** Ticker narrowing for Analytics(L), mirroring the History(L) filter. */
const ANALYTICS_TICKER_FILTER_KEY = "sfe-analytics-local-ticker-filter-v1";
export const ANALYTICS_TICKER_FILTER_INPUT_ID = "sfeAnalyticsTicker";
let analyticsTickerFilter = loadAnalyticsTickerFilter();

/** Row-type narrowing for Analytics(L), keyed by the day table's code column. */
const ANALYTICS_TYPE_FILTER_KEY = "sfe-analytics-local-type-filter-v1";
export const ANALYTICS_TYPE_FILTER_INPUT_ID = "sfeAnalyticsType";
type AnalyticsTypeFilter = IncomeLine["kind"] | "all";
/** Display order groups short options, then stock legs, then long options. */
const ANALYTICS_TYPE_FILTER_OPTIONS: { value: AnalyticsTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "sto", label: "STO" },
  { value: "btc", label: "BTC" },
  { value: "exp", label: "EXP" },
  { value: "asgn", label: "ASG" },
  { value: "buy", label: "BUY" },
  { value: "sell", label: "SELL" },
  { value: "stc", label: "STC" },
  { value: "bto", label: "BTO" },
];
let analyticsTypeFilter = loadAnalyticsTypeFilter();

function loadAnalyticsTickerFilter(): string {
  try {
    return sanitizeTickerFilterValue(
      localStorage.getItem(ANALYTICS_TICKER_FILTER_KEY) ?? "",
    ).toUpperCase();
  } catch {
    return "";
  }
}

function saveAnalyticsTickerFilter(value: string): void {
  try {
    if (value) localStorage.setItem(ANALYTICS_TICKER_FILTER_KEY, value);
    else localStorage.removeItem(ANALYTICS_TICKER_FILTER_KEY);
  } catch {
    // Filtering still works for this session without persistence.
  }
}

/** Substring match on ticker, same semantics as the History(L) filter. */
function matchesAnalyticsTickerFilter(txn: WheelTransaction, needle: string): boolean {
  if (!needle) return true;
  return String(txn.ticker ?? "")
    .toUpperCase()
    .includes(needle);
}

function loadAnalyticsTypeFilter(): AnalyticsTypeFilter {
  try {
    const saved = localStorage.getItem(ANALYTICS_TYPE_FILTER_KEY) ?? "all";
    return ANALYTICS_TYPE_FILTER_OPTIONS.some((option) => option.value === saved)
      ? (saved as AnalyticsTypeFilter)
      : "all";
  } catch {
    return "all";
  }
}

function saveAnalyticsTypeFilter(value: AnalyticsTypeFilter): void {
  try {
    if (value === "all") localStorage.removeItem(ANALYTICS_TYPE_FILTER_KEY);
    else localStorage.setItem(ANALYTICS_TYPE_FILTER_KEY, value);
  } catch {
    // Filtering still works for this session without persistence.
  }
}

function forecastMaxYear(): number {
  return currentCalendarYear() + 5;
}

/** FIFO-section collapsing runs independently of the Analytics+ tab — it
 * just quietly wraps the native "realized closed-trade history (FIFO)"
 * heading in a `<details>` whenever the user visits *native* Analytics. */
const FIFO_DETAILS_ID = "sfe-analytics-fifo-details";
const FIFO_COLLAPSE_ATTR = "data-sfe-fifo-collapse";
const FIFO_COLLAPSE_STORAGE_KEY = "sfe-fifo-section-collapsed";
const ANALYTICS_COLLAPSIBLE_SCOPE = "analytics";
/** Persist income-day accordion open/closed across soft re-renders and full refreshes. */
const ANALYTICS_INCOME_DAY_SCOPE = "analytics-income-days";
const ANALYTICS_L_INCOME_DAY_SCOPE = "analytics-l-income-days";
const ANALYTICS_FIFO_SECTION_ID = "fifo";
const ANALYTICS_TOTAL_PERFORMANCE_SECTION_ID = "total-performance";
const ANALYTICS_INCOME_CHART_SECTION_ID = "total-performance-chart";
const ANALYTICS_PANEL_SEL = '[data-wheel-view="analytics"]';
const WHEEL_API_BASE = "/api/v1/wheel";

const FIFO_HEADING_RE =
  /Analytics[\s\S]{0,40}realized[\s\S]{0,20}closed[- ]trade[\s\S]{0,20}history|realized[\s\S]{0,20}closed[- ]trade[\s\S]{0,20}history[\s\S]{0,10}\(FIFO\)/i;

interface WheelTransaction {
  id?: number;
  linked_transaction_id?: number;
  /** All ledger link targets (open_close, assign_stock, wheel_pair, …). */
  linked_transaction_ids?: number[];
  /** Explicit put ASGN ↔ call ASGN links (own ledger link_type wheel_pair). */
  wheel_pair_linked_ids?: number[];
  /** Runtime-only links inferred from matching assignment option/stock legs.
   * A migration reconciliation row may represent several option lots. */
  inferred_linked_transaction_ids?: number[];
  instrument_type?: string;
  action?: string;
  side?: string;
  ticker?: string;
  trade_date?: string;
  /** Broker fill time (ISO). Within-day display order uses this before created_at. */
  trade_timestamp?: string;
  /** Ledger write timestamp (ISO). Fallback when the fill time is missing. */
  created_at?: string;
  quantity?: number;
  avg_price?: number;
  strike?: number;
  exp_date?: string;
  option_symbol?: string;
  close_reason?: string;
  option_trans_code?: string;
  total_credit?: number;
  /** Legacy archive: share cost basis at call assignment (from open_long reverse replay). */
  assignment_cost_avg?: number;
  /** Legacy archive: realized stock P/L for this call assignment (Robinhood lot replay). */
  assignment_realized_pl?: number;
  /** Legacy archive: month-bucket date for ASGN income (may differ from exp when
   * XL books an early-next-month call on the prior month's tab). */
  assignment_income_date?: string;
  /** Stock leg generated by an option assignment; consumed through the
   * option settlement so average-cost replay must not apply it twice. */
  assignment_companion?: boolean;
  /** RH tax-lot / premium-adjusted cost per share (own-ledger extension). */
  tax_lot_cost_per_share?: number;
  /** Underlying share price when this contract was opened (own ledger). */
  underlying_open_price?: number;
  /** Underlying share price when it closed, expired or was assigned (own ledger). */
  underlying_close_price?: number;
  /** Provenance of this row's own underlying price (md_fill_minute, md_day_close, …). */
  underlying_price_source?: string;
  /** POP (1 − |delta|) the contract was sold at; closes report their opening's. */
  open_pop?: number;
  /** md_delta when captured, not_captured when the contract predates capture. */
  open_pop_source?: string;
  /** Own-ledger / SF extension bag (tax_lot_cost_per_share, total_credit, …). */
  extensions?: Record<string, string>;
  account_id?: string | null;
  account_label?: string | null;
  account_name?: string | null;
  broker_id?: string | null;
  broker_name?: string | null;
  account_short_name?: string | null;
  tags?: string[];
  source?: string | null;
  notes_preview?: string;
  notes?: Array<{ body?: string | null; kind?: string | null }>;
  note?: string;
}

/** Blended weighted-average cost basis for a ticker's shares (average-cost
 * method, not FIFO/LIFO lots): buys blend into the average, sells only
 * reduce quantity and leave the average of remaining shares unchanged. */
interface AvgPosition {
  qty: number;
  avg: number;
}

/** Keep each ledger account's share book separate so paper lots cannot change
 * live call-assignment P/L (or the reverse). */
function shareAvgBookKey(txn: Pick<WheelTransaction, "account_id">): string {
  return String(txn.account_id ?? "").trim() || "_";
}

function shareAvgPositionKey(
  txn: Pick<WheelTransaction, "account_id" | "ticker">,
  ticker = txn.ticker ?? "",
): string {
  return `${shareAvgBookKey(txn)}|${ticker}`;
}

export interface IncomeBreakdown {
  total: number;
  /** STO premium credits only (month received = STO trade_date). */
  premiums: number;
  /** STC credits from closing long options — income, but not short premium. */
  stcCredits: number;
  assignIncome: number;
  stockSellIncome: number;
  btcCost: number;
  btoCost: number;
}

interface IncomeLine {
  kind: "sto" | "stc" | "btc" | "bto" | "asgn" | "exp" | "buy" | "sell" | "cash";
  label: string;
  ticker: string;
  side: string;
  strike: string;
  qty: string;
  open: string;
  /** Option expiration date. Dash for stock and cash rows. */
  exp: string;
  credit: string;
  btc: string;
  avg: string;
  symbol: string;
  amount: number;
  /** False for raw stock rows shown for traceability but excluded from income. */
  incomeAccounted: boolean;
  /** Optional dim secondary text (e.g. assignment shares × strike, call avg). */
  detail?: string;
  /** Absolute option contracts (or shares for stock rows). */
  contracts?: number | null;
  /** Per-share option premium / stock price. */
  premium?: number | null;
  /** Calendar days from trade date to expiration (options). */
  dte?: number | null;
  txnId?: number;
  linkedTxnIds: number[];
  linkStatus: "ledger" | "inferred" | "unlinked";
  /** Epoch ms for within-day sort (newer first). Prefer created_at, else txn id. */
  sortAt?: number;
  /** Stock buys: holding avg before this purchase (0 if flat). */
  holdingAvgBefore?: number | null;
  /** Stock buys: holding avg after blending this purchase. */
  holdingAvgAfter?: number | null;
  /** Trading system (Robinhood / TastyTrades / Manual) — Analytics(L). */
  system?: string;
  /** Broker account label / last-4 — Analytics(L). */
  account?: string;
  brokerCode?: string;
  brokerColor?: string;
  brokerTitle?: string;
  accountShort?: string;
  accountTitle?: string;
  accountId?: string;
  /** Underlying share price when the contract was opened — Analytics(L). */
  underlyingOpen?: number | null;
  /** Underlying share price at close, expiration or assignment — Analytics(L). */
  underlyingClose?: number | null;
  /** True when that side's price stood in for an unavailable fill minute. */
  underlyingOpenEstimated?: boolean;
  underlyingCloseEstimated?: boolean;
  /** POP (1 − |delta|) the contract was sold at — Analytics(L). */
  openPop?: number | null;
  /** User memo from transaction_notes or cash_flows.note. */
  note?: string;
  /** Own-ledger cash_flows.id when this line is a cash row. */
  cashFlowId?: number;
}

interface IncomeDayBucket {
  date: string;
  premiums: number;
  stcCredits: number;
  assignIncome: number;
  stockSellIncome: number;
  btcCost: number;
  btoCost: number;
  total: number;
  lines: IncomeLine[];
}

interface IncomeMonthBucket {
  month: string; // YYYY-MM
  label: string;
  premiums: number;
  stcCredits: number;
  assignIncome: number;
  stockSellIncome: number;
  btcCost: number;
  btoCost: number;
  total: number;
  /** Total collateral (strike × contracts × 100) for options opened (STO)
   * in this month — attributed to the month capital was locked up, not the
   * month the option later closed/expired/assigned. */
  deployedCapital: number;
  days: IncomeDayBucket[];
}

/** Previous calendar month key (YYYY-MM) for a given YYYY-MM key, handling
 * year rollover (e.g. "2026-01" → "2025-12"). */
function prevMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${prevYear}-${String(prevMonth).padStart(2, "0")}`;
}

function nextMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth).padStart(2, "0")}`;
}

function rocPerfCls(realized: number | null, target: number): string {
  if (realized == null || !Number.isFinite(realized)) return "";
  return realized >= target ? "roi-strong" : "roi-loss";
}

function incomeHeadlineCls(value: number): string {
  return value < 0 ? "sfe-neg" : "roi-strong";
}

function normalizeText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function isVisible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  let current: HTMLElement | null = el;
  while (current) {
    const style = getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    current = current.parentElement;
  }
  return true;
}

function fmtUsd(value: number): string {
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Whole-dollar formatting for compact KPI cards. */
function fmtUsdWhole(value: number): string {
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })}`;
}

function fmtUsdSignedParen(delta: number): string {
  const sign = delta >= 0 ? "+" : "−";
  return `(${sign}${fmtUsdWhole(Math.abs(delta)).replace(/^-/, "")})`;
}

function fmtNum(value: number): string {
  return Math.round(value).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function fmtPct(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return "";
  const sign = pct < 0 ? "-" : "";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

/** Month ROC % — two decimals so the % matches Target / Realized dollars. */
function fmtRocPct(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return "";
  const sign = pct < 0 ? "-" : "";
  return `${sign}${Math.abs(pct).toFixed(2)}%`;
}

function parseYear(closeDate?: string): number | null {
  if (!closeDate) return null;
  const year = Number(closeDate.slice(0, 4));
  return Number.isFinite(year) ? year : null;
}


function isAnalyticsSubviewVisible(): boolean {
  const panel = document.querySelector('[data-wheel-view="analytics"]');
  if (!(panel instanceof HTMLElement)) return false;
  if (panel.classList.contains("hidden") || panel.hasAttribute("hidden")) return false;
  return isVisible(panel);
}

const SPX_CACHE_MS = 30 * 60 * 1000;
let spxMonthlyReturns = new Map<string, number>();
let spxLoadedAt = 0;

function spxMonthlyReturn(monthKey: string): number | null {
  const value = spxMonthlyReturns.get(monthKey);
  return value != null && Number.isFinite(value) ? value : null;
}

async function loadSpxMonthlyReturns(): Promise<void> {
  if (spxLoadedAt && Date.now() - spxLoadedAt < SPX_CACHE_MS && spxMonthlyReturns.size) return;
  try {
    const res = await fetch("/analytics/spx-monthly", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return;
    const body = (await res.json()) as { months?: Record<string, unknown> };
    const next = new Map<string, number>();
    for (const [key, value] of Object.entries(body.months ?? {})) {
      const n = Number(value);
      if (/^\d{4}-\d{2}$/.test(key) && Number.isFinite(n)) next.set(key, n);
    }
    if (next.size) {
      spxMonthlyReturns = next;
      spxLoadedAt = Date.now();
    }
  } catch {
    /* The chart leaves the S&P line off when the index history is unavailable. */
  }
}

async function wheelFetch<T = unknown>(path: string): Promise<T> {
  const fullPath = `${WHEEL_API_BASE}${path}`;
  const pageApi = (window as unknown as { API?: { get: (p: string, o?: object) => Promise<T> } }).API;
  if (pageApi?.get) {
    return pageApi.get(fullPath, { silent: true });
  }

  const res = await fetch(fullPath, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

/** Shared TTL for every cache in `AnalyticsPlusController` (transactions,
 * stock summary, own-capital ledger). Aligns with proxy account GET TTL. */
const DATA_CACHE_MS = 3 * 60_000;
const TXN_PAGE_LIMIT = 200;
const TXN_MAX_PAGES = 10;

interface StockOpenSummary {
  count: number;
  quantity: number;
  cost_basis: number;
  market_value: number;
  open_pl: number;
  open_pl_pct: number | null;
}

const EMPTY_STOCK_OPEN_SUMMARY: StockOpenSummary = {
  count: 0,
  quantity: 0,
  cost_basis: 0,
  market_value: 0,
  open_pl: 0,
  open_pl_pct: null,
};

async function fetchStockOpenSummary(): Promise<StockOpenSummary> {
  return fetchStockOpenSummaryVia((path) => wheelFetch(path));
}

/** Manually-entered cash flow for a calendar month, persisted on the user's
 * own Linode proxy (not Stock Forge) — see server/index.mjs `/own-capital`. */
interface OwnCapitalEntry {
  cashAdded: number;
  withdrawn: number;
  /** Tax withdrawals tagged in History(L) cash ledger (subset of withdrawn). */
  taxWithdrawn?: number;
  /** Margin interest and financing costs for the month. */
  marginCost?: number;
  /** Stock lending interest credited for the month (Analytics+ manual ledger). */
  stockLendingInterest?: number;
  /** Imported stock lending income from own-ledger cash flows (Analytics L). */
  stockLendingIncome?: number;
  /** When true, use forecast margin from stock cost basis vs own capital. */
  usePredictedMarginCost?: boolean;
  /** Per-trade fee in dollars. */
  perTradeFee?: number;
  /** Number of trades charged at perTradeFee. */
  tradeCount?: number;
  /** Additive adjustment applied at month-end with income and cash flows. */
  ownCapitalCorrection?: number;
  /** Target return on own capital % for forecast / planning (stored on proxy). */
  targetRocPct?: number;
}

/** Default per-trade fee when JSON omits `perTradeFee` (matches server seed). */
const DEFAULT_PER_TRADE_FEE = 0.06;

function perTradeExpenseTotal(
  entry: Pick<OwnCapitalEntry, "perTradeFee">,
  tradeCount: number,
): number {
  const fee = Number(entry.perTradeFee) || 0;
  const count = Math.max(0, Math.round(tradeCount));
  return fee * count;
}

function resolvedMarginCost(
  entry: Pick<OwnCapitalEntry, "marginCost" | "usePredictedMarginCost">,
  opts?: { stockCostBasis?: number; ownCapitalEom?: number; monthKey?: string },
): number {
  const monthKey = opts?.monthKey ?? currentMonthKey();
  if (
    entry.usePredictedMarginCost &&
    opts?.stockCostBasis != null &&
    opts.ownCapitalEom != null &&
    Number.isFinite(opts.ownCapitalEom)
  ) {
    return predictedMarginCostForMonth(monthKey, opts.stockCostBasis, opts.ownCapitalEom);
  }
  return factualMarginCostForMonth(entry, monthKey);
}

function expenseTotal(
  entry: Pick<OwnCapitalEntry, "marginCost" | "perTradeFee" | "usePredictedMarginCost">,
  tradeCount: number,
  marginOpts?: { stockCostBasis?: number; ownCapitalEom?: number },
): number {
  return resolvedMarginCost(entry, marginOpts) + perTradeExpenseTotal(entry, tradeCount);
}

function countTradesInMonth(month: IncomeMonthBucket): number {
  let count = 0;
  for (const day of month.days) {
    for (const line of day.lines) {
      if (line.txnId != null) count += 1;
      else if (line.kind === "asgn") count += 1;
    }
  }
  return count;
}

/** Analytics(L): TT/RH own-ledger history starts in 2025. */
const ANALYTICS_LOCAL_MIN_YEAR = 2025;

function filterAnalyticsLocalYears(years: number[]): number[] {
  return years.filter((year) => year >= ANALYTICS_LOCAL_MIN_YEAR);
}

function monthHasTradingActivity(month: IncomeMonthBucket): boolean {
  return countTradesInMonth(month) > 0;
}

function isInactiveLocalLedgerMonthChip(
  monthKey: string,
  month: IncomeMonthBucket,
  localLedgerAnalytics: boolean,
  forecastEnabled: boolean,
  selectedYear: number | "all",
): boolean {
  if (!localLedgerAnalytics) return false;
  if (isForecastMonthChip(monthKey, forecastEnabled, selectedYear)) return false;
  return !monthHasTradingActivity(month);
}

function expenseTotalForMonth(
  entry: Pick<OwnCapitalEntry, "marginCost" | "perTradeFee" | "tradeCount" | "usePredictedMarginCost">,
  month?: IncomeMonthBucket,
  effectiveMarginCost?: number,
): number {
  const tradeCount = month ? countTradesInMonth(month) : Math.max(0, Math.round(Number(entry.tradeCount) || 0));
  const margin =
    effectiveMarginCost ??
    resolvedMarginCost(entry);
  return margin + perTradeExpenseTotal(entry, tradeCount);
}

function normalizeOwnCapitalEntry(entry: Partial<OwnCapitalEntry> | undefined): OwnCapitalEntry {
  const legacy = entry as { expenses?: unknown; perTradeCost?: unknown } | undefined;
  const marginRaw = entry?.marginCost ?? legacy?.expenses;
  const stockLendingRaw =
    entry?.stockLendingIncome ??
    entry?.stockLendingInterest ??
    (legacy as { stockLendingIncome?: unknown } | undefined)?.stockLendingIncome;
  const perTradeRaw = entry?.perTradeFee ?? legacy?.perTradeCost;
  return {
    cashAdded: Number(entry?.cashAdded) || 0,
    withdrawn: Number(entry?.withdrawn) || 0,
    taxWithdrawn: Number(entry?.taxWithdrawn) || 0,
    marginCost: Number(marginRaw) || 0,
    stockLendingIncome: Number(stockLendingRaw) || 0,
    perTradeFee: Number(perTradeRaw) || 0,
    tradeCount: Math.max(0, Math.round(Number(entry?.tradeCount) || 0)),
    ownCapitalCorrection:
      entry?.ownCapitalCorrection != null && Number.isFinite(Number(entry.ownCapitalCorrection))
        ? Number(entry.ownCapitalCorrection)
        : undefined,
    targetRocPct:
      entry?.targetRocPct != null && Number.isFinite(Number(entry.targetRocPct))
        ? Number(entry.targetRocPct)
        : undefined,
    usePredictedMarginCost:
      entry?.usePredictedMarginCost === true
        ? true
        : entry?.usePredictedMarginCost === false
          ? false
          : undefined,
  };
}

/** Resolve per-trade fee: positive JSON value, else inherit, else default.
 * Zero is treated as unset (legacy rows normalized before perTradeFee existed). */
function resolvePerTradeFee(
  monthKey: string,
  raw: Partial<OwnCapitalEntry> | undefined,
  ledger: Record<string, OwnCapitalEntry>,
): number {
  const legacy = raw as { perTradeCost?: unknown } | undefined;
  if (raw != null && Object.prototype.hasOwnProperty.call(raw, "perTradeFee")) {
    const fee = Number(raw.perTradeFee);
    if (Number.isFinite(fee) && fee > 0) return fee;
  }
  if (legacy != null && Object.prototype.hasOwnProperty.call(legacy, "perTradeCost")) {
    const fee = Number(legacy.perTradeCost);
    if (Number.isFinite(fee) && fee > 0) return fee;
  }
  const inherited = inheritedPerTradeFee(monthKey, ledger);
  if (inherited != null) return inherited;
  return DEFAULT_PER_TRADE_FEE;
}

function ledgerEntryWithResolvedFee(
  monthKey: string,
  raw: Partial<OwnCapitalEntry> | undefined,
  ledger: Record<string, OwnCapitalEntry>,
): OwnCapitalEntry {
  const legacy = (raw as { ownCapital?: unknown } | undefined)?.ownCapital;
  const correctionRaw = raw?.ownCapitalCorrection ?? legacy;
  return {
    ...normalizeOwnCapitalEntry({
      ...raw,
      ownCapitalCorrection:
        correctionRaw != null && Number.isFinite(Number(correctionRaw))
          ? Number(correctionRaw)
          : undefined,
    }),
    perTradeFee: resolvePerTradeFee(monthKey, raw, ledger),
  };
}

/** When a month has no ledger row yet, inherit the most recent prior month's fee. */
function inheritedPerTradeFee(
  monthKey: string,
  ledger: Record<string, OwnCapitalEntry>,
): number | undefined {
  for (const key of Object.keys(ledger).filter((k) => k <= monthKey).sort().reverse()) {
    const fee = Number(ledger[key]?.perTradeFee);
    if (Number.isFinite(fee) && fee > 0) return fee;
  }
  for (const entry of Object.values(ledger)) {
    const fee = Number(entry.perTradeFee);
    if (Number.isFinite(fee) && fee > 0) return fee;
  }
  return undefined;
}

function mergeOwnCapitalEntry(
  prev: OwnCapitalEntry,
  patch: Partial<OwnCapitalEntry>,
): OwnCapitalEntry {
  return {
    cashAdded: patch.cashAdded !== undefined ? patch.cashAdded : prev.cashAdded,
    withdrawn: patch.withdrawn !== undefined ? patch.withdrawn : prev.withdrawn,
    marginCost: patch.marginCost !== undefined ? patch.marginCost : prev.marginCost,
    perTradeFee: patch.perTradeFee !== undefined ? patch.perTradeFee : prev.perTradeFee,
    ownCapitalCorrection:
      patch.ownCapitalCorrection !== undefined
        ? patch.ownCapitalCorrection
        : prev.ownCapitalCorrection,
    targetRocPct: patch.targetRocPct !== undefined ? patch.targetRocPct : prev.targetRocPct,
    usePredictedMarginCost:
      patch.usePredictedMarginCost !== undefined
        ? patch.usePredictedMarginCost
        : prev.usePredictedMarginCost,
  };
}

function aggregateExpenses(
  ledger: Record<string, OwnCapitalEntry>,
  monthsByKey: Map<string, IncomeMonthBucket>,
): {
  margin: number;
  perTrade: number;
  tradeCount: number;
  total: number;
} {
  let margin = 0;
  let perTrade = 0;
  let tradeCount = 0;
  const monthKeys = new Set<string>([...Object.keys(ledger), ...monthsByKey.keys()]);
  for (const monthKey of monthKeys) {
    const entry = ledgerEntryWithResolvedFee(monthKey, ledger[monthKey], ledger);
    const month = monthsByKey.get(monthKey);
    const count = month ? countTradesInMonth(month) : 0;
    margin += entry.marginCost ?? 0;
    perTrade += perTradeExpenseTotal(entry, count);
    tradeCount += count;
  }
  return { margin, perTrade, tradeCount, total: margin + perTrade };
}

/** Cumulative expenses through the selected month chip (Fact mode). */
function aggregateExpensesThroughMonth(
  ledger: Record<string, OwnCapitalEntry>,
  monthsByKey: Map<string, IncomeMonthBucket>,
  selectedMonth: string | null,
  ownCapitalByMonth?: Map<string, OwnCapitalPoint>,
): ReturnType<typeof aggregateExpenses> {
  const cm = currentMonthKey();
  const throughMonth =
    selectedMonth && selectedMonth <= cm ? selectedMonth : cm;

  let margin = 0;
  let perTrade = 0;
  let tradeCount = 0;
  const monthKeys = new Set<string>([...Object.keys(ledger), ...monthsByKey.keys()]);
  for (const monthKey of [...monthKeys].sort()) {
    if (monthKey > throughMonth || monthKey > cm) continue;
    const entry = ledgerEntryWithResolvedFee(monthKey, ledger[monthKey], ledger);
    const month = monthsByKey.get(monthKey);
    const count = month ? countTradesInMonth(month) : 0;
    margin += ownCapitalByMonth?.get(monthKey)?.effectiveMarginCost ?? entry.marginCost ?? 0;
    perTrade += perTradeExpenseTotal(entry, count);
    tradeCount += count;
  }
  return { margin, perTrade, tradeCount, total: margin + perTrade };
}

function readExpenseFieldsFromWrap(wrap: HTMLElement): Pick<OwnCapitalEntry, "marginCost" | "perTradeFee"> {
  const marginCostInput = wrap.querySelector<HTMLInputElement>(".sfe-own-capital-margin-cost");
  const perTradeFeeInput = wrap.querySelector<HTMLInputElement>(".sfe-own-capital-per-trade-fee");
  return {
    marginCost: Number(marginCostInput?.value) || 0,
    perTradeFee: Number(perTradeFeeInput?.value) || 0,
  };
}

function ledgerEntryForMonth(
  monthKey: string,
  ledger: Record<string, OwnCapitalEntry>,
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
): OwnCapitalEntry {
  const raw = ledger[monthKey] ?? ownCapitalByMonth.get(monthKey);
  return ledgerEntryWithResolvedFee(monthKey, raw, ledger);
}

function readOwnCapitalFieldsFromWrap(wrap: HTMLElement): Pick<OwnCapitalEntry, "cashAdded" | "withdrawn"> {
  const cashAddedInput = wrap.querySelector<HTMLInputElement>(".sfe-own-capital-cash-added");
  const withdrawnInput = wrap.querySelector<HTMLInputElement>(".sfe-own-capital-withdrawn");
  return {
    cashAdded: Number(cashAddedInput?.value) || 0,
    withdrawn: Number(withdrawnInput?.value) || 0,
  };
}

/** One month's "own capital" build-up: manual cash flow + this month's
 * trading income, plus the running balance carried from prior months. */
interface OwnCapitalPoint extends OwnCapitalEntry {
  income: number;
  /** Computed own capital at month-end (after income and cash). */
  ownCapital: number;
  /** Margin cost used in timeline (manual or predicted). */
  effectiveMarginCost?: number;
}

const OWN_CAPITAL_PATH = "/own-capital";

function ownCapitalApiUrl(suffix = ""): string | null {
  const { baseUrl } = getApiConfig();
  if (!baseUrl) return null;
  const path = `${OWN_CAPITAL_PATH}${suffix}`;
  if (baseUrl === window.location.origin) return path;
  return `${baseUrl}${path}`;
}

function ownCapitalHeaders(): HeadersInit {
  return { Accept: "application/json" };
}

/** Own-capital ledger lives only on the user's own proxy (not Stock Forge
 * itself), so this silently returns {} when no proxy is configured. */
async function fetchOwnCapitalLedger(opts?: { bustCache?: boolean }): Promise<Record<string, OwnCapitalEntry>> {
  const cacheSuffix = opts?.bustCache ? `?t=${Date.now()}` : "";
  const url = ownCapitalApiUrl(cacheSuffix);
  if (!url) return {};

  try {
    const res = await proxyFetch(url, {
      headers: ownCapitalHeaders(),
      cache: "no-store",
      credentials: "include",
    });
    if (!res.ok) {
      if (res.status === 401) {
        if (!opts?.quiet) {
          showToast("Sign in via Settings to access Own Capital.", "error");
        }
      } else if (!opts?.quiet) {
        showToast(`Could not load own-capital ledger (${res.status}).`, "error");
      }
      return {};
    }
    const data = (await res.json()) as
      | { months?: Record<string, Partial<OwnCapitalEntry>> }
      | Record<string, Partial<OwnCapitalEntry>>;
    const rawMonths =
      "months" in data && data.months != null
        ? data.months
        : Object.fromEntries(
            Object.entries(data).filter(([key]) => /^\d{4}-\d{2}$/.test(key)),
          );
    const months: Record<string, OwnCapitalEntry> = {};
    for (const [month, entry] of Object.entries(rawMonths)) {
      months[month] = normalizeOwnCapitalEntry(entry);
    }
    for (const month of Object.keys(rawMonths).sort()) {
      months[month] = ledgerEntryWithResolvedFee(month, rawMonths[month], months);
    }
    return months;
  } catch {
    return {};
  }
}

async function saveOwnCapitalEntry(
  month: string,
  entry: OwnCapitalEntry,
  opts?: { quiet?: boolean },
): Promise<boolean> {
  const url = ownCapitalApiUrl(`/${month}`);
  if (!url) {
    if (!opts?.quiet) showToast("Configure the API proxy to save own capital.", "error");
    return false;
  }

  const payload: OwnCapitalEntry = {
    cashAdded: Number(entry.cashAdded) || 0,
    withdrawn: Number(entry.withdrawn) || 0,
    marginCost: Number(entry.marginCost) || 0,
    perTradeFee: Number(entry.perTradeFee) || 0,
    tradeCount: Math.max(0, Math.round(Number(entry.tradeCount) || 0)),
    // Analytics+ no longer applies month-end own-capital corrections.
    ownCapitalCorrection: 0,
    ...(entry.targetRocPct != null && Number.isFinite(Number(entry.targetRocPct))
      ? { targetRocPct: Number(entry.targetRocPct) }
      : {}),
    ...(entry.usePredictedMarginCost !== undefined
      ? { usePredictedMarginCost: entry.usePredictedMarginCost === true }
      : {}),
  };

  try {
    const res = await proxyFetch(url, {
      method: "PUT",
      headers: { ...ownCapitalHeaders(), "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      if (res.status === 401) {
        if (!opts?.quiet) {
          showToast("Sign in via Settings to access Own Capital.", "error");
        }
      } else if (!opts?.quiet) {
        showToast(`Could not save own capital (${res.status}).`, "error");
      }
      return false;
    }
    if (!opts?.quiet) showToast(`Saved own capital for ${month}.`, "success");
    invalidateAnalyticsPlusCache();
    return true;
  } catch {
    if (!opts?.quiet) showToast("Could not save own capital.", "error");
    return false;
  }
}

/**
 * Own capital (EOM) = prior EOM + trading income + cash added − withdrawn
 * − expenses (margin + per-trade fees) − tax cash outflow.
 * Tax cash = estimated + balance-due + extension (Income cards still subtract
 * estimated only). Cash flows, expenses, and tax payments settle at month-end —
 * they never change the opening balance used for ROC / target (that stays prior
 * EOM). Builds from the earliest month on record; the year filter only changes
 * which months are displayed, not the running balance.
 */
type OwnCapitalTimelineOpts = {
  localLedger?: boolean;
  openingBalance?: number;
  /** Non–cash-ledger Analytics+: predicted tax added into withdrawn (2027+). */
  predictedTaxWithdrawals?: Map<string, number>;
  /** When false, the book stops at the current month and uses accrued margin. */
  forecastEnabled?: boolean;
};

function computeOwnCapitalTimelinePass(
  allMonths: IncomeMonthBucket[],
  ledger: Record<string, OwnCapitalEntry>,
  stockCostBasis: number,
  predOwnCapitalRef: Map<string, number> | null,
  ignorePredictedMargin: boolean,
  opts: OwnCapitalTimelineOpts = {},
): Map<string, OwnCapitalPoint> {
  const incomeByMonth = new Map<string, number>();
  for (const month of allMonths) {
    incomeByMonth.set(month.month, cashIncome(month));
  }

  const monthKeys = new Set<string>([
    ...incomeByMonth.keys(),
    ...Object.keys(ledger),
    ...(opts.predictedTaxWithdrawals?.keys() ?? []),
  ]);
  const sorted = [...monthKeys].sort((a, b) => a.localeCompare(b));

  const monthsByKey = new Map(allMonths.map((month) => [month.month, month]));

  const timeline = new Map<string, OwnCapitalPoint>();
  let running = opts.openingBalance ?? 0;
  const allowFuture = opts.forecastEnabled !== false;
  const factThrough = currentMonthKey();
  for (const monthKey of sorted) {
    if (!allowFuture && monthKey > factThrough) continue;
    const entry = ledgerEntryWithResolvedFee(monthKey, ledger[monthKey], ledger);
    const income = incomeByMonth.get(monthKey) ?? 0;
    const monthBucket = monthsByKey.get(monthKey);
    const tradeCount = monthBucket ? countTradesInMonth(monthBucket) : Math.max(0, Math.round(Number(entry.tradeCount) || 0));
    const perTrade = opts.localLedger ? 0 : perTradeExpenseTotal(entry, tradeCount);
    const predictedWd = opts.localLedger
      ? 0
      : Math.max(0, Number(opts.predictedTaxWithdrawals?.get(monthKey)) || 0);
    const withdrawn = (Number(entry.withdrawn) || 0) + predictedWd;
    const estimatedTaxPaid = opts.localLedger ? 0 : taxCashOutflowForMonthKey(monthKey);
    const preMarginOwnCapital =
      running +
      income +
      entry.cashAdded -
      withdrawn -
      perTrade -
      estimatedTaxPaid +
      (opts.localLedger ? Number(entry.stockLendingIncome) || 0 : 0);
    const ownCapitalEomForPred =
      predOwnCapitalRef?.get(monthKey) ?? preMarginOwnCapital;
    const effectiveMarginCost = opts.localLedger
      ? localLedgerMarginCostForMonth(
          monthKey,
          entry,
          stockCostBasis,
          ownCapitalEomForPred,
          timeline,
          allowFuture,
        )
      : ignorePredictedMargin || !entry.usePredictedMarginCost
        ? factualMarginCostForMonth(entry, monthKey)
        : resolvedMarginCost(entry, {
            stockCostBasis,
            ownCapitalEom: ownCapitalEomForPred,
            monthKey,
          });
    running = preMarginOwnCapital - effectiveMarginCost;
    timeline.set(monthKey, {
      ...entry,
      withdrawn,
      income,
      ownCapital: running,
      effectiveMarginCost,
    });
  }
  return timeline;
}

function computeOwnCapitalTimeline(
  allMonths: IncomeMonthBucket[],
  ledger: Record<string, OwnCapitalEntry>,
  stockCostBasis = 0,
  opts: OwnCapitalTimelineOpts = {},
): Map<string, OwnCapitalPoint> {
  const manualTimeline = computeOwnCapitalTimelinePass(
    allMonths,
    ledger,
    stockCostBasis,
    null,
    true,
    opts,
  );
  if (opts.localLedger || !stockCostBasis || !ledgerUsesPredictedMarginCost(ledger)) {
    return manualTimeline;
  }

  let predRef = new Map<string, number>(
    [...manualTimeline.entries()].map(([monthKey, point]) => [monthKey, point.ownCapital]),
  );
  let timeline = manualTimeline;
  for (let iteration = 0; iteration < 8; iteration += 1) {
    const next = computeOwnCapitalTimelinePass(allMonths, ledger, stockCostBasis, predRef, false, opts);
    let changed = false;
    const nextRef = new Map<string, number>();
    for (const [monthKey, point] of next) {
      nextRef.set(monthKey, point.ownCapital);
      const prev = timeline.get(monthKey);
      if (
        !prev ||
        prev.ownCapital !== point.ownCapital ||
        prev.effectiveMarginCost !== point.effectiveMarginCost
      ) {
        changed = true;
      }
    }
    timeline = next;
    predRef = nextRef;
    if (!changed) break;
  }

  if (stockCostBasis) {
    for (const [monthKey, point] of timeline) {
      if (!point.usePredictedMarginCost) continue;
      timeline.set(monthKey, {
        ...point,
        effectiveMarginCost: predictedMarginCostForMonth(
          monthKey,
          stockCostBasis,
          point.ownCapital,
        ),
      });
    }
  }
  return timeline;
}

async function fetchTransactionsVia(
  get: <T>(path: string) => Promise<T>,
): Promise<WheelTransaction[]> {
  const txns: WheelTransaction[] = [];
  let offset = 0;

  for (let page = 0; page < TXN_MAX_PAGES; page += 1) {
    const data = await get<{ transactions?: Record<string, unknown>[] }>(
      `/transactions?limit=${TXN_PAGE_LIMIT}&offset=${offset}`,
    );
    const batch = (data.transactions ?? []).map(normalizeTransaction);
    txns.push(...batch);
    if (batch.length < TXN_PAGE_LIMIT) break;
    offset += TXN_PAGE_LIMIT;
  }

  return txns.sort(
    (a, b) =>
      String(a.trade_date).localeCompare(String(b.trade_date)) ||
      (Number(a.id) || 0) - (Number(b.id) || 0),
  );
}

async function fetchTransactions(): Promise<WheelTransaction[]> {
  return fetchTransactionsVia((path) => wheelFetch(path));
}

async function fetchStockOpenSummaryVia(
  get: <T>(path: string) => Promise<T>,
): Promise<StockOpenSummary> {
  const data = await get<{ stock_open?: Partial<StockOpenSummary> }>("/performance/summary");
  const s = data.stock_open ?? {};
  return {
    count: Number(s.count) || 0,
    quantity: Number(s.quantity) || 0,
    cost_basis: Number(s.cost_basis) || 0,
    market_value: Number(s.market_value) || 0,
    open_pl: Number(s.open_pl) || 0,
    open_pl_pct: s.open_pl_pct == null ? null : Number(s.open_pl_pct),
  };
}

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function extensionNumber(raw: Record<string, unknown>, key: string): number | undefined {
  if (raw[key] != null) {
    const top = Number(raw[key]);
    if (Number.isFinite(top)) return top;
  }
  const ext = raw.extensions;
  if (ext && typeof ext === "object" && !Array.isArray(ext)) {
    const nested = Number((ext as Record<string, unknown>)[key]);
    if (Number.isFinite(nested)) return nested;
  }
  return undefined;
}

function normalizeTransaction(raw: Record<string, unknown>): WheelTransaction {
  const id = raw.id != null ? Number(raw.id) : undefined;
  const linkedId =
    raw.linked_transaction_id != null ? Number(raw.linked_transaction_id) : undefined;
  const linkedIdsRaw = raw.linked_transaction_ids;
  const linkedTransactionIds = Array.isArray(linkedIdsRaw)
    ? linkedIdsRaw
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    : undefined;
  const wheelPairRaw = raw.wheel_pair_linked_ids;
  const wheelPairLinkedIds = Array.isArray(wheelPairRaw)
    ? wheelPairRaw
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    : undefined;
  const qty = raw.quantity != null ? Number(raw.quantity) : undefined;
  const avg = raw.avg_price != null ? Number(raw.avg_price) : undefined;
  const strike = raw.strike != null ? Number(raw.strike) : undefined;
  const credit = extensionNumber(raw, "total_credit");
  const assignAvg =
    raw.assignment_cost_avg != null ? Number(raw.assignment_cost_avg) : undefined;
  const assignPl =
    raw.assignment_realized_pl != null ? Number(raw.assignment_realized_pl) : undefined;
  const assignIncomeDate =
    typeof raw.assignment_income_date === "string" ? raw.assignment_income_date : undefined;
  const createdAt = typeof raw.created_at === "string" ? raw.created_at : undefined;
  const extensionsRaw = raw.extensions;
  const extensions =
    extensionsRaw && typeof extensionsRaw === "object" && !Array.isArray(extensionsRaw)
      ? Object.fromEntries(
          Object.entries(extensionsRaw as Record<string, unknown>).map(([k, v]) => [
            k,
            v == null ? "" : String(v),
          ]),
        )
      : undefined;
  const taxLot = extensionNumber(raw, "tax_lot_cost_per_share");
  const underlyingOpen = Number(raw.underlying_open_price);
  const underlyingClose = Number(raw.underlying_close_price);
  const openPop = Number(raw.open_pop);
  const tagsRaw = raw.tags;
  const tags = Array.isArray(tagsRaw)
    ? tagsRaw.filter((t): t is string => typeof t === "string")
    : undefined;
  const assignmentCompanion =
    raw.assignment_companion === true ||
    String(extensions?.assignment_companion ?? "").toLowerCase() === "true";

  return {
    id: Number.isFinite(id) ? id : undefined,
    linked_transaction_id: Number.isFinite(linkedId) ? linkedId : undefined,
    linked_transaction_ids:
      linkedTransactionIds && linkedTransactionIds.length ? linkedTransactionIds : undefined,
    wheel_pair_linked_ids:
      wheelPairLinkedIds && wheelPairLinkedIds.length ? wheelPairLinkedIds : undefined,
    instrument_type:
      typeof raw.instrument_type === "string" ? raw.instrument_type : undefined,
    action: typeof raw.action === "string" ? raw.action : undefined,
    side: typeof raw.side === "string" ? raw.side : undefined,
    ticker: typeof raw.ticker === "string" ? raw.ticker : undefined,
    trade_date: typeof raw.trade_date === "string" ? raw.trade_date : undefined,
    created_at: createdAt || undefined,
    quantity: Number.isFinite(qty) ? qty : undefined,
    avg_price: Number.isFinite(avg) ? avg : undefined,
    strike: Number.isFinite(strike) ? strike : undefined,
    exp_date: typeof raw.exp_date === "string" ? raw.exp_date : undefined,
    option_symbol: typeof raw.option_symbol === "string" ? raw.option_symbol : undefined,
    close_reason: typeof raw.close_reason === "string" ? raw.close_reason : undefined,
    option_trans_code:
      typeof raw.option_trans_code === "string" ? raw.option_trans_code : undefined,
    total_credit: Number.isFinite(credit) ? credit : undefined,
    assignment_cost_avg: Number.isFinite(assignAvg) && assignAvg! > 0 ? assignAvg : undefined,
    assignment_realized_pl:
      Number.isFinite(assignPl) && assignPl != null ? assignPl : undefined,
    assignment_income_date: assignIncomeDate || undefined,
    assignment_companion: assignmentCompanion || undefined,
    tax_lot_cost_per_share: Number.isFinite(taxLot) && taxLot! > 0 ? taxLot : undefined,
    underlying_open_price: underlyingOpen > 0 ? underlyingOpen : undefined,
    underlying_close_price: underlyingClose > 0 ? underlyingClose : undefined,
    underlying_price_source:
      typeof raw.underlying_price_source === "string" ? raw.underlying_price_source : undefined,
    open_pop: openPop > 0 ? openPop : undefined,
    open_pop_source:
      typeof raw.open_pop_source === "string" ? raw.open_pop_source : undefined,
    extensions,
    account_id: typeof raw.account_id === "string" ? raw.account_id : null,
    account_label: typeof raw.account_label === "string" ? raw.account_label : null,
    account_name: typeof raw.account_name === "string" ? raw.account_name : null,
    broker_id: typeof raw.broker_id === "string" ? raw.broker_id : null,
    broker_name: typeof raw.broker_name === "string" ? raw.broker_name : null,
    account_short_name: typeof raw.account_short_name === "string" ? raw.account_short_name : null,
    tags,
    source: typeof raw.source === "string" ? raw.source : null,
  };
}

/** Where the underlying stood when a contract was written and when it resolved. */
function incomeLineUnderlying(txn: WheelTransaction | undefined | null): {
  underlyingOpen: number | null;
  underlyingClose: number | null;
  underlyingOpenEstimated: boolean;
  underlyingCloseEstimated: boolean;
} {
  const open = Number(txn?.underlying_open_price);
  const close = Number(txn?.underlying_close_price);
  // A row records only its own spot, so its source describes the close side on a
  // settlement row and the open side on the row that wrote the contract.
  const estimated = txn?.underlying_price_source === "md_day_close_estimated";
  const ownsClose = Boolean(txn?.close_reason) || txn?.action === "buy";
  return {
    underlyingOpen: open > 0 ? open : null,
    underlyingClose: close > 0 ? close : null,
    underlyingOpenEstimated: estimated && !ownsClose,
    underlyingCloseEstimated: estimated && ownsClose,
  };
}

/** The odds the contract was written at, for judging outcomes against them. */
function incomeLineOpenPop(txn: WheelTransaction | undefined | null): { openPop: number | null } {
  const pop = Number(txn?.open_pop);
  return { openPop: pop > 0 ? pop : null };
}

function incomeLineSystemAccount(txn: WheelTransaction | undefined | null): {
  system: string;
  account: string;
} {
  if (!txn) return { system: "", account: "" };
  const meta: HistoryLedgerMetaTxn & { account_name?: string | null } = {
    account_id: txn.account_id,
    account_label: txn.account_label,
    account_name: txn.account_name,
    account_short_name: txn.account_short_name,
    broker_id: txn.broker_id,
    broker_name: txn.broker_name,
    tags: txn.tags,
    extensions: txn.extensions,
    source: txn.source,
  };
  return {
    system: historySystemLabel(meta),
    account: historyAccountLabel(meta),
  };
}

function incomeLineMarks(txn: LedgerRowMarkSource | null | undefined): {
  brokerCode: string;
  brokerColor: string;
  brokerTitle: string;
  accountShort: string;
  accountTitle: string;
  accountId: string;
} {
  const mark = ledgerRowMark(txn);
  return {
    brokerCode: mark?.brokerCode ?? "",
    brokerColor: mark?.brokerColor ?? "",
    brokerTitle: mark?.brokerTitle ?? "",
    accountShort: mark?.accountShort ?? "",
    accountTitle: mark?.accountTitle ?? "",
    accountId: String(txn?.account_id ?? "").trim(),
  };
}

function isCallAssignmentTxn(txn: WheelTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    txn.action === "buy" &&
    txn.side === "call" &&
    txn.close_reason === "assigned"
  );
}

function isPutAssignmentTxn(txn: WheelTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    txn.action === "buy" &&
    txn.side === "put" &&
    txn.close_reason === "assigned"
  );
}

/** Below this many shares a position counts as closed out. */
const FLAT_POSITION_EPSILON = 0.000001;

/** Blend `qty` new shares acquired at `price` into the running average cost.
 * A position holding only rounding residue contributes no cost, so the new
 * lot's price becomes the average outright. */
function blendBuy(pos: AvgPosition, qty: number, price: number): void {
  if (qty <= 0) return;
  if (pos.qty < FLAT_POSITION_EPSILON) {
    pos.qty = qty;
    pos.avg = price;
    return;
  }
  const totalCost = pos.qty * pos.avg + qty * price;
  pos.qty += qty;
  pos.avg = pos.qty > 0 ? totalCost / pos.qty : 0;
}

/** Remove `qty` shares; the average cost of shares that remain is unchanged
 * (standard average-cost accounting — "whenever I get a few shares, I
 * recalculate the average and use it as a north star").
 *
 * Going flat clears the average so the next put assignment's strike starts a
 * fresh cycle instead of blending into a closed one. Fractional lots and
 * rounding can leave a sub-share residue, which must still count as flat. */
function reduceQty(pos: AvgPosition, qty: number): void {
  if (qty <= 0) return;
  pos.qty = Math.max(0, pos.qty - qty);
  if (pos.qty < FLAT_POSITION_EPSILON) {
    pos.qty = 0;
    pos.avg = 0;
  }
}

/**
 * Share cost for Income / assignment / ordinary stock-sale P/L replay.
 * Own-ledger weighted avg (assignment lots = strike; premiums excluded).
 * Not RH tax-lot / FIFO / adjusted avg — those are separate views.
 */
function sharePurchaseCost(txn: WheelTransaction): number {
  return Number(txn.avg_price) || 0;
}

/** One share lot a stock buy contributed, held for the assignment that claims it. */
interface ShareCoverLot {
  shares: number;
  transition: SharePositionTransition;
}

/** Stock buy lots a put assignment can be reported through, keyed by day, ticker
 *  and price. Assignment lots are recorded at the strike (rule 2), so a buy at
 *  another price is a separate purchase and must not be credited to the contract. */
interface ShareCoverIndex {
  lots: Map<string, ShareCoverLot[]>;
  /** Days that saw any stock buy for a ticker+account, whatever the price. */
  hasStockBuy(date: string, ticker: string, accountId?: string | null): boolean;
}

function shareCoverKey(
  date: string,
  ticker: string,
  price: number,
  accountId?: string | null,
): string {
  const book = String(accountId ?? "").trim() || "_";
  return `${book}|${date.slice(0, 10)}|${ticker}|${Math.round(price * 100)}`;
}

function createShareCoverIndex(txns: WheelTransaction[]): ShareCoverIndex {
  const days = new Set<string>();
  for (const txn of txns) {
    if (
      txn.instrument_type === "stock" &&
      txn.action === "buy" &&
      txn.ticker &&
      txn.trade_date
    ) {
      days.add(`${shareAvgBookKey(txn)}|${txn.trade_date.slice(0, 10)}|${txn.ticker}`);
    }
  }
  return {
    lots: new Map(),
    hasStockBuy: (date, ticker, accountId) =>
      days.has(`${String(accountId ?? "").trim() || "_"}|${date.slice(0, 10)}|${ticker}`),
  };
}

/** Claims up to `shares` from the lots a stock buy already blended in, trying
 *  each day the lot could have landed on.
 *  Returns the shares covered and the position span those lots moved through. */
function claimShareCover(
  cover: ShareCoverIndex | undefined,
  dates: readonly string[],
  ticker: string,
  price: number,
  shares: number,
  accountId?: string | null,
): { covered: number; transition: SharePositionTransition | null } {
  if (!cover || shares <= 0) return { covered: 0, transition: null };

  let remaining = shares;
  let first: SharePositionTransition | null = null;
  let last: SharePositionTransition | null = null;
  for (const date of dates) {
    const lots = cover.lots.get(shareCoverKey(date, ticker, price, accountId));
    while (remaining > 0 && lots?.length) {
      const lot = lots[0]!;
      if (!first) first = lot.transition;
      last = lot.transition;
      if (lot.shares > remaining) {
        lot.shares -= remaining;
        remaining = 0;
        break;
      }
      remaining -= lot.shares;
      lots.shift();
    }
    if (remaining <= 0) break;
  }
  return {
    covered: shares - remaining,
    transition:
      first && last
        ? {
            sharesBefore: first.sharesBefore,
            avgBefore: first.avgBefore,
            sharesAfter: last.sharesAfter,
            avgAfter: last.avgAfter,
          }
        : null,
  };
}

/** Days an assignment's stock lot can sit on. The row replays on its expiry
 * date, but an early assignment delivers shares the day it settles, so the lot
 * is dated before expiry and a lookup on the expiry date alone would miss it. */
function assignmentCoverDates(txn: WheelTransaction): string[] {
  const dates = [avgCostReplayDate(txn), txn.trade_date, txn.assignment_income_date];
  return [...new Set(dates.map((date) => String(date ?? "").slice(0, 10)).filter(Boolean))];
}

/** Effective date for avg-cost replay — assignments book on exp/assign date, not
 * settlement or contract open. */
function avgCostReplayDate(txn: WheelTransaction): string {
  if (isPutAssignmentTxn(txn) || isCallAssignmentTxn(txn)) {
    return assignmentEventDate(txn) ?? txn.trade_date ?? "";
  }
  return txn.trade_date ?? "";
}

/** For put assignments: the span the stock legs that stood in for it moved
 *  through, so the assignment row can report the shares they delivered. */
function applyAvgCostEvent(
  txn: WheelTransaction,
  positions: Map<string, AvgPosition>,
  cover?: ShareCoverIndex,
): SharePositionTransition | null {
  if (txn.instrument_type === "stock" && txn.ticker) {
    // Call-assignment stock sells are consumed via the call ASGN option leg.
    // Put-assignment stock buys must still blend (strike avg on the stock lot).
    if (txn.assignment_companion && txn.action === "sell") return null;
    const key = shareAvgPositionKey(txn);
    const pos = positions.get(key) ?? { qty: 0, avg: 0 };
    const qty = Number(txn.quantity) || 0;
    const price = sharePurchaseCost(txn);
    const before: SharePositionTransition = {
      sharesBefore: Math.round(pos.qty),
      avgBefore: pos.avg,
      sharesAfter: 0,
      avgAfter: 0,
    };
    if (txn.action === "buy") blendBuy(pos, qty, price);
    else if (txn.action === "sell") reduceQty(pos, qty);
    positions.set(key, pos);
    if (cover && txn.action === "buy" && qty > 0 && price > 0 && txn.trade_date) {
      const lotKey = shareCoverKey(txn.trade_date, txn.ticker, price, txn.account_id);
      const lots = cover.lots.get(lotKey) ?? [];
      lots.push({
        shares: Math.round(qty),
        transition: { ...before, sharesAfter: Math.round(pos.qty), avgAfter: pos.avg },
      });
      cover.lots.set(lotKey, lots);
    }
    return null;
  }

  if (isPutAssignmentTxn(txn) && txn.ticker) {
    const dates = assignmentCoverDates(txn);
    const key = shareAvgPositionKey(txn);
    const pos = positions.get(key) ?? { qty: 0, avg: 0 };
    const contracts = Number(txn.quantity) || 0;
    const strike = Number(txn.strike) || 0;
    const shares = Math.round(contracts * 100);
    // Claimed only to report which lot delivered the shares; the lot itself has
    // already blended them.
    const { transition } = claimShareCover(
      cover,
      dates,
      txn.ticker,
      strike,
      shares,
      txn.account_id,
    );
    // Stock lots are the share source of truth even when the contract count says
    // more (the broker agrees with the lots), so a same-day lot for this ticker
    // suppresses synthesis entirely — only bare assignments blend from the strike.
    if (dates.some((date) => cover?.hasStockBuy(date, txn.ticker!, txn.account_id))) {
      return transition;
    }
    if (shares > 0 && strike > 0) blendBuy(pos, shares, strike);
    positions.set(key, pos);
    return transition;
  }

  if (!isCallAssignmentTxn(txn) || !txn.ticker) return null;

  const key = shareAvgPositionKey(txn);
  const pos = positions.get(key) ?? { qty: 0, avg: 0 };
  const contracts = Number(txn.quantity) || 0;
  const shares = Math.round(contracts * 100);
  reduceQty(pos, shares);
  positions.set(key, pos);
  return null;
}


function txnCode(txn: WheelTransaction): string {
  return (txn.option_trans_code ?? "").toUpperCase();
}

function contractKey(txn: WheelTransaction): string | null {
  if (txn.instrument_type !== "option" || !txn.ticker) return null;
  // Keep RH / TT / manual books separate when the same OCC contract appears in both.
  const book =
    txn.account_id ||
    txn.extensions?.broker ||
    txn.source ||
    "";
  return `${book}|${txn.ticker}|${txn.side ?? ""}|${txn.strike ?? ""}|${txn.exp_date ?? ""}`;
}

function txnContracts(txn: WheelTransaction): number {
  return Number(txn.quantity) || 0;
}

/** Calendar DTE from trade/book date to option expiration. */
function optionDteOnDate(
  tradeDate: string | undefined,
  expDate: string | undefined,
): number | null {
  if (!tradeDate || !expDate) return null;
  const start = Date.parse(`${tradeDate}T12:00:00Z`);
  const end = Date.parse(`${expDate}T12:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.round((end - start) / 86_400_000);
}

/**
 * Gross option premium/debit in dollars (fees/commissions excluded).
 *
 * Broker `total_credit` is often *net* cash (Tastytrade total = premium ± fees).
 * Using Math.abs(net) understates STO income (e.g. HPE $1 premium → −$0.12 net →
 * $0.12 P/L) and skews month totals. Prefer:
 * 1) net − fees − commissions when those extensions exist
 * 2) avg×qty that matches the broker total's scale (TT: $/contract; RH: $/share×100)
 * 3) abs(total_credit) / avg×qty×100 fallbacks
 */
function txnNotional(txn: WheelTransaction): number {
  const qty = txnContracts(txn);
  const avg = Math.abs(Number(txn.avg_price) || 0);
  const perContract = qty > 0 && avg > 0 ? qty * avg : 0;
  const perShare = qty > 0 && avg > 0 ? qty * avg * 100 : 0;

  const net = Number(txn.total_credit);
  const fees = extensionNumber({ extensions: txn.extensions }, "fees");
  const commissions = extensionNumber({ extensions: txn.extensions }, "commissions");
  const hasFeeExtras =
    (fees != null && Number.isFinite(fees) && fees !== 0) ||
    (commissions != null && Number.isFinite(commissions) && commissions !== 0);

  if (Number.isFinite(net) && hasFeeExtras) {
    const gross = net - (fees ?? 0) - (commissions ?? 0);
    if (Math.abs(gross) > 1e-9) return Math.abs(gross);
  }

  if (Number.isFinite(net) && net !== 0) {
    const absNet = Math.abs(net);
    if (perContract > 0 || perShare > 0) {
      const dContract = perContract > 0 ? Math.abs(perContract - absNet) : Number.POSITIVE_INFINITY;
      const dShare = perShare > 0 ? Math.abs(perShare - absNet) : Number.POSITIVE_INFINITY;
      // Allow typical per-contract fees (~$1–2) when matching scale.
      const feeSlack = 5;
      if (dContract <= dShare && dContract <= feeSlack) return perContract;
      if (dShare <= feeSlack) return perShare;
      // Fee-eaten nets (absNet ≪ gross): pick the closer avg scale.
      if (Number.isFinite(dContract) || Number.isFinite(dShare)) {
        return dContract <= dShare ? perContract : perShare;
      }
    }
    return absNet;
  }

  if (perShare > 0) return perShare;
  return perContract;
}

function isAssignedOptionTxn(txn: WheelTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    (txn.close_reason === "assigned" || txnCode(txn) === "OASGN")
  );
}

/**
 * Older ledger rows can omit close_reason / OASGN on the option leg. Match
 * zero-cost option settlements to the generated stock leg:
 * put assignment -> stock buy; call assignment -> stock sell.
 */
function inferAssignmentsFromStockLegs(txns: WheelTransaction[]): WheelTransaction[] {
  const adjusted = txns.map((txn) => ({ ...txn }));
  const byId = new Map(
    adjusted
      .filter((txn): txn is WheelTransaction & { id: number } => txn.id != null)
      .map((txn) => [txn.id, txn]),
  );
  const stockLegs = new Map<
    string,
    Array<{ txn: WheelTransaction; remaining: number }>
  >();

  for (const txn of adjusted) {
    if (txn.instrument_type !== "stock" || !txn.ticker || !txn.trade_date) continue;
    if (txn.action !== "buy" && txn.action !== "sell") continue;
    const quantity = Number(txn.quantity) || 0;
    if (quantity <= 0) continue;
    const key = `${txn.trade_date}|${txn.ticker}|${txn.action}`;
    const legs = stockLegs.get(key) ?? [];
    legs.push({ txn, remaining: quantity });
    stockLegs.set(key, legs);
  }

  for (const txn of adjusted) {
    if (
      txn.instrument_type !== "option" ||
      txn.action !== "buy" ||
      !txn.ticker ||
      !txn.trade_date ||
      (txn.side !== "put" && txn.side !== "call")
    ) {
      continue;
    }

    const contracts = txnContracts(txn);
    const shares = Math.round(contracts * 100);
    if (shares <= 0) continue;

    const explicitAssignment = isAssignedOptionTxn(txn);
    const zeroCostSettlement =
      txnNotional(txn) === 0 &&
      txn.exp_date != null &&
      txn.trade_date >= txn.exp_date;

    const linked =
      (txn.linked_transaction_id != null
        ? byId.get(txn.linked_transaction_id)
        : undefined) ??
      adjusted.find(
        (candidate) =>
          txn.id != null &&
          candidate.linked_transaction_id === txn.id &&
          candidate.instrument_type === "stock",
      );
    const stockAction = txn.side === "put" ? "buy" : "sell";
    const linkedStock =
      linked?.instrument_type === "stock" &&
      linked.action === stockAction &&
      linked.ticker === txn.ticker &&
      (!linked.trade_date || linked.trade_date === txn.trade_date) &&
      (Number(linked.quantity) || 0) >= shares
        ? linked
        : undefined;

    const key = `${txn.trade_date}|${txn.ticker}|${stockAction}`;
    const quantityMatched = (stockLegs.get(key) ?? []).find(
      (leg) => leg.remaining >= shares,
    );
    const companion = linkedStock
      ? { txn: linkedStock, remaining: Number(linkedStock.quantity) || shares }
      : quantityMatched;
    if (
      !explicitAssignment &&
      !linkedStock &&
      !(zeroCostSettlement && quantityMatched)
    ) {
      continue;
    }

    txn.close_reason = "assigned";
    txn.option_trans_code = "OASGN";
    if (companion) {
      if (companion === quantityMatched) companion.remaining -= shares;
      companion.txn.assignment_companion = true;
      if (
        txn.linked_transaction_id == null &&
        txn.id != null &&
        companion.txn.id != null
      ) {
        txn.inferred_linked_transaction_ids = [
          ...new Set([
            ...(txn.inferred_linked_transaction_ids ?? []),
            companion.txn.id,
          ]),
        ];
        companion.txn.inferred_linked_transaction_ids = [
          ...new Set([
            ...(companion.txn.inferred_linked_transaction_ids ?? []),
            txn.id,
          ]),
        ];
      }
    }
  }

  return adjusted;
}

interface TransactionLinkMeta {
  linkedIds: number[];
  status: "ledger" | "inferred" | "unlinked";
}

/** Build two-way trace metadata. The ledger may store linked_transaction_id
 * on only one side, so add a reverse lookup for the companion row. */
function buildTransactionLinkMeta(
  txns: WheelTransaction[],
): Map<number, TransactionLinkMeta> {
  const result = new Map<number, TransactionLinkMeta>();

  const addLink = (
    id: number,
    linkedId: number,
    status: "ledger" | "inferred",
  ) => {
    if (id === linkedId) return;
    const prev = result.get(id);
    result.set(id, {
      linkedIds: [...new Set([...(prev?.linkedIds ?? []), linkedId])],
      status: prev?.status === "ledger" || status === "ledger" ? "ledger" : "inferred",
    });
  };

  for (const txn of txns) {
    if (txn.id == null) continue;
    if (txn.linked_transaction_id != null) {
      addLink(txn.id, txn.linked_transaction_id, "ledger");
      addLink(txn.linked_transaction_id, txn.id, "ledger");
    }
    for (const linkedId of txn.linked_transaction_ids ?? []) {
      addLink(txn.id, linkedId, "ledger");
      addLink(linkedId, txn.id, "ledger");
    }
    for (const linkedId of txn.wheel_pair_linked_ids ?? []) {
      addLink(txn.id, linkedId, "ledger");
      addLink(linkedId, txn.id, "ledger");
    }
    for (const linkedId of txn.inferred_linked_transaction_ids ?? []) {
      addLink(txn.id, linkedId, "inferred");
      addLink(linkedId, txn.id, "inferred");
    }
  }

  return result;
}

function transactionLinkMeta(
  txn: WheelTransaction,
  links: Map<number, TransactionLinkMeta>,
): TransactionLinkMeta {
  if (txn.id == null) return { linkedIds: [], status: "unlinked" };
  return links.get(txn.id) ?? { linkedIds: [], status: "unlinked" };
}

function isBuyToOpenTxn(txn: WheelTransaction): boolean {
  if (txn.instrument_type !== "option" || txn.action !== "buy") return false;
  if (txnCode(txn) === "BTO") return true;
  return isRhOpeningLongBuy(txn);
}

/** Worthless or OEXP-settled expiry — not a cash BTC. Includes ledger rows
 * with close_reason "expired" / code OEXP, plus orphan zero-cost buys on or
 * after the contract's exp date (Stock Forge's legacy expiration format). */
function isOptionExpirationTxn(txn: WheelTransaction): boolean {
  if (txn.instrument_type !== "option" || txn.action !== "buy") return false;
  if (isAssignedOptionTxn(txn)) return false;
  if (txn.close_reason === "expired" || txnCode(txn) === "OEXP") return true;
  return (
    txnNotional(txn) === 0 &&
    txn.trade_date != null &&
    txn.exp_date != null &&
    txn.trade_date >= txn.exp_date
  );
}

function isRhOpeningLongBuy(txn: WheelTransaction): boolean {
  const opening = String(txn.extensions?.opening_strategy ?? "").toLowerCase();
  return opening.startsWith("long_");
}

function isRhClosingShortBuy(txn: WheelTransaction): boolean {
  const closing = String(txn.extensions?.closing_strategy ?? "").toLowerCase();
  if (closing.startsWith("short_")) return true;
  return String(txn.extensions?.pnl_kind ?? "").toLowerCase() === "btc";
}

function isRhClosingLongSell(txn: WheelTransaction): boolean {
  const closing = String(txn.extensions?.closing_strategy ?? "").toLowerCase();
  return closing.startsWith("long_");
}

/** PNL history rows duplicate order fills — income uses order rows only. */
function isPnlTradeHistoryRow(txn: WheelTransaction): boolean {
  return txn.extensions?.import_source === "pnl_trade_history";
}

/**
 * P/L history rows are skipped as order-fill duplicates, but Robinhood's orders
 * feed never reports an expiration, and it misses the odd assignment or cheap
 * buy-to-close as well. Those rows are the only record of the settlement, so
 * they are admitted up to the contract quantity the broker left unaccounted
 * for — a P/L row that repeats a reported fill still finds nothing open and
 * stays hidden.
 */
function visiblePnlSettlementIds(txns: WheelTransaction[]): Set<number> {
  const unsettled = new Map<string, number>();
  for (const txn of txns) {
    if (txn.instrument_type !== "option" || isPnlTradeHistoryRow(txn)) continue;
    const key = contractKey(txn);
    const qty = txnContracts(txn);
    const typed = optionTxnTypeLabel(txn);
    if (!key || qty <= 0 || !typed) continue;
    const opening = typed.kind === "sto" || typed.kind === "bto";
    unsettled.set(key, (unsettled.get(key) ?? 0) + (opening ? qty : -qty));
  }

  const ids = new Set<number>();
  const chronological = [...txns].sort((a, b) =>
    String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? "")),
  );
  for (const txn of chronological) {
    if (txn.instrument_type !== "option" || !isPnlTradeHistoryRow(txn)) continue;
    if (txn.id == null) continue;
    const key = contractKey(txn);
    const qty = txnContracts(txn);
    const typed = optionTxnTypeLabel(txn);
    if (!key || qty <= 0 || !typed) continue;
    if (typed.kind === "sto" || typed.kind === "bto") continue;
    const open = unsettled.get(key) ?? 0;
    if (open < qty) continue;
    unsettled.set(key, open - qty);
    ids.add(txn.id);
  }
  return ids;
}

function isBuyToCloseTxn(txn: WheelTransaction): boolean {
  if (txn.instrument_type !== "option" || txn.action !== "buy") return false;
  if (isBuyToOpenTxn(txn)) return false;
  if (isOptionExpirationTxn(txn)) return false;
  if (isRhClosingShortBuy(txn)) return true;
  if (txnCode(txn) === "BTC") return true;
  if (txn.close_reason === "assigned" || txn.close_reason === "expired") return false;
  const code = txnCode(txn);
  if (code && code !== "BTC") return false;
  const credit = Number(txn.total_credit);
  return Number.isFinite(credit) && credit > 0;
}

function isSellToCloseTxn(txn: WheelTransaction): boolean {
  if (txn.instrument_type !== "option" || txn.action !== "sell") return false;
  return txnCode(txn) === "STC" || isRhClosingLongSell(txn);
}

function isSellToOpenTxn(txn: WheelTransaction): boolean {
  if (txn.instrument_type !== "option" || txn.action !== "sell") return false;
  const code = txnCode(txn);
  if (code === "STC") return false;
  if (code === "STO") return true;
  if (code) return false;
  return !txn.close_reason;
}

interface OptionOpenInterest {
  shortQty: number;
  longQty: number;
}

/** Ledger rows often omit option_trans_code. Replay chronologically and
 * infer STO/BTC/STO/STC from open short/long interest per contract. */
function classifyOptionIncomeTxns(txns: WheelTransaction[]): WheelTransaction[] {
  const adjusted = inferAssignmentsFromStockLegs(txns);
  // Snapshot APIs return newest-first; STO/BTC inference must replay chronologically.
  const chronological = [...adjusted].sort(
    (a, b) =>
      String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? "")) ||
      (Number(a.id) || 0) - (Number(b.id) || 0),
  );
  const open = new Map<string, OptionOpenInterest>();

  for (const txn of chronological) {
    if (txn.instrument_type !== "option") continue;
    const key = contractKey(txn);
    if (!key) continue;

    const qty = txnContracts(txn);
    if (qty <= 0) continue;

    const pos = open.get(key) ?? { shortQty: 0, longQty: 0 };
    const code = txnCode(txn);

    if (txn.close_reason === "assigned") {
      pos.shortQty = Math.max(0, pos.shortQty - qty);
      open.set(key, pos);
      continue;
    }

    if (txn.close_reason === "expired" || code === "OEXP") {
      txn.option_trans_code = "OEXP";
      pos.shortQty = Math.max(0, pos.shortQty - qty);
      open.set(key, pos);
      continue;
    }

    if (txn.action === "sell") {
      if (code === "STC" || isRhClosingLongSell(txn) || (!code && pos.longQty >= qty)) {
        txn.option_trans_code = "STC";
        pos.longQty = Math.max(0, pos.longQty - qty);
      } else {
        txn.option_trans_code = "STO";
        pos.shortQty += qty;
      }
    } else if (txn.action === "buy") {
      if (isRhOpeningLongBuy(txn) || code === "BTO") {
        txn.option_trans_code = "BTO";
        pos.longQty += qty;
      } else if (
        code === "BTC" ||
        isRhClosingShortBuy(txn) ||
        isBuyToCloseTxn(txn) ||
        (!code && pos.shortQty >= qty)
      ) {
        txn.option_trans_code = isOptionExpirationTxn(txn) ? "OEXP" : "BTC";
        pos.shortQty = Math.max(0, pos.shortQty - qty);
      }
    }

    open.set(key, pos);
  }

  return chronological;
}

/** Ledger-style type codes for the day table (STO, BTC, BTO, STC, ASGN, EXP). */
function optionTxnTypeLabel(txn: WheelTransaction): {
  kind: IncomeLine["kind"];
  label: string;
} | null {
  if (isBuyToOpenTxn(txn)) return { kind: "bto", label: "BTO" };
  if (isSellToCloseTxn(txn)) return { kind: "stc", label: "STC" };
  if (isAssignedOptionTxn(txn)) return { kind: "asgn", label: "ASG" };
  if (isOptionExpirationTxn(txn)) return { kind: "exp", label: "EXP" };
  const code = txnCode(txn);
  if (code === "BTC") return { kind: "btc", label: "BTC" };
  if (code === "STO" || isSellToOpenTxn(txn)) return { kind: "sto", label: "STO" };
  return null;
}

/** Opening trade_date for income rows — ledger open_close links only (no FIFO). */
function resolveIncomeLineOpenDate(
  txn: WheelTransaction,
  byId: Map<number, WheelTransaction>,
  links: Map<number, TransactionLinkMeta>,
): string {
  const typed = optionTxnTypeLabel(txn);
  if (typed?.kind === "sto" || typed?.kind === "bto") {
    return txn.trade_date ?? "—";
  }
  if (txn.id != null) {
    for (const linkedId of links.get(txn.id)?.linkedIds ?? []) {
      const linked = byId.get(linkedId);
      if (!linked || linked.instrument_type !== "option") continue;
      const linkedType = optionTxnTypeLabel(linked);
      if (linkedType?.kind === "sto" || linkedType?.kind === "bto") {
        return linked.trade_date ?? "—";
      }
    }
  }
  return "—";
}

function incomeLineExp(expDate: string | undefined | null): string {
  const exp = String(expDate ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(exp) ? exp : "—";
}

function assignIncomeInScope(assignDate: string | undefined, scopeYear: number | null): boolean {
  if (scopeYear == null) return true;
  return parseYear(assignDate) === scopeYear;
}

/** Assignment P/L month = when the call assigns (option exp date). Ledger
 * trade_date / stock settlement can fall in the following month. */
function assignmentEventDate(txn: WheelTransaction): string | undefined {
  return txn.exp_date || txn.assignment_income_date || txn.trade_date;
}

type CallAssignIncomeAgg = { income: number; avgCost: number; shares: number };

/** Match call-assignment P/L to the ASGN option row. Income events may be
 * keyed by assignment_income_date / exp_date while the ledger row sits on
 * trade_date — try those dates, then any same-contract key. */
function takeCallAssignIncomeAgg(
  map: Map<string, CallAssignIncomeAgg>,
  txn: WheelTransaction,
  tradeDate: string,
): CallAssignIncomeAgg | undefined {
  const ticker = txn.ticker ?? "";
  const strike = String(Number(txn.strike) || txn.strike || "");
  const exp = txn.exp_date ?? "";
  const dates: string[] = [];
  for (const d of [tradeDate, txn.exp_date, txn.assignment_income_date, txn.trade_date]) {
    if (d && !dates.includes(d)) dates.push(d);
  }
  for (const d of dates) {
    const key = `${d}|${ticker}|call|${strike}|${exp}`;
    const agg = map.get(key);
    if (agg) {
      map.delete(key);
      return agg;
    }
  }
  for (const [key, agg] of map) {
    const [, keyTicker, side, keyStrike, keyExp] = key.split("|");
    if (
      side === "call" &&
      keyTicker === ticker &&
      String(Number(keyStrike) || keyStrike || "") === strike &&
      keyExp === exp
    ) {
      map.delete(key);
      return agg;
    }
  }
  return undefined;
}

function extensionNumberFromTxn(txn: WheelTransaction, key: string): number | undefined {
  const top = (txn as Record<string, unknown>)[key];
  if (top != null && Number.isFinite(Number(top))) return Number(top);
  const nested = txn.extensions?.[key];
  if (nested != null && nested !== "") {
    const n = Number(nested);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * Same-day ordering for avg-cost replay: disposals settle first, so a call
 * assigned on the same day a new put assigns prices against the shares that
 * were actually on hand — not against the next cycle's lot blended in early.
 */
/** Same-day order: take in put/buy shares first, then call assignments, then
 *  sells. Expiration often delivers puts and calls together — the new put
 *  lots must raise avg cost before call P/L is booked, or losses (strike
 *  below that avg) never appear. */
function avgCostReplayPriority(txn: WheelTransaction): number {
  if (isPutAssignmentTxn(txn)) return 0;
  if (txn.instrument_type === "stock" && txn.action === "buy") return 1;
  if (isCallAssignmentTxn(txn)) return 2;
  if (txn.instrument_type === "stock" && txn.action === "sell") return 3;
  return 2;
}

function sortForAvgCostReplay(txns: WheelTransaction[]): WheelTransaction[] {
  return [...txns].sort(
    (a, b) =>
      avgCostReplayDate(a).localeCompare(avgCostReplayDate(b)) ||
      avgCostReplayPriority(a) - avgCostReplayPriority(b) ||
      (Number(a.id) || 0) - (Number(b.id) || 0),
  );
}

/**
 * A short call that already has a closing buy row was closed for cash (or
 * expired) rather than assigned, so it cannot explain an assignment share sale.
 */
function shortCallWasClosedWithoutAssignment(
  sto: WheelTransaction,
  txns: WheelTransaction[],
): boolean {
  const key = contractKey(sto);
  if (!key) return false;
  return txns.some(
    (candidate) =>
      candidate.id !== sto.id &&
      candidate.action === "buy" &&
      contractKey(candidate) === key,
  );
}

/** Matching short call (STO) expiring on a stock sell date — own-ledger assign detection. */
function findMatchingCallStoForAssignmentStockSell(
  stockTxn: WheelTransaction,
  txns: WheelTransaction[],
): WheelTransaction | undefined {
  const shares = Math.round(Number(stockTxn.quantity) || 0);
  const contracts = shares / 100;
  if (!(shares > 0) || !(contracts > 0) || !stockTxn.ticker || !stockTxn.trade_date) {
    return undefined;
  }
  const salePrice = Number(stockTxn.avg_price) || 0;
  const matches = txns.filter(
    (o) =>
      o.instrument_type === "option" &&
      o.action === "sell" &&
      o.side === "call" &&
      o.ticker === stockTxn.ticker &&
      o.exp_date === stockTxn.trade_date &&
      Math.abs((Number(o.quantity) || 0) - contracts) < 0.001 &&
      !shortCallWasClosedWithoutAssignment(o, txns),
  );
  if (!matches.length) return undefined;
  if (!(salePrice > 0)) return matches[0];
  return [...matches].sort(
    (a, b) =>
      Math.abs(salePrice - (Number(a.strike) || 0)) -
      Math.abs(salePrice - (Number(b.strike) || 0)),
  )[0];
}

function callAssignIncomeAtAvg(
  strike: number,
  shares: number,
  avg: number,
  sharesOnHand: number,
): number {
  if (!(strike > 0) || !(shares > 0) || !(avg > 0)) return 0;
  const onHand = sharesOnHand > 0 ? Math.min(shares, Math.round(sharesOnHand)) : 0;
  return onHand > 0 ? (strike - avg) * onHand : 0;
}

/** One inventory event in a call-assignment cost trace. */
export interface AssignTraceStep {
  date: string;
  kind: "put_assign" | "stock_buy" | "stock_sell" | "call_assign";
  label: string;
  txnId?: number;
  /** Signed share delta (+ acquired, − disposed). */
  shares: number;
  /** Per-share cost for acquisitions. */
  price: number | null;
  qtyAfter: number;
  avgAfter: number;
  /** True for the call whose trace this is. */
  isTarget?: boolean;
}

export interface AssignTrace {
  ticker: string;
  date: string;
  strike: number;
  callShares: number;
  /** The opening STO of this same call, via ledger open_close links. */
  opening: {
    txnId?: number;
    date: string;
    contracts: number;
    premiumPerShare: number;
    credit: number;
  } | null;
  steps: AssignTraceStep[];
  avgAtAssign: number;
  sharesCounted: number;
  pnl: number;
}

/**
 * Replay share inventory up to a call assignment so the UI can show how the
 * average cost was built. Uses the same ordering and blending helpers as
 * listAssignIncomeEvents, so the numbers shown always match booked income.
 */
export function buildCallAssignTrace(
  txns: WheelTransaction[],
  callTxnId: number,
): AssignTrace | null {
  const chronological = sortForAvgCostReplay(txns);
  const target = chronological.find(
    (txn) => txn.id === callTxnId && isCallAssignmentTxn(txn),
  );
  if (!target || !target.ticker) return null;

  const ticker = target.ticker;
  const cover = createShareCoverIndex(chronological);
  const assignmentSellKeys = assignmentLinkedStockSellKeys(chronological);
  for (const txn of chronological) {
    if (txn.instrument_type === "stock" && txn.action === "sell" && txn.ticker) {
      const shares = Math.round(Number(txn.quantity) || 0);
      if (isAssignmentLinkedStockSell(txn, shares, assignmentSellKeys)) {
        txn.assignment_companion = true;
      }
    }
  }

  // Assignment stock lots are reported as the put assignment that produced them.
  const putAssignsByDate = new Map<string, WheelTransaction>();
  for (const txn of chronological) {
    if (txn.ticker === ticker && isPutAssignmentTxn(txn)) {
      putAssignsByDate.set(String(avgCostReplayDate(txn)).slice(0, 10), txn);
    }
  }

  const positions = new Map<string, AvgPosition>();
  const steps: AssignTraceStep[] = [];
  let avgAtAssign = 0;
  let sharesCounted = 0;
  let pnl = 0;
  const positionKey = shareAvgPositionKey(target, ticker);

  for (const txn of chronological) {
    const isTarget = txn.id === callTxnId;
    const sameTicker = txn.ticker === ticker && shareAvgBookKey(txn) === shareAvgBookKey(target);
    const before = positions.get(positionKey);

    if (isTarget) {
      const avg = before?.avg ?? 0;
      const onHand = Math.round(before?.qty ?? 0);
      const callShares = Math.round((Number(txn.quantity) || 0) * 100);
      avgAtAssign = avg;
      sharesCounted = Math.min(callShares, Math.max(0, onHand));
      pnl =
        Number(txn.strike) > 0 && sharesCounted > 0 && avg > 0
          ? (Number(txn.strike) - avg) * sharesCounted
          : 0;
    }

    const qtyBefore = Math.round(before?.qty ?? 0);
    applyAvgCostEvent(txn, positions, cover);

    if (!sameTicker) {
      if (isTarget) break;
      continue;
    }

    const after = positions.get(positionKey);
    const qtyAfter = Math.round(after?.qty ?? 0);
    const avgAfter = after?.avg ?? 0;
    const date = String(avgCostReplayDate(txn)).slice(0, 10);
    const step = (
      kind: AssignTraceStep["kind"],
      label: string,
      shares: number,
      price: number | null,
    ) => {
      steps.push({ date, kind, label, txnId: txn.id, shares, price, qtyAfter, avgAfter, isTarget });
    };

    if (isTarget) {
      const shares = Math.round((Number(txn.quantity) || 0) * 100);
      step("call_assign", `Call assigned $${Number(txn.strike)}`, -shares, Number(txn.strike));
      break;
    }
    if (isPutAssignmentTxn(txn)) {
      // Shares a stock leg already brought in are reported on that row as the put
      // assignment they came from, so only the synthesized part belongs here.
      const shares = qtyAfter - qtyBefore;
      if (shares <= 0) continue;
      step("put_assign", `Put assigned $${Number(txn.strike)}`, shares, Number(txn.strike));
    } else if (txn.instrument_type === "stock" && txn.action === "buy") {
      const shares = Math.round(Number(txn.quantity) || 0);
      const sourcePut = putAssignsByDate.get(date);
      if (sourcePut) {
        steps.push({
          date,
          kind: "put_assign",
          label: `Put assigned $${Number(sourcePut.strike)}`,
          txnId: sourcePut.id ?? txn.id,
          shares,
          price: sharePurchaseCost(txn),
          qtyAfter,
          avgAfter,
        });
      } else {
        step("stock_buy", "Shares bought", shares, sharePurchaseCost(txn));
      }
    } else if (txn.instrument_type === "stock" && txn.action === "sell") {
      // Assignment companions are accounted on the option leg — no inventory change.
      if (txn.assignment_companion) continue;
      const shares = Math.round(Number(txn.quantity) || 0);
      step("stock_sell", "Shares sold", -shares, sharePurchaseCost(txn));
    } else if (isCallAssignmentTxn(txn)) {
      const shares = Math.round((Number(txn.quantity) || 0) * 100);
      step("call_assign", `Earlier call assigned $${Number(txn.strike)}`, -shares, Number(txn.strike));
    }
  }

  // Only the acquisitions since the position was last flat build this average.
  let firstRelevant = 0;
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    if (steps[i].qtyAfter === 0 && !steps[i].isTarget) {
      firstRelevant = i + 1;
      break;
    }
  }

  const byId = new Map<number, WheelTransaction>();
  for (const txn of chronological) {
    if (txn.id != null) byId.set(txn.id, txn);
  }
  const links = buildTransactionLinkMeta(chronological);
  let opening: AssignTrace["opening"] = null;
  for (const linkedId of links.get(callTxnId)?.linkedIds ?? []) {
    const linked = byId.get(linkedId);
    if (!linked || linked.instrument_type !== "option") continue;
    if (optionTxnTypeLabel(linked)?.kind !== "sto") continue;
    const contracts = Number(linked.quantity) || 0;
    const premiumPerShare = Number(linked.avg_price) || 0;
    opening = {
      txnId: linked.id,
      date: String(linked.trade_date ?? "").slice(0, 10),
      contracts,
      premiumPerShare,
      credit: premiumPerShare * contracts * 100,
    };
    break;
  }

  return {
    ticker,
    date: String(assignmentEventDate(target) ?? "").slice(0, 10),
    strike: Number(target.strike) || 0,
    callShares: Math.round((Number(target.quantity) || 0) * 100),
    opening,
    steps: steps.slice(firstRelevant),
    avgAtAssign,
    sharesCounted,
    pnl,
  };
}

interface AssignIncomeEvent {
  date: string;
  amount: number;
  ticker: string;
  strike: number;
  contracts: number;
  avg: number;
  exp_date?: string;
}

/** Share inventory around one ledger row, as seen by the avg-cost replay. */
export interface ShareAvgCostSnapshot {
  /** Shares held / running average cost before this row moved inventory. */
  sharesBefore: number;
  avgBefore: number;
  /** Shares held / running average cost after it. */
  sharesAfter: number;
  avgAfter: number;
  /** Call assignments only: avg cost charged against the strike. */
  assignAvg?: number;
  /** Call assignments only: (strike − avg) × shares assigned away. */
  assignPnl?: number;
  assignShares?: number;
}

interface SharePositionTransition {
  sharesBefore: number;
  avgBefore: number;
  sharesAfter: number;
  avgAfter: number;
}

function replayShareAvgCost(txns: WheelTransaction[]): {
  events: AssignIncomeEvent[];
  snapshots: Map<number, ShareAvgCostSnapshot>;
} {
  const chronological = sortForAvgCostReplay(txns);
  const positions = new Map<string, AvgPosition>();
  const cover = createShareCoverIndex(chronological);
  const assignmentSellKeys = assignmentLinkedStockSellKeys(chronological);
  for (const txn of chronological) {
    if (txn.instrument_type === "stock" && txn.action === "sell" && txn.ticker) {
      const shares = Math.round(Number(txn.quantity) || 0);
      if (isAssignmentLinkedStockSell(txn, shares, assignmentSellKeys)) {
        txn.assignment_companion = true;
      }
    }
  }
  const events: AssignIncomeEvent[] = [];
  const snapshots = new Map<number, ShareAvgCostSnapshot>();
  for (const txn of chronological) {
    const ticker = txn.ticker ?? "";
    const positionKey = ticker ? shareAvgPositionKey(txn) : "";
    const before = positionKey ? positions.get(positionKey) : undefined;
    const sharesBefore = Math.round(before?.qty ?? 0);
    const avgBefore = before?.avg ?? 0;
    let assign: Pick<ShareAvgCostSnapshot, "assignAvg" | "assignPnl" | "assignShares"> = {};

    if (isCallAssignmentTxn(txn) && ticker && txn.id != null) {
      const contracts = Number(txn.quantity) || 0;
      const strike = Number(txn.strike) || 0;
      const callShares = Math.round(contracts * 100);
      const assignDate = assignmentEventDate(txn);

      // Cost is the running average at this moment in the replay: every earlier
      // share acquisition (put assign at its strike, plus outright buys) has
      // already blended in. Read it before applyAvgCostEvent removes the shares.
      const incomeShares = Math.min(callShares, Math.max(0, sharesBefore));

      if (contracts > 0 && strike > 0 && callShares > 0 && incomeShares > 0 && avgBefore > 0) {
        const amount = callAssignIncomeAtAvg(strike, incomeShares, avgBefore, incomeShares);
        assign = { assignAvg: avgBefore, assignPnl: amount, assignShares: incomeShares };
        if (amount !== 0 && assignDate) {
          events.push({
            date: assignDate,
            amount,
            ticker,
            strike,
            contracts: incomeShares / 100,
            avg: avgBefore,
            exp_date: txn.exp_date,
          });
        }
      }
    }

    const coveredSpan = applyAvgCostEvent(txn, positions, cover);

    if (!ticker || !positionKey) continue;
    const after = positions.get(positionKey);
    const own: SharePositionTransition = {
      sharesBefore,
      avgBefore,
      sharesAfter: Math.round(after?.qty ?? 0),
      avgAfter: after?.avg ?? 0,
    };
    // Shares delivered by a stock leg belong to the assignment that produced it,
    // so the row spans from before that leg through whatever it settled itself.
    const movedItself = own.sharesAfter !== own.sharesBefore;
    const transition = coveredSpan
      ? {
          sharesBefore: coveredSpan.sharesBefore,
          avgBefore: coveredSpan.avgBefore,
          sharesAfter: movedItself ? own.sharesAfter : coveredSpan.sharesAfter,
          avgAfter: movedItself ? own.avgAfter : coveredSpan.avgAfter,
        }
      : own;
    if (txn.id != null) snapshots.set(txn.id, { ...transition, ...assign });
  }

  return { events, snapshots };
}

function listAssignIncomeEvents(txns: WheelTransaction[]): AssignIncomeEvent[] {
  return replayShareAvgCost(txns).events;
}

/** Share inventory + avg cost around each ledger row, plus call-assignment P/L —
 *  the same replay Analytics(L) prices assignments with. */
export function buildShareAvgCostSnapshots(
  txns: WheelTransaction[],
): Map<number, ShareAvgCostSnapshot> {
  return replayShareAvgCost(txns).snapshots;
}

interface StockSellIncomeEvent {
  date: string;
  amount: number;
  ticker: string;
  shares: number;
  salePrice: number;
  avgCost: number;
  transaction: WheelTransaction;
}

/** Keys that identify stock sells created by covered-call assignment. */
function assignmentLinkedStockSellKeys(txns: WheelTransaction[]): Set<string> {
  const keys = new Set<string>();
  for (const txn of txns) {
    if (
      txn.instrument_type === "stock" &&
      txn.action === "sell" &&
      txn.assignment_companion &&
      txn.ticker &&
      txn.trade_date
    ) {
      const shares = Number(txn.quantity) || 0;
      keys.add(`${txn.trade_date}|${txn.account_id ?? ""}|${txn.ticker}|${shares}`);
      if (txn.id != null) keys.add(`id:${txn.id}`);
    }

    if (isCallAssignmentTxn(txn) && txn.ticker) {
      const assignDate = assignmentEventDate(txn) ?? txn.trade_date;
      const shares = Math.round(txnContracts(txn) * 100);
      if (assignDate) keys.add(`${assignDate}|${txn.account_id ?? ""}|${txn.ticker}|${shares}`);
      if (txn.linked_transaction_id != null) {
        keys.add(`id:${txn.linked_transaction_id}`);
      }
    }

    if (
      txn.instrument_type === "stock" &&
      txn.action === "sell" &&
      txn.linked_transaction_id != null &&
      txn.ticker &&
      txn.trade_date
    ) {
      const linked = txns.find((candidate) => candidate.id === txn.linked_transaction_id);
      if (linked && isCallAssignmentTxn(linked)) {
        const shares = Number(txn.quantity) || 0;
        keys.add(`${txn.trade_date}|${txn.account_id ?? ""}|${txn.ticker}|${shares}`);
        if (txn.id != null) keys.add(`id:${txn.id}`);
      }
    }

    // Orphan assignment stock sells: same-day STO call expiring, no ASGN option row.
    if (txn.instrument_type === "stock" && txn.action === "sell" && txn.ticker && txn.trade_date) {
      const shares = Number(txn.quantity) || 0;
      if (shares > 0 && findMatchingCallStoForAssignmentStockSell(txn, txns)) {
        keys.add(`${txn.trade_date}|${txn.account_id ?? ""}|${txn.ticker}|${shares}`);
        if (txn.id != null) keys.add(`id:${txn.id}`);
      }
    }
  }
  return keys;
}

function isAssignmentLinkedStockSell(
  txn: WheelTransaction,
  shares: number,
  keys: Set<string>,
): boolean {
  if (txn.assignment_companion) return true;
  if (txn.id != null && keys.has(`id:${txn.id}`)) return true;
  if (txn.ticker && txn.trade_date && keys.has(`${txn.trade_date}|${txn.account_id ?? ""}|${txn.ticker}|${shares}`)) {
    return true;
  }
  return false;
}

/**
 * Realized P/L for ordinary stock sales using the same running weighted
 * average cost as assignment P/L. Covered-call assignment stock sells are
 * skipped here — their P/L belongs in Assignments (via the ASGN option row).
 */
function listStockSellIncomeEvents(txns: WheelTransaction[]): StockSellIncomeEvent[] {
  const chronological = sortForAvgCostReplay(txns);
  const positions = new Map<string, AvgPosition>();
  const cover = createShareCoverIndex(chronological);
  const events: StockSellIncomeEvent[] = [];
  const assignmentSellKeys = assignmentLinkedStockSellKeys(chronological);

  for (const txn of chronological) {
    if (
      txn.instrument_type === "stock" &&
      txn.action === "sell" &&
      txn.ticker &&
      txn.trade_date
    ) {
      const shares = Number(txn.quantity) || 0;
      if (isAssignmentLinkedStockSell(txn, shares, assignmentSellKeys)) {
        // Keep P/L in Assignments only. Mark companion so avg-cost replay
        // does not also treat this stock sell as a separate inventory event
        // when the linked call ASGN option already adjusts the position.
        txn.assignment_companion = true;
        applyAvgCostEvent(txn, positions, cover);
        continue;
      }
      const salePrice = Number(txn.avg_price) || 0;
      const position = positions.get(shareAvgPositionKey(txn));
      if (shares > 0 && salePrice > 0 && position && position.avg > 0) {
        events.push({
          date: txn.trade_date,
          amount: (salePrice - position.avg) * shares,
          ticker: txn.ticker,
          shares,
          salePrice,
          avgCost: position.avg,
          transaction: txn,
        });
      }
    }

    applyAvgCostEvent(txn, positions, cover);
  }

  return events;
}

function computeAssignIncomeFromTransactions(
  txns: WheelTransaction[],
  scopeYear: number | null,
): number {
  return listAssignIncomeEvents(txns)
    .filter((ev) => assignIncomeInScope(ev.date, scopeYear))
    .reduce((sum, ev) => sum + ev.amount, 0);
}

function computeBtcCostFromTransactions(
  txns: WheelTransaction[],
  scopeYear: number | null,
): number {
  let cost = 0;
  for (const txn of classifyOptionIncomeTxns(txns)) {
    if (txnCode(txn) !== "BTC") continue;
    if (!assignIncomeInScope(txn.trade_date, scopeYear)) continue;
    cost += txnNotional(txn);
  }
  return cost;
}

function emptyDay(date: string): IncomeDayBucket {
  return {
    date,
    premiums: 0,
    stcCredits: 0,
    assignIncome: 0,
    stockSellIncome: 0,
    btcCost: 0,
    btoCost: 0,
    total: 0,
    lines: [],
  };
}

/** Fill time first, then ledger created_at, then id. Same-day rows follow the trade clock. */
function txnFillTimestamp(txn: WheelTransaction): string {
  const ext = txn.extensions;
  return (
    String(txn.trade_timestamp ?? "").trim() ||
    String(ext?.trade_timestamp ?? "").trim() ||
    String(ext?.pnl_timestamp ?? "").trim() ||
    String(ext?.broker_filled_at ?? "").trim()
  );
}

function txnDisplaySortAt(txn: WheelTransaction | undefined): number {
  if (!txn) return 0;
  const fill = txnFillTimestamp(txn);
  if (fill) {
    const ms = Date.parse(fill);
    if (Number.isFinite(ms)) return ms;
  }
  if (txn.created_at) {
    const ms = Date.parse(txn.created_at);
    if (Number.isFinite(ms)) return ms;
  }
  return Number(txn.id) || 0;
}

function sortIncomeLinesNewestFirst(lines: IncomeLine[]): void {
  lines.sort(
    (a, b) =>
      (b.sortAt ?? 0) - (a.sortAt ?? 0) || (b.txnId ?? 0) - (a.txnId ?? 0),
  );
}

function incomeLinesLinked(a: IncomeLine, b: IncomeLine): boolean {
  if (a.txnId != null && b.linkedTxnIds.includes(a.txnId)) return true;
  if (b.txnId != null && a.linkedTxnIds.includes(b.txnId)) return true;
  return false;
}

function assignmentStockShares(asgn: IncomeLine): number {
  const contracts = Number(asgn.qty);
  if (Number.isFinite(contracts) && contracts > 0) return Math.round(contracts * 100);
  return 0;
}

/** Match an ASG row to its put-buy / call-sell stock companion on the same day. */
function findAssignmentCompanionStock(
  asgn: IncomeLine,
  stocks: IncomeLine[],
): IncomeLine | undefined {
  const wantKind: IncomeLine["kind"] | null =
    asgn.side === "put" ? "buy" : asgn.side === "call" ? "sell" : null;
  if (!wantKind) return undefined;
  const shares = assignmentStockShares(asgn);
  const strike = Number(asgn.strike);
  return stocks.find((stock) => {
    if (stock.kind !== wantKind || stock.ticker !== asgn.ticker) return false;
    if (incomeLinesLinked(asgn, stock)) return true;
    const stockShares = Number(stock.qty) || 0;
    if (shares > 0 && Math.abs(stockShares - shares) > 0.1) return false;
    if (!(shares > 0)) return false;
    // Prefer strike ≈ stock cost when both are present ("$17.00").
    if (Number.isFinite(strike) && strike > 0 && stock.avg && stock.avg !== "—") {
      const cost = Number(String(stock.avg).replace(/[^0-9.-]/g, ""));
      if (Number.isFinite(cost) && Math.abs(cost - strike) > 0.06) return false;
    }
    return true;
  });
}

/**
 * Keep assignment ↔ stock settlement rows adjacent:
 * real ASG clusters as [ASG, stock]; stock-led inferred ASG as [stock, ASG].
 * Remaining rows keep newest-first order between clusters.
 */
function sortIncomeLinesWithAssignmentPairs(lines: IncomeLine[]): void {
  if (lines.length < 2) {
    sortIncomeLinesNewestFirst(lines);
    return;
  }

  const used = new Set<IncomeLine>();
  const clusters: IncomeLine[][] = [];
  const clusterSortAt = (cluster: IncomeLine[]) =>
    Math.max(...cluster.map((line) => line.sortAt ?? 0));

  const asgns = lines
    .filter((line) => line.kind === "asgn")
    .sort(
      (a, b) =>
        (b.sortAt ?? 0) - (a.sortAt ?? 0) || (b.txnId ?? 0) - (a.txnId ?? 0),
    );
  const stocks = () =>
    lines.filter(
      (line) =>
        !used.has(line) && (line.kind === "buy" || line.kind === "sell"),
    );

  for (const asgn of asgns) {
    if (used.has(asgn)) continue;
    const stock = findAssignmentCompanionStock(asgn, stocks());
    if (stock) {
      used.add(asgn);
      used.add(stock);
      // Inferred/synthetic ASG (no ledger txn id) sits under the stock row.
      if (asgn.txnId == null && asgn.linkStatus === "inferred") {
        clusters.push([stock, asgn]);
      } else {
        clusters.push([asgn, stock]);
      }
    } else {
      used.add(asgn);
      clusters.push([asgn]);
    }
  }

  const rest = lines
    .filter((line) => !used.has(line))
    .sort(
      (a, b) =>
        (b.sortAt ?? 0) - (a.sortAt ?? 0) || (b.txnId ?? 0) - (a.txnId ?? 0),
    );
  for (const line of rest) clusters.push([line]);

  clusters.sort(
    (a, b) =>
      clusterSortAt(b) - clusterSortAt(a) ||
      (b[0]?.txnId ?? 0) - (a[0]?.txnId ?? 0),
  );
  lines.splice(0, lines.length, ...clusters.flat());
}

/** Remaining short contracts per OCC symbol as of a date (sell − buy before stock id). */
function remainingShortBySymbol(
  txns: WheelTransaction[],
  optionSymbol: string,
  asOfDate: string,
  beforeId?: number | null,
): number {
  let net = 0;
  for (const txn of txns) {
    if (txn.instrument_type !== "option" || txn.option_symbol !== optionSymbol) continue;
    const date = String(txn.trade_date ?? "").slice(0, 10);
    if (!date || date > asOfDate) continue;
    const id = Number(txn.id) || 0;
    if (beforeId != null && date === asOfDate && id >= beforeId) continue;
    const qty = Number(txn.quantity) || 0;
    if (!(qty > 0)) continue;
    if (txn.action === "sell") net += qty;
    else if (txn.action === "buy") net -= qty;
  }
  return net;
}

/** Short put/call that a strike-priced stock buy/sell likely settled. */
function findShortOptionForAssignmentStock(
  txns: WheelTransaction[],
  stock: WheelTransaction,
): WheelTransaction | null {
  if (stock.instrument_type !== "stock" || !stock.ticker || !stock.trade_date) {
    return null;
  }
  const shares = Number(stock.quantity) || 0;
  const price = Number(stock.avg_price) || Number(stock.strike) || 0;
  if (!(shares >= 100) || shares % 100 !== 0 || !(price > 0)) return null;
  const contracts = shares / 100;
  const wantSide =
    stock.action === "buy" ? "put" : stock.action === "sell" ? "call" : null;
  if (!wantSide) return null;
  const stockId = stock.id != null ? Number(stock.id) : null;

  let best: WheelTransaction | null = null;
  let bestScore = -Infinity;
  for (const txn of txns) {
    if (
      txn.instrument_type !== "option" ||
      txn.action !== "sell" ||
      txn.side !== wantSide ||
      txn.ticker !== stock.ticker
    ) {
      continue;
    }
    if (Math.abs((Number(txn.quantity) || 0) - contracts) > 0.001) continue;
    const strike = Number(txn.strike) || 0;
    if (!(strike > 0)) continue;
    // RH PnL stock legs for call assigns are often near — but not exactly — strike.
    const strikeTol = Math.max(0.06, strike * 0.05);
    if (Math.abs(strike - price) > strikeTol) continue;
    const exp = txn.exp_date ? String(txn.exp_date).slice(0, 10) : "";
    if (!exp || exp > stock.trade_date) continue;
    const symbol = txn.option_symbol ?? "";
    if (
      symbol &&
      remainingShortBySymbol(txns, symbol, stock.trade_date, stockId) + 1e-9 < contracts
    ) {
      continue;
    }
    let score = exp === stock.trade_date ? 10 : 1;
    if (txn.trade_date && txn.trade_date <= stock.trade_date) score += 1;
    // Prefer closer strike match when RH reports a non-strike PnL price.
    score += Math.max(0, 2 - Math.abs(strike - price) / Math.max(strikeTol, 1e-9));
    if (score > bestScore) {
      bestScore = score;
      best = txn;
    }
  }
  return best;
}

function buildStockTraceLine(
  txn: WheelTransaction,
  transactionLinks: Map<number, TransactionLinkMeta>,
  opts?: {
    assignmentPaired?: boolean;
    holdingAvgBefore?: number | null;
    holdingAvgAfter?: number | null;
  },
): IncomeLine {
  const shares = Number(txn.quantity) || 0;
  const price = Number(txn.avg_price) || 0;
  const link = transactionLinkMeta(txn, transactionLinks);
  const isSell = txn.action === "sell";
  const companionNote = opts?.assignmentPaired
    ? "assignment stock"
    : link.status === "unlinked"
      ? "unlinked stock"
      : "linked stock";
  const holdingAvgBefore =
    opts?.holdingAvgBefore != null && opts.holdingAvgBefore > 0
      ? opts.holdingAvgBefore
      : null;
  const holdingAvgAfter =
    opts?.holdingAvgAfter != null && opts.holdingAvgAfter > 0
      ? opts.holdingAvgAfter
      : null;
  const { system, account } = incomeLineSystemAccount(txn);
  const marks = incomeLineMarks(txn);
  return {
    kind: isSell ? "sell" : "buy",
    label: isSell ? "SELL" : "BUY",
    ticker: txn.ticker ?? "—",
    side: "stock",
    strike: "—",
    qty: shares ? String(shares) : "—",
    open: "—",
    exp: "—",
    credit: isSell && shares > 0 && price > 0 ? fmtUsd(shares * price) : "—",
    btc: "—",
    avg: price > 0 ? fmtUsd(price) : "—",
    symbol: `${companionNote}${price > 0 ? ` @ ${fmtUsd(price)}` : ""}`,
    amount: 0,
    incomeAccounted: false,
    txnId: txn.id,
    linkedTxnIds: link.linkedIds,
    linkStatus: link.status,
    sortAt: txnDisplaySortAt(txn),
    holdingAvgBefore,
    holdingAvgAfter,
    system,
    account,
    ...marks,
  };
}

/** STO premiums + STC credits − BTC/BTO + assignment P/L + ordinary stock-sale P/L. */
function cashIncome(parts: {
  premiums: number;
  stcCredits?: number;
  btcCost: number;
  btoCost?: number;
  assignIncome: number;
  stockSellIncome?: number;
}): number {
  return (
    parts.premiums +
    (parts.stcCredits ?? 0) -
    parts.btcCost -
    (parts.btoCost ?? 0) +
    parts.assignIncome +
    (parts.stockSellIncome ?? 0)
  );
}

function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

function currentCalendarYear(): number {
  return new Date().getFullYear();
}

function currentMonthKey(): string {
  return todayIsoDate().slice(0, 7);
}

/** Closed calendar months — margin forecast toggle applies to the current month only. */
export function isPastAnalyticsMonthKey(monthKey: string): boolean {
  return monthKey < currentMonthKey();
}

export function currentAnalyticsMonthKey(): string {
  return currentMonthKey();
}

/** Month + year to show when opening Analytics+ (current month; bump year if needed). */
export function defaultAnalyticsPlusOpenSelection(selectedYear: number | "all"): {
  monthKey: string;
  year: number;
} {
  const monthKey = currentMonthKey();
  const currentYear = currentCalendarYear();
  const year = selectedYear === "all" ? currentYear : selectedYear;
  const [monthYearRaw] = monthKey.split("-");
  const monthYear = Number(monthYearRaw);
  if (year !== monthYear) return { monthKey, year: currentYear };
  return { monthKey, year };
}

/** Reset desktop Analytics+ month/year pickers when the tab is opened. */
export function resetAnalyticsPlusMonthSelection(selectedYear: number | "all" = selectedIncomeYear): void {
  const defaults = defaultAnalyticsPlusOpenSelection(selectedYear);
  selectedIncomeMonthKey = defaults.monthKey;
  selectedIncomeYear = defaults.year;
}

/** Analytics(L) keeps the year/month you were looking at, so a page reload or a
 * trip through another tab does not throw the view back to the current month. */
const ANALYTICS_LOCAL_VIEW_KEY = "sfe-analytics-local-view-v1";

function saveAnalyticsLocalViewSelection(): void {
  try {
    localStorage.setItem(
      ANALYTICS_LOCAL_VIEW_KEY,
      JSON.stringify({ year: selectedIncomeYear, month: selectedIncomeMonthKey }),
    );
  } catch {
    // Selection still holds for this session without persistence.
  }
}

export function restoreAnalyticsLocalViewSelection(): void {
  let year: number | "all" | null = null;
  let month: string | null = null;
  try {
    const raw = localStorage.getItem(ANALYTICS_LOCAL_VIEW_KEY);
    const saved = raw ? (JSON.parse(raw) as { year?: unknown; month?: unknown }) : null;
    if (typeof saved?.year === "number" && Number.isFinite(saved.year)) year = saved.year;
    else if (saved?.year === "all") year = "all";
    if (typeof saved?.month === "string" && /^\d{4}-\d{2}$/.test(saved.month)) month = saved.month;
  } catch {
    year = null;
    month = null;
  }
  // All spans every year, so it carries no month.
  if (year === "all") {
    selectedIncomeYear = "all";
    selectedIncomeMonthKey = null;
    return;
  }
  if (year == null || month == null || !month.startsWith(`${year}-`)) {
    resetAnalyticsPlusMonthSelection();
    return;
  }
  selectedIncomeYear = year;
  selectedIncomeMonthKey = month;
}

/** Forecast months for a single selected year (not All). */
function forecastScopeYear(
  selectedYear: number | "all",
  forecastEnabled: boolean,
): number | null {
  if (!forecastEnabled || selectedYear === "all") return null;
  const nowYear = currentCalendarYear();
  if (selectedYear < nowYear || selectedYear > forecastMaxYear()) return null;
  return selectedYear;
}

function withLocalLedgerCashMonths(
  months: IncomeMonthBucket[],
  ledger: Record<string, OwnCapitalEntry>,
  selectedYear: number | "all",
): IncomeMonthBucket[] {
  const byKey = new Map(months.map((month) => [month.month, month]));
  const ensure = (monthKey: string): void => {
    if (!byKey.has(monthKey)) {
      byKey.set(monthKey, emptyIncomeMonthBucket(monthKey));
    }
  };

  if (selectedYear !== "all") {
    for (let m = 1; m <= 12; m += 1) {
      ensure(`${selectedYear}-${String(m).padStart(2, "0")}`);
    }
  }

  for (const monthKey of Object.keys(ledger)) {
    if (selectedYear === "all" || monthKey.startsWith(`${selectedYear}-`)) {
      ensure(monthKey);
    }
  }

  return [...byKey.values()].sort((a, b) => b.month.localeCompare(a.month));
}

function isCashHistoryIncomeLine(line: IncomeLine): boolean {
  return line.kind === "cash";
}

/** Cash ledger rows are display-only — never show a P/L figure in Analytics history. */
export function shouldShowIncomeLinePl(
  line: Pick<IncomeLine, "kind" | "incomeAccounted">,
): boolean {
  if (line.kind === "cash") return false;
  return line.incomeAccounted === true;
}

/** Display-only cash ops (DEP / WDL / MARG / …). Never counted in daily income. */
function cashHistoryLineFromFlow(flow: OwnLedgerCashFlow): IncomeLine | null {
  if (flow.ignored === true || !isFactCashFlow(flow)) return null;
  const category = String(flow.category ?? "").trim().toLowerCase();
  const direction = flow.direction === "out" ? "out" : "in";
  const amountAbs = Math.abs(Number(flow.amount) || 0);
  if (!(amountAbs > 0)) return null;
  const date = String(flow.flow_date ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const historyRow = cashFlowToHistoryRow(flow);
  const ts = Date.parse(cashFlowDisplayTimestamp(date, category));
  const note = String(flow.note ?? "").trim();
  return {
    kind: "cash",
    label: cashFlowActionLabel(category, direction),
    ticker: "—",
    side: "—",
    strike: "—",
    qty: "—",
    open: "—",
    exp: "—",
    credit: direction === "in" ? fmtUsd(amountAbs) : "—",
    btc: direction === "out" ? fmtUsd(amountAbs) : "—",
    avg: "—",
    symbol: note || cashFlowCategoryLabel(category),
    amount: direction === "out" ? -amountAbs : amountAbs,
    incomeAccounted: false,
    detail: note ? cashFlowCategoryLabel(category) : undefined,
    contracts: null,
    premium: null,
    dte: null,
    linkedTxnIds: [],
    linkStatus: "unlinked",
    sortAt: Number.isFinite(ts) ? ts : 0,
    system: historySystemLabel(historyRow),
    account: historyAccountLabel(historyRow),
    ...incomeLineMarks(historyRow),
    note: String(flow.note ?? "").trim(),
    cashFlowId: Number(flow.id) > 0 ? Number(flow.id) : undefined,
  };
}

function attachLocalLedgerCashHistory(
  months: IncomeMonthBucket[],
  flows: OwnLedgerCashFlow[],
): IncomeMonthBucket[] {
  if (!flows.length) return months;
  const byKey = new Map(months.map((month) => [month.month, month]));
  for (const flow of flows) {
    const line = cashHistoryLineFromFlow(flow);
    if (!line) continue;
    const date = String(flow.flow_date ?? "").slice(0, 10);
    const month = byKey.get(date.slice(0, 7));
    if (!month) continue;
    let day = month.days.find((row) => row.date === date);
    if (!day) {
      day = emptyDay(date);
      month.days.push(day);
    }
    day.lines.push(line);
  }
  for (const month of byKey.values()) {
    month.days.sort((a, b) => a.date.localeCompare(b.date));
    for (const day of month.days) {
      sortIncomeLinesNewestFirst(day.lines);
      day.total = cashIncome(day);
    }
  }
  return [...byKey.values()].sort((a, b) => b.month.localeCompare(a.month));
}

function attachIncomeLineNotes(
  months: IncomeMonthBucket[],
  txns: WheelTransaction[],
  flows: OwnLedgerCashFlow[],
): void {
  const byTxn = new Map<number, string>();
  for (const txn of txns) {
    if (txn.id == null) continue;
    const note = ledgerAttachedNote(txn);
    if (note) byTxn.set(txn.id, note);
  }
  const byCash = new Map<number, string>();
  for (const flow of flows) {
    const note = ledgerAttachedNote({ instrument_type: "cash", note: flow.note });
    if (note && Number(flow.id) > 0) byCash.set(Number(flow.id), note);
  }
  for (const month of months) {
    for (const day of month.days) {
      for (const line of day.lines) {
        if (line.cashFlowId != null) {
          line.note = byCash.get(line.cashFlowId) ?? "";
        } else if (line.txnId != null) {
          line.note = byTxn.get(line.txnId) ?? "";
        }
      }
    }
  }
}

function incomeHistoryLineCount(month: IncomeMonthBucket): number {
  let count = 0;
  for (const day of month.days) count += day.lines.length;
  return count;
}

function stripOwnCapitalOverlaysFromLedger(
  ledger: Record<string, OwnCapitalEntry>,
): Record<string, OwnCapitalEntry> {
  const out: Record<string, OwnCapitalEntry> = {};
  for (const [month, raw] of Object.entries(ledger)) {
    const entry = ledgerEntryWithResolvedFee(month, raw, ledger);
    out[month] = {
      cashAdded: entry.cashAdded,
      withdrawn: entry.withdrawn,
      perTradeFee: entry.perTradeFee,
      targetRocPct: entry.targetRocPct,
      marginCost: 0,
      usePredictedMarginCost: false,
    };
  }
  return out;
}

function capitalEntriesFromOwnLedgerCashMonths(
  raw: Record<string, OwnCapitalMonthCash>,
  opts?: { analyticsLocal?: boolean },
): Record<string, OwnCapitalEntry> {
  const months: Record<string, OwnCapitalEntry> = {};
  for (const [month, entry] of Object.entries(raw)) {
    months[month] = normalizeOwnCapitalEntry({
      cashAdded: entry.cashAdded,
      withdrawn: entry.withdrawn,
      taxWithdrawn: entry.taxWithdrawn,
      marginCost: entry.marginCost,
      stockLendingIncome: entry.stockLendingIncome,
      ...(opts?.analyticsLocal
        ? {
            perTradeFee: 0,
            usePredictedMarginCost: !isPastAnalyticsMonthKey(month),
          }
        : {}),
    });
  }
  return months;
}

/** Overlay planned ROC % from `/own-capital` onto own-ledger cash months. */
function mergeOwnCapitalTargetRocFromPlans(
  cashMonths: Record<string, OwnCapitalEntry>,
  plans: Record<string, OwnCapitalEntry>,
): Record<string, OwnCapitalEntry> {
  const out: Record<string, OwnCapitalEntry> = { ...cashMonths };
  for (const [month, plan] of Object.entries(plans)) {
    const prev = out[month] ?? normalizeOwnCapitalEntry(undefined);
    const targetRocPct = plan.targetRocPct;
    const roc =
      targetRocPct != null && Number.isFinite(targetRocPct) ? { targetRocPct } : {};
    const marginPred =
      plan.usePredictedMarginCost !== undefined && !isPastAnalyticsMonthKey(month)
        ? { usePredictedMarginCost: plan.usePredictedMarginCost === true }
        : {};
    if (!Object.keys(roc).length && !Object.keys(marginPred).length) continue;
    out[month] = { ...prev, ...roc, ...marginPred };
  }
  return out;
}

async function loadOwnLedgerAnalyticsCapitalLedger(
  force = false,
  opts: { includeForecast?: boolean } = {},
): Promise<Record<string, OwnCapitalEntry>> {
  const [raw, plans] = await Promise.all([
    loadOwnLedgerCashMonthLedger(force, opts),
    fetchOwnCapitalLedger(force ? { bustCache: true } : undefined),
  ]);
  return mergeOwnCapitalTargetRocFromPlans(
    capitalEntriesFromOwnLedgerCashMonths(raw, { analyticsLocal: true }),
    plans,
  );
}

async function fetchOwnCapitalCashFlowsLedger(): Promise<Record<string, OwnCapitalEntry>> {
  const ledger = await fetchOwnCapitalLedger();
  return stripOwnCapitalOverlaysFromLedger(ledger);
}

function emptyIncomeMonthBucket(monthKey: string): IncomeMonthBucket {
  return {
    month: monthKey,
    label: monthLabel(monthKey),
    premiums: 0,
    stcCredits: 0,
    assignIncome: 0,
    stockSellIncome: 0,
    btcCost: 0,
    btoCost: 0,
    total: 0,
    deployedCapital: 0,
    days: [],
  };
}

/** Month keys for forecast padding: current month→Dec (this year) or Jan→Dec (future years). */
function monthKeysForForecastYear(year: number): string[] {
  const [nowYear, nowMonth] = currentMonthKey().split("-").map(Number);
  if (year < nowYear) return [];
  if (year > nowYear) {
    return Array.from({ length: 12 }, (_, index) =>
      `${year}-${String(index + 1).padStart(2, "0")}`,
    );
  }
  const keys: string[] = [];
  for (let m = nowMonth; m <= 12; m += 1) {
    keys.push(`${year}-${String(m).padStart(2, "0")}`);
  }
  return keys;
}

function withFactMonths(
  months: IncomeMonthBucket[],
  selectedYear: number | "all",
): IncomeMonthBucket[] {
  const currentMonth = currentMonthKey();
  const currentYear = currentCalendarYear();
  if (selectedYear !== "all" && selectedYear !== currentYear) return months;

  const byKey = new Map(months.map((month) => [month.month, month]));
  if (!byKey.has(currentMonth)) {
    byKey.set(currentMonth, emptyIncomeMonthBucket(currentMonth));
  }
  return [...byKey.values()].sort((a, b) => b.month.localeCompare(a.month));
}

function withForecastMonths(
  months: IncomeMonthBucket[],
  selectedYear: number | "all",
  forecastEnabled: boolean,
): IncomeMonthBucket[] {
  const factMonths = withFactMonths(months, selectedYear);
  if (!forecastEnabled) {
    const currentMonth = currentMonthKey();
    return factMonths.filter((month) => month.month <= currentMonth);
  }
  const scopeYear = forecastScopeYear(selectedYear, forecastEnabled);
  if (scopeYear == null) return factMonths;

  const byKey = new Map(factMonths.map((month) => [month.month, month]));
  for (const monthKey of monthKeysForForecastYear(scopeYear)) {
    if (!byKey.has(monthKey)) {
      byKey.set(monthKey, emptyIncomeMonthBucket(monthKey));
    }
  }
  return [...byKey.values()].sort((a, b) => b.month.localeCompare(a.month));
}

function isForecastMonthChip(
  monthKey: string,
  forecastEnabled: boolean,
  selectedYear: number | "all",
): boolean {
  if (!forecastEnabled) return false;
  const cm = currentMonthKey();
  if (monthKey <= cm) return false;
  const [year] = monthKey.split("-").map(Number);
  if (year > forecastMaxYear()) return false;
  if (selectedYear === "all") return year === currentCalendarYear();
  if (selectedYear < currentCalendarYear() || selectedYear > forecastMaxYear()) return false;
  return monthKey.startsWith(`${selectedYear}-`);
}

function isCurrentMonthChip(monthKey: string): boolean {
  return monthKey === currentMonthKey();
}

/** Live stock/margin KPIs reflect today — dim when viewing a non-current month. */
function shouldDimStockKpis(selectedMonth: string | null): boolean {
  return selectedMonth != null && selectedMonth !== currentMonthKey();
}

/** Past = green; current year = gold; future years = blue; All is neutral. */
function yearChipClassName(year: number | "all", isSelected: boolean): string {
  const classes = ["year-chip", "sfe-income-year-chip"];
  if (year === "all") {
    classes.push("sfe-year-chip--all");
    if (isSelected) classes.push("active");
    return classes.join(" ");
  }
  const nowYear = currentCalendarYear();
  if (year < nowYear) classes.push("sfe-year-chip--past");
  else if (year === nowYear) classes.push("sfe-year-chip--current");
  else if (year <= forecastMaxYear()) classes.push("sfe-year-chip--blue");
  if (isSelected) classes.push("active");
  return classes.join(" ");
}

function yearChipTipLabel(year: number): string | null {
  const nowYear = currentCalendarYear();
  if (year === nowYear) return "Fact + Forecast";
  if (year < nowYear) return "Fact";
  if (year <= forecastMaxYear()) return "Forecast";
  return null;
}

function monthChipClassName(monthKey: string, isSelected: boolean, inactive = false): string {
  const cm = currentMonthKey();
  let toneClass = "sfe-month-chip--blue";
  if (monthKey < cm) toneClass = "sfe-month-chip--past";
  else if (monthKey === cm) toneClass = "sfe-month-chip--current";
  const classes = ["sfe-income-month-chip", toneClass];
  if (inactive) classes.push("sfe-month-chip--inactive");
  if (isSelected) classes.push("active");
  return classes.join(" ");
}

function monthChipTipLabel(
  monthKey: string,
  forecastEnabled: boolean,
  selectedYear: number | "all",
): string | null {
  if (isCurrentMonthChip(monthKey)) return "Fact + Forecast";
  if (monthKey < currentMonthKey()) return "Fact";
  if (isForecastMonthChip(monthKey, forecastEnabled, selectedYear)) return "Forecast";
  return null;
}

export type AnalyticsPerformanceScopeBadge = "Fact" | "Fact + Forecast" | "Forecast";

/** Badge for Total Performance header (Fact+Forecast only for the current month). */
export function analyticsPerformanceScopeBadge(
  selectedMonthKey: string | null,
  _selectedYear: number | "all",
  isForecastView: boolean,
): AnalyticsPerformanceScopeBadge {
  if (isForecastView) return "Forecast";
  if (selectedMonthKey === currentMonthKey()) return "Fact + Forecast";
  return "Fact";
}

/** Badge for a single month performance card header. */
export function analyticsMonthScopeBadge(
  monthKey: string,
  isForecastView: boolean,
): AnalyticsPerformanceScopeBadge {
  if (isForecastView) return "Forecast";
  if (monthKey === currentMonthKey()) return "Fact + Forecast";
  return "Fact";
}

function realOwnCapitalEom(
  monthKey: string,
  actualTimeline: Map<string, OwnCapitalPoint>,
): number {
  return actualTimeline.get(monthKey)?.ownCapital ?? 0;
}

/** BOM for a month: prior month EOM — actual when prior is current/past, projected when prior is forecast. */
function baseCapitalForMonth(
  monthKey: string,
  actualTimeline: Map<string, OwnCapitalPoint>,
  projectedTimeline: Map<string, OwnCapitalPoint>,
): number {
  const prevKey = prevMonthKey(monthKey);
  if (prevKey <= currentMonthKey()) {
    return realOwnCapitalEom(prevKey, actualTimeline);
  }
  return projectedTimeline.get(prevKey)?.ownCapital ?? 0;
}

/** Project own capital forward from the month after today through `throughMonth`
 * using each month's target ROC % as expected *net* income (after margin).
 * The first forecast month's BOM always anchors on the prior month's real EOM. */
function ensureProjectedOwnCapitalTimeline(
  actualTimeline: Map<string, OwnCapitalPoint>,
  ledger: Record<string, OwnCapitalEntry>,
  throughMonth: string,
  stockCostBasis = 0,
  predictedTaxWithdrawals?: Map<string, number>,
  localLedger = false,
): Map<string, OwnCapitalPoint> {
  const timeline = new Map(actualTimeline);
  const cm = currentMonthKey();
  if (throughMonth <= cm) return timeline;

  let cursor = nextMonthKey(cm);
  while (cursor <= throughMonth) {
    const prevKey = prevMonthKey(cursor);
    const prevOwnCapital =
      prevKey <= cm
        ? realOwnCapitalEom(prevKey, actualTimeline)
        : timeline.get(prevKey)?.ownCapital ?? 0;
    const targetPct = readTargetRocPct(cursor, ledger);
    const targetIncome = prevOwnCapital > 0 ? (targetPct / 100) * prevOwnCapital : 0;
    const entry = ledgerEntryWithResolvedFee(cursor, ledger[cursor], ledger);
    const perTrade = perTradeExpenseTotal(entry, 0);
    const predictedWd = localLedger
      ? 0
      : Math.max(0, Number(predictedTaxWithdrawals?.get(cursor)) || 0);
    const withdrawn = (Number(entry.withdrawn) || 0) + predictedWd;
    const estimatedTaxPaid = localLedger ? 0 : taxCashOutflowForMonthKey(cursor);
    const ownCapital = plannedOwnCapitalEom({
      baseCapitalBom: prevOwnCapital,
      targetIncomeNet: targetIncome,
      cashAdded: entry.cashAdded,
      withdrawn,
      stockLendingIncome: localLedger ? Number(entry.stockLendingIncome) || 0 : 0,
      perTradeFees: perTrade,
      taxCashOutflow: estimatedTaxPaid,
    });
    const currentMargin = Number(actualTimeline.get(cm)?.effectiveMarginCost) || 0;
    const dailyForecast = forecastMarginCostFromCurrentDaily();
    const effectiveMarginCost =
      dailyForecast != null
        ? dailyForecast
        : localLedger
          ? entry.usePredictedMarginCost === false
            ? Number(entry.marginCost) || 0
            : currentMargin
          : entry.usePredictedMarginCost
            ? currentMargin
            : factualMarginCostForMonth(entry, cursor);
    timeline.set(cursor, {
      ...entry,
      withdrawn,
      income: targetIncome,
      ownCapital,
      effectiveMarginCost,
    });
    cursor = nextMonthKey(cursor);
  }
  return timeline;
}

/** Performance graph only. The open month is forecast, so the dotted line uses
 * the plan and later months chain off that plan instead of the partial month. */
function projectPerformanceChartTimeline(
  actualTimeline: Map<string, OwnCapitalPoint>,
  ledger: Record<string, OwnCapitalEntry>,
  throughMonth: string,
  predictedTaxWithdrawals?: Map<string, number>,
  localLedger = false,
): Map<string, OwnCapitalPoint> {
  const timeline = new Map(actualTimeline);
  const cm = currentMonthKey();
  if (throughMonth < cm) return timeline;

  let cursor = cm;
  while (cursor <= throughMonth) {
    const prevKey = prevMonthKey(cursor);
    const prevOwnCapital =
      prevKey < cm
        ? realOwnCapitalEom(prevKey, actualTimeline)
        : timeline.get(prevKey)?.ownCapital ?? 0;
    const targetPct = readTargetRocPct(cursor, ledger);
    const targetIncome = prevOwnCapital > 0 ? (targetPct / 100) * prevOwnCapital : 0;
    const entry = ledgerEntryWithResolvedFee(cursor, ledger[cursor], ledger);
    const perTrade = perTradeExpenseTotal(entry, 0);
    const predictedWd = localLedger
      ? 0
      : Math.max(0, Number(predictedTaxWithdrawals?.get(cursor)) || 0);
    const withdrawn = (Number(entry.withdrawn) || 0) + predictedWd;
    const estimatedTaxPaid = localLedger ? 0 : taxCashOutflowForMonthKey(cursor);
    const ownCapital = plannedOwnCapitalEom({
      baseCapitalBom: prevOwnCapital,
      targetIncomeNet: targetIncome,
      cashAdded: entry.cashAdded,
      withdrawn,
      stockLendingIncome: localLedger ? Number(entry.stockLendingIncome) || 0 : 0,
      perTradeFees: perTrade,
      taxCashOutflow: estimatedTaxPaid,
    });
    const currentMargin = Number(actualTimeline.get(cm)?.effectiveMarginCost) || 0;
    const dailyForecast = forecastMarginCostFromCurrentDaily();
    const effectiveMarginCost =
      dailyForecast != null
        ? dailyForecast
        : localLedger
          ? entry.usePredictedMarginCost === false
            ? Number(entry.marginCost) || 0
            : currentMargin
          : entry.usePredictedMarginCost
            ? currentMargin
            : factualMarginCostForMonth(entry, cursor);
    timeline.set(cursor, {
      ...entry,
      withdrawn,
      income: targetIncome,
      ownCapital,
      effectiveMarginCost,
    });
    cursor = nextMonthKey(cursor);
  }
  return timeline;
}

function monthInScope(monthKey: string, scopeYear: number | null): boolean {
  if (scopeYear == null) return true;
  return monthKey.startsWith(`${scopeYear}-`);
}

/** Latest month on or before today. Forecast cash (tax through later years) must not
 * stand in for the All-years balance. */
function latestFactOwnCapitalMonth(
  timeline: Map<string, OwnCapitalPoint>,
): string | null {
  const cm = currentMonthKey();
  let best: string | null = null;
  for (const monthKey of timeline.keys()) {
    if (monthKey > cm) continue;
    if (best == null || monthKey > best) best = monthKey;
  }
  return best;
}

/** Month the Total Own Capital card represents.
 * Forecast off, or Year = All: the current month. A future month only when that chip is open. */
function resolveOwnCapitalKpiMonth(
  selectedMonth: string | null,
  forecastEnabled: boolean,
  timeline: Map<string, OwnCapitalPoint>,
): string {
  const cm = currentMonthKey();
  const fact = latestFactOwnCapitalMonth(timeline) ?? cm;
  if (!forecastEnabled) {
    if (selectedMonth && selectedMonth <= cm) return selectedMonth;
    return fact;
  }
  if (!selectedMonth) return fact;
  return selectedMonth;
}

function selectedMonthOwnCapitalEom(
  selectedMonth: string | null,
  isForecastView: boolean,
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  projectedTimeline: Map<string, OwnCapitalPoint>,
  forecastEnabled = true,
): number {
  if (isForecastView && selectedMonth) {
    return projectedTimeline.get(selectedMonth)?.ownCapital ?? 0;
  }
  const monthKey = resolveOwnCapitalKpiMonth(selectedMonth, forecastEnabled, ownCapitalByMonth);
  return ownCapitalByMonth.get(monthKey)?.ownCapital ?? 0;
}

/** Cumulative cash flows through the selected month (FACT) or forecast scope. */
function cashFlowTotalsForKpi(
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  forecastTotals: { cashAdded: number; withdrawn: number } | null,
  selectedMonth: string | null,
): {
  cashAdded: number;
  withdrawn: number;
  taxWithdrawn: number;
  netCashAdded: number;
} {
  if (forecastTotals) {
    const { cashAdded, withdrawn } = forecastTotals;
    return { cashAdded, withdrawn, taxWithdrawn: 0, netCashAdded: cashAdded - withdrawn };
  }

  const cm = currentMonthKey();
  const throughMonth =
    selectedMonth && selectedMonth <= cm ? selectedMonth : cm;

  let cashAdded = 0;
  let withdrawn = 0;
  let taxWithdrawn = 0;
  for (const [monthKey, point] of ownCapitalByMonth) {
    if (monthKey > throughMonth || monthKey > cm) continue;
    cashAdded += point.cashAdded ?? 0;
    withdrawn += point.withdrawn ?? 0;
    taxWithdrawn += point.taxWithdrawn ?? 0;
  }
  return { cashAdded, withdrawn, taxWithdrawn, netCashAdded: cashAdded - withdrawn };
}

function buildCashOpsTip(
  isForecastView: boolean,
  taxWithdrawnLedger: number,
  totalWithdrawn: number,
): string {
  const lines = [
    "Cash deposited from external sources (not trading income), with cash taken out shown below.",
    cumulativePeriodTip(isForecastView),
  ];
  const otherWithdrawn = Math.max(0, totalWithdrawn - taxWithdrawnLedger);
  if (taxWithdrawnLedger > 0) {
    lines.push(`Withdrawals include ${fmtUsdWhole(taxWithdrawnLedger)} tax (cash ledger).`);
    if (otherWithdrawn > 0) {
      lines.push(`Other withdrawals: ${fmtUsdWhole(otherWithdrawn)}.`);
    }
  }
  return dataTipParagraphs(...lines);
}

/** Totals through a selected month — actual income for past/current, target ROC
 * income for projected forecast months in range. */
function totalsThroughMonth(
  months: IncomeMonthBucket[],
  projectedTimeline: Map<string, OwnCapitalPoint>,
  throughMonth: string,
  scopeYear: number | null,
  ledger: Record<string, OwnCapitalEntry>,
  allMonthsByKey: Map<string, IncomeMonthBucket>,
): {
  income: number;
  ownCapital: number;
  cashAdded: number;
  withdrawn: number;
  expenses: ReturnType<typeof aggregateExpenses>;
} {
  const cm = currentMonthKey();
  const monthsByKey = new Map(months.map((month) => [month.month, month]));
  let income = 0;
  let cashAdded = 0;
  let withdrawn = 0;
  let margin = 0;
  let perTrade = 0;
  let tradeCount = 0;

  const monthKeys = new Set<string>([
    ...months.map((month) => month.month),
    ...projectedTimeline.keys(),
    ...Object.keys(ledger),
  ]);

  for (const monthKey of [...monthKeys].sort()) {
    if (monthKey > throughMonth) break;
    if (!monthInScope(monthKey, scopeYear)) continue;

    const bucket = monthsByKey.get(monthKey) ?? allMonthsByKey.get(monthKey);
    const point = projectedTimeline.get(monthKey);
    if (monthKey > cm) {
      income += point?.income ?? 0;
    } else if (bucket) {
      income += cashIncome(bucket);
    } else {
      income += point?.income ?? 0;
    }

    if (point) {
      cashAdded += point.cashAdded;
      withdrawn += point.withdrawn;
    } else {
      const entry = ledgerEntryForMonth(monthKey, ledger, projectedTimeline);
      cashAdded += entry.cashAdded;
      withdrawn += entry.withdrawn;
    }

    const entry = ledgerEntryForMonth(monthKey, ledger, projectedTimeline);
    const count = bucket ? countTradesInMonth(bucket) : 0;
    margin += point?.effectiveMarginCost ?? entry.marginCost ?? 0;
    perTrade += perTradeExpenseTotal(entry, count);
    tradeCount += count;
  }

  return {
    income,
    ownCapital: projectedTimeline.get(throughMonth)?.ownCapital ?? 0,
    cashAdded,
    withdrawn,
    expenses: { margin, perTrade, tradeCount, total: margin + perTrade },
  };
}

function marginForecastBadgeHtml(): string {
  return `<span class="sfe-margin-forecast-badge">forecast</span>`;
}

function renderForecastIncomeDetails(marginCost: number): string {
  return `
    <div class="sfe-income-kpi-details">
      <span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold">Net target ROC</span>
      <span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">Margin cost ${marginForecastBadgeHtml()}<b>-${fmtUsdWhole(marginCost)}</b></span>
    </div>
  `;
}

/**
 * Capital deployed per month, keyed by YYYY-MM.
 *
 * For calls, every STO's collateral (strike × contracts × 100) is simply
 * summed — the underlying shares stay tied up regardless of the call being
 * closed/reopened, so there's no capital to "reuse".
 *
 * For puts, closing a position (BTC, assignment, or expiration) frees its
 * cash collateral for reuse. So instead of summing every STO, we replay each
 * ticker's puts in chronological order *within the month* and track the
 * running concurrently-deployed collateral, taking its peak. That way a put
 * that's opened, closed, and reopened on the same ticker within the same
 * month only counts its collateral once (the capital was recycled, not
 * multiplied) — while genuinely overlapping puts (different lots open at
 * the same time) still add up. Capital carried over from a prior month
 * (opened earlier, closed this month) is not counted here — see
 * buildIncomeCalendar's month attribution, which books it in the month it
 * was actually opened.
 */
function computeDeployedCapitalByMonth(
  txns: WheelTransaction[],
  scopeYear: number | null,
): Map<string, number> {
  const isPutClose = (txn: WheelTransaction): boolean =>
    txn.instrument_type === "option" &&
    txn.side === "put" &&
    (isBuyToCloseTxn(txn) ||
      isOptionExpirationTxn(txn) ||
      txn.close_reason === "assigned" ||
      txn.close_reason === "expired");

  const byMonthTicker = new Map<string, Map<string, WheelTransaction[]>>();
  for (const txn of txns) {
    if (txn.instrument_type !== "option" || !txn.ticker) continue;
    const date = txn.trade_date;
    if (!date || !assignIncomeInScope(date, scopeYear)) continue;
    if (!isSellToOpenTxn(txn) && !isPutClose(txn)) continue;

    const monthKey = date.slice(0, 7);
    let byTicker = byMonthTicker.get(monthKey);
    if (!byTicker) {
      byTicker = new Map();
      byMonthTicker.set(monthKey, byTicker);
    }
    const list = byTicker.get(txn.ticker) ?? [];
    list.push(txn);
    byTicker.set(txn.ticker, list);
  }

  const result = new Map<string, number>();
  for (const [monthKey, byTicker] of byMonthTicker) {
    let monthTotal = 0;
    for (const tickerTxns of byTicker.values()) {
      const sorted = [...tickerTxns].sort((a, b) =>
        (a.trade_date ?? "").localeCompare(b.trade_date ?? ""),
      );

      let putCurrent = 0;
      let putPeak = 0;
      let callSum = 0;

      for (const txn of sorted) {
        const collateral = (Number(txn.strike) || 0) * (Number(txn.quantity) || 0) * 100;
        if (txn.side === "put") {
          if (isSellToOpenTxn(txn)) {
            putCurrent += collateral;
            putPeak = Math.max(putPeak, putCurrent);
          } else {
            putCurrent = Math.max(0, putCurrent - collateral);
          }
        } else if (isSellToOpenTxn(txn)) {
          callSum += collateral;
        }
      }

      monthTotal += putPeak + callSum;
    }
    result.set(monthKey, monthTotal);
  }

  return result;
}

function buildIncomeCalendar(
  txns: WheelTransaction[],
  scopeYear: number | null,
  opts?: { omitLongOptionFlow?: boolean },
): { breakdown: IncomeBreakdown; months: IncomeMonthBucket[] } {
  const omitLongOptionFlow = opts?.omitLongOptionFlow === true;
  const incomeTxns = classifyOptionIncomeTxns(txns);
  lastIncomeTxnsForTrace = incomeTxns;
  const transactionLinks = buildTransactionLinkMeta(incomeTxns);
  const txnById = new Map<number, WheelTransaction>();
  for (const txn of incomeTxns) {
    if (txn.id != null) txnById.set(txn.id, txn);
  }
  const byDate = new Map<string, IncomeDayBucket>();

  const touch = (date: string): IncomeDayBucket => {
    let day = byDate.get(date);
    if (!day) {
      day = emptyDay(date);
      byDate.set(date, day);
    }
    return day;
  };

  // Covered-call assignment P/L keyed for merging into ASGN rows. Tracks the
  // shares-weighted average purchase cost alongside the income so the table /
  // mobile detail can show "avg" (share cost basis used for the P/L calc).
  const assignIncomeByOcc = new Map<string, CallAssignIncomeAgg>();
  // Avg purchase cost by contract, even when the income book-date is outside
  // the current year filter — used so ASGN rows still show avg inline.
  const callAssignAvgByContract = new Map<string, number>();
  for (const ev of listAssignIncomeEvents(incomeTxns)) {
    const contractKey = `${ev.ticker}|${ev.strike}|${ev.exp_date ?? ""}`;
    if (ev.avg > 0) callAssignAvgByContract.set(contractKey, ev.avg);
    if (!assignIncomeInScope(ev.date, scopeYear)) continue;
    const key = `${ev.date}|${ev.ticker}|call|${ev.strike}|${ev.exp_date ?? ""}`;
    const shares = Math.round(ev.contracts * 100);
    const prev = assignIncomeByOcc.get(key) ?? { income: 0, avgCost: 0, shares: 0 };
    const totalShares = prev.shares + shares;
    const avgCost =
      ev.avg > 0 && totalShares > 0
        ? (prev.avgCost * prev.shares + ev.avg * shares) / totalShares
        : prev.avgCost > 0
          ? prev.avgCost
          : ev.avg;
    assignIncomeByOcc.set(key, { income: prev.income + ev.amount, avgCost, shares: totalShares });
  }

  const deployedByMonth = computeDeployedCapitalByMonth(incomeTxns, scopeYear);

  // STO premiums (month received), STC credits, BTC/BTO cost, ASGN (call assignment P/L)
  const shownPnlSettlements = visiblePnlSettlementIds(incomeTxns);
  for (const txn of incomeTxns) {
    if (txn.instrument_type !== "option") continue;
    if (isPnlTradeHistoryRow(txn) && !(txn.id != null && shownPnlSettlements.has(txn.id))) continue;
    const typed = optionTxnTypeLabel(txn);
    if (!typed) continue;

    const { kind, label } = typed;
    const date = kind === "asgn" ? assignmentEventDate(txn) : txn.trade_date;
    if (!date || !assignIncomeInScope(date, scopeYear)) continue;

    const notional = txnNotional(txn);
    const day = touch(date);

    let amount = 0;
    let credit = "—";
    let btc = "—";
    let avg = "—";
    let assignCost = 0;

    if (kind === "sto") {
      // Premium received — attributed to STO trade_date, not expiration.
      amount = notional;
      day.premiums += notional;
      credit = notional > 0 ? fmtUsd(notional) : "—";
    } else if (kind === "stc") {
      amount = notional;
      if (!omitLongOptionFlow) day.stcCredits += notional;
      credit = notional > 0 ? fmtUsd(notional) : "—";
    } else if (kind === "btc") {
      amount = -notional;
      day.btcCost += notional;
      btc = notional > 0 ? fmtUsd(notional) : "—";
    } else if (kind === "bto") {
      amount = -notional;
      if (!omitLongOptionFlow) day.btoCost += notional;
      btc = notional > 0 ? fmtUsd(notional) : "—";
    } else if (kind === "asgn") {
      if (txn.side === "call") {
        // Call assignment P/L — shown on the option expiration-date row.
        const agg = takeCallAssignIncomeAgg(assignIncomeByOcc, txn, date);
        const income = agg?.income ?? 0;
        amount = income;
        day.assignIncome += income;
        assignCost =
          agg && agg.avgCost > 0
            ? agg.avgCost
            : callAssignAvgByContract.get(
                  `${txn.ticker ?? ""}|${txn.strike ?? ""}|${txn.exp_date ?? ""}`,
                ) ?? 0;
        avg = assignCost > 0 ? fmtUsd(assignCost) : "—";
      } else {
        // A put assignment acquires shares at the strike. It has no immediate
        // realized income, but remains visible as the option settlement.
        assignCost = Number(txn.strike) || 0;
        avg = assignCost > 0 ? fmtUsd(assignCost) : "—";
      }
    }

    let detail: string | undefined;
    if (kind === "asgn") {
      const contracts = Math.abs(Number(txn.quantity) || 0);
      const shares = Math.round(contracts * 100);
      const cost = assignCost > 0 ? assignCost : Number(txn.strike) || 0;
      // Mobile/desktop compact label: shares × share cost (avg for calls, strike for puts).
      if (shares > 0 && cost > 0) detail = `${shares}×${fmtUsd(cost)}`;
    }

    const link = transactionLinkMeta(txn, transactionLinks);
    const contractsAbs = Math.abs(txnContracts(txn));
    const premiumRaw = Number(txn.avg_price);
    const premium = Number.isFinite(premiumRaw) && premiumRaw > 0 ? premiumRaw : null;
    const { system, account } = incomeLineSystemAccount(txn);
    const marks = incomeLineMarks(txn);
    day.lines.push({
      kind,
      label,
      ticker: txn.ticker ?? "—",
      side: txn.side ?? "—",
      strike: txn.strike != null ? String(txn.strike) : "—",
      qty: txn.quantity != null ? String(txn.quantity) : "—",
      open: resolveIncomeLineOpenDate(txn, txnById, transactionLinks),
      exp: incomeLineExp(txn.exp_date),
      credit,
      btc,
      avg,
      symbol: kind === "asgn" ? "—" : txn.exp_date ? `exp ${txn.exp_date}` : "—",
      amount,
      incomeAccounted: kind !== "bto" && kind !== "stc" ? true : !omitLongOptionFlow,
      detail,
      contracts: contractsAbs > 0 ? contractsAbs : null,
      premium: kind === "asgn" ? null : premium,
      dte: kind === "asgn" ? null : optionDteOnDate(date, txn.exp_date),
      txnId: txn.id,
      linkedTxnIds: link.linkedIds,
      linkStatus: link.status,
      sortAt: txnDisplaySortAt(txn),
      system,
      account,
      ...marks,
      ...incomeLineUnderlying(txn),
      ...incomeLineOpenPop(txn),
    });
  }

  const stockSellEvents = listStockSellIncomeEvents(incomeTxns);
  const accountedStockSells = new Set<WheelTransaction>();
  for (const sale of stockSellEvents) {
    if (!assignIncomeInScope(sale.date, scopeYear)) continue;
    accountedStockSells.add(sale.transaction);
    const day = touch(sale.date);
    day.stockSellIncome += sale.amount;
    const link = transactionLinkMeta(sale.transaction, transactionLinks);
    const { system, account } = incomeLineSystemAccount(sale.transaction);
    const saleMarks = incomeLineMarks(sale.transaction);
    day.lines.push({
      kind: "sell",
      label: "SELL",
      ticker: sale.ticker,
      side: "stock",
      strike: "—",
      qty: String(sale.shares),
      open: "—",
      exp: "—",
      credit: fmtUsd(sale.salePrice * sale.shares),
      btc: "—",
      avg: fmtUsd(sale.avgCost),
      symbol: `sold @ ${fmtUsd(sale.salePrice)}`,
      amount: sale.amount,
      incomeAccounted: true,
      txnId: sale.transaction.id,
      linkedTxnIds: link.linkedIds,
      linkStatus: link.status,
      sortAt: txnDisplaySortAt(sale.transaction),
      system,
      account,
      ...saleMarks,
    });
  }

  // Show stock ledger rows for traceability. Assignment companions are kept
  // (paired under/over the ASG row) rather than suppressed.
  const stockAvgById = buildStockLegAvgCostDetailIndex(incomeTxns);
  const shownStockIds = new Set<number>();
  for (const txn of incomeTxns) {
    if (
      txn.instrument_type !== "stock" ||
      (txn.action !== "buy" && txn.action !== "sell") ||
      !txn.trade_date ||
      !assignIncomeInScope(txn.trade_date, scopeYear) ||
      accountedStockSells.has(txn)
    ) {
      continue;
    }
    if (txn.id != null) shownStockIds.add(txn.id);
    const paired =
      txn.assignment_companion === true ||
      (findShortOptionForAssignmentStock(incomeTxns, txn) != null &&
        (txn.action === "buy" || txn.action === "sell"));
    const avgSnap = txn.id != null ? stockAvgById.get(txn.id) : undefined;
    touch(txn.trade_date).lines.push(
      buildStockTraceLine(txn, transactionLinks, {
        assignmentPaired: paired,
        holdingAvgBefore: avgSnap?.beforeAvg ?? null,
        holdingAvgAfter: avgSnap?.afterAvg ?? null,
      }),
    );
  }

  // ASG without a same-day stock line (link / strike match) — pull companion in.
  for (const day of byDate.values()) {
    for (const asgn of day.lines.filter((line) => line.kind === "asgn")) {
      if (findAssignmentCompanionStock(
        asgn,
        day.lines.filter((line) => line.kind === "buy" || line.kind === "sell"),
      )) {
        continue;
      }
      const stockAction = asgn.side === "put" ? "buy" : asgn.side === "call" ? "sell" : null;
      if (!stockAction || !asgn.ticker) continue;
      const shares = assignmentStockShares(asgn);
      const strike = Number(asgn.strike) || 0;
      const companion = incomeTxns.find((txn) => {
        if (
          txn.instrument_type !== "stock" ||
          txn.action !== stockAction ||
          txn.ticker !== asgn.ticker
        ) {
          return false;
        }
        if (txn.trade_date !== day.date) return false;
        if (
          asgn.txnId != null &&
          txn.inferred_linked_transaction_ids?.includes(asgn.txnId)
        ) {
          return true;
        }
        if (txn.id != null && asgn.linkedTxnIds.includes(txn.id)) return true;
        if (shares > 0 && Math.abs((Number(txn.quantity) || 0) - shares) > 0.1) return false;
        if (strike > 0 && Math.abs((Number(txn.avg_price) || 0) - strike) > 0.06) {
          return false;
        }
        return shares > 0;
      });
      if (!companion || (companion.id != null && shownStockIds.has(companion.id))) continue;
      if (companion.id != null) shownStockIds.add(companion.id);
      // Cross-link for pairing / LINKED ID column.
      if (asgn.txnId != null && companion.id != null) {
        if (!asgn.linkedTxnIds.includes(companion.id)) asgn.linkedTxnIds.push(companion.id);
        asgn.linkStatus = asgn.linkStatus === "ledger" ? "ledger" : "inferred";
      }
      const avgSnap = companion.id != null ? stockAvgById.get(companion.id) : undefined;
      day.lines.push(
        buildStockTraceLine(companion, transactionLinks, {
          assignmentPaired: true,
          holdingAvgBefore: avgSnap?.beforeAvg ?? null,
          holdingAvgAfter: avgSnap?.afterAvg ?? null,
        }),
      );
    }
  }

  // Stock settlement without ASG option row (e.g. TT put-assign buy) — infer ASG.
  for (const day of byDate.values()) {
    for (const stockLine of [
      ...day.lines.filter((line) => line.kind === "buy" || line.kind === "sell"),
    ]) {
      const alreadyPaired = day.lines.some(
        (line) =>
          line.kind === "asgn" &&
          (incomeLinesLinked(line, stockLine) ||
            (line.ticker === stockLine.ticker &&
              ((stockLine.kind === "buy" && line.side === "put") ||
                (stockLine.kind === "sell" && line.side === "call")) &&
              Math.abs(assignmentStockShares(line) - (Number(stockLine.qty) || 0)) <
                0.1)),
      );
      if (alreadyPaired) continue;

      const stockTxn =
        stockLine.txnId != null
          ? incomeTxns.find((txn) => txn.id === stockLine.txnId)
          : undefined;
      if (!stockTxn) continue;

      const existingAsgnTxn = incomeTxns.find(
        (txn) =>
          isAssignedOptionTxn(txn) &&
          txn.ticker === stockTxn.ticker &&
          (assignmentEventDate(txn) === day.date || txn.trade_date === day.date) &&
          Math.abs((Number(txn.strike) || 0) - (Number(stockTxn.avg_price) || 0)) <
            0.06,
      );
      if (
        existingAsgnTxn &&
        day.lines.some((line) => line.txnId === existingAsgnTxn.id && line.kind === "asgn")
      ) {
        continue;
      }

      const shortOpt = findShortOptionForAssignmentStock(incomeTxns, stockTxn);
      if (!shortOpt && !existingAsgnTxn) continue;
      if (
        !existingAsgnTxn &&
        shortOpt &&
        !stockTxn.assignment_companion &&
        String(shortOpt.exp_date ?? "").slice(0, 10) !== stockTxn.trade_date
      ) {
        // Only infer ASG from a non-flagged stock buy/sell when it lands on the
        // short option's expiration date (classic put/call assignment settlement).
        continue;
      }

      const sourceOpt = existingAsgnTxn ?? shortOpt!;
      const strike = Number(sourceOpt.strike) || Number(stockTxn.avg_price) || 0;
      const contracts =
        txnContracts(sourceOpt) || Math.round((Number(stockTxn.quantity) || 0) / 100);
      const shares = Math.round(contracts * 100);
      const cost = Number(stockTxn.avg_price) || strike;
      const linkedIds = [
        ...new Set(
          [
            ...(stockTxn.id != null ? [stockTxn.id] : []),
            ...(sourceOpt.id != null ? [sourceOpt.id] : []),
            ...stockLine.linkedTxnIds,
          ].filter((id): id is number => Number.isFinite(id)),
        ),
      ];
      for (const id of linkedIds) {
        if (id !== stockTxn.id && !stockLine.linkedTxnIds.includes(id)) {
          stockLine.linkedTxnIds.push(id);
        }
      }
      stockLine.linkStatus =
        stockLine.linkStatus === "ledger" ? "ledger" : "inferred";
      stockLine.symbol = `assignment stock${cost > 0 ? ` @ ${fmtUsd(cost)}` : ""}`;

      const metaTxn = existingAsgnTxn ?? stockTxn;
      const { system, account } = incomeLineSystemAccount(metaTxn);
      const asgnMarks = incomeLineMarks(metaTxn);
      day.lines.push({
        kind: "asgn",
        label: "ASG",
        ticker: stockTxn.ticker ?? "—",
        side: sourceOpt.side ?? (stockLine.kind === "buy" ? "put" : "call"),
        strike: strike > 0 ? String(strike) : "—",
        qty: contracts > 0 ? String(contracts) : "—",
        open: resolveIncomeLineOpenDate(sourceOpt, txnById, transactionLinks),
        exp: incomeLineExp(sourceOpt.exp_date),
        credit: "—",
        btc: "—",
        avg: cost > 0 ? fmtUsd(cost) : "—",
        symbol: "—",
        amount: 0,
        incomeAccounted: true,
        detail: shares > 0 && cost > 0 ? `${shares}×${fmtUsd(cost)}` : undefined,
        contracts: contracts > 0 ? contracts : null,
        premium: null,
        dte: null,
        txnId: existingAsgnTxn?.id,
        linkedTxnIds: linkedIds,
        linkStatus: existingAsgnTxn ? "ledger" : "inferred",
        sortAt: existingAsgnTxn
          ? txnDisplaySortAt(existingAsgnTxn)
          : Math.max(0, (stockLine.sortAt ?? 0) - 1),
        system: system || stockLine.system || "",
        account: account || stockLine.account || "",
        ...asgnMarks,
        // The settlement row carries both sides; a bare short leg only knows its open.
        ...incomeLineUnderlying(existingAsgnTxn ?? sourceOpt),
        ...incomeLineOpenPop(existingAsgnTxn ?? sourceOpt),
      });
    }
  }

  // Any call-assignment P/L that didn't match a txn row
  for (const [key, agg] of assignIncomeByOcc) {
    if (!agg.income) continue;
    const [date, ticker, side, strike, exp] = key.split("|");
    const day = touch(date);
    day.assignIncome += agg.income;
    const strikeNum = Number(strike) || 0;
    const cost = agg.avgCost > 0 ? agg.avgCost : strikeNum;
    const avgText = cost > 0 ? fmtUsd(cost) : "—";
    const leftoverContracts =
      agg.shares > 0 ? Math.max(1, Math.round(agg.shares / 100)) : null;
    day.lines.push({
      kind: "asgn",
      label: "ASG",
      ticker,
      side,
      strike,
      qty: leftoverContracts != null ? String(leftoverContracts) : "—",
      open: "—",
      exp: incomeLineExp(exp),
      credit: "—",
      btc: "—",
      avg: avgText,
      symbol: "—",
      amount: agg.income,
      incomeAccounted: true,
      detail: agg.shares > 0 && cost > 0 ? `${agg.shares}×${fmtUsd(cost)}` : undefined,
      contracts: leftoverContracts,
      premium: null,
      dte: null,
      linkedTxnIds: [],
      linkStatus: "unlinked",
      // Synthetic leftover row — keep after real ledger lines for the day.
      sortAt: 0,
    });
  }

  for (const day of byDate.values()) {
    day.total = cashIncome(day);
    sortIncomeLinesWithAssignmentPairs(day.lines);
  }

  const monthsMap = new Map<string, IncomeMonthBucket>();
  for (const day of [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))) {
    if (
      !day.lines.length &&
      day.total === 0 &&
      day.premiums === 0 &&
      day.stcCredits === 0 &&
      day.assignIncome === 0 &&
      day.stockSellIncome === 0 &&
      day.btcCost === 0 &&
      day.btoCost === 0
    ) {
      continue;
    }
    const monthKey = day.date.slice(0, 7);
    let month = monthsMap.get(monthKey);
    if (!month) {
      month = {
        month: monthKey,
        label: monthLabel(monthKey),
        premiums: 0,
        stcCredits: 0,
        assignIncome: 0,
        stockSellIncome: 0,
        btcCost: 0,
        btoCost: 0,
        total: 0,
        deployedCapital: deployedByMonth.get(monthKey) ?? 0,
        days: [],
      };
      monthsMap.set(monthKey, month);
    }
    month.days.push(day);
    month.premiums += day.premiums;
    month.stcCredits += day.stcCredits;
    month.assignIncome += day.assignIncome;
    month.stockSellIncome += day.stockSellIncome;
    month.btcCost += day.btcCost;
    month.btoCost += day.btoCost;
    month.total += day.total;
  }

  const months = [...monthsMap.values()].sort((a, b) => b.month.localeCompare(a.month));
  const breakdown: IncomeBreakdown = {
    premiums: months.reduce((s, m) => s + m.premiums, 0),
    stcCredits: months.reduce((s, m) => s + m.stcCredits, 0),
    assignIncome: months.reduce((s, m) => s + m.assignIncome, 0),
    stockSellIncome: months.reduce((s, m) => s + m.stockSellIncome, 0),
    btcCost: months.reduce((s, m) => s + m.btcCost, 0),
    btoCost: months.reduce((s, m) => s + m.btoCost, 0),
    total: 0,
  };
  breakdown.total = cashIncome(breakdown);

  return { breakdown, months };
}

/** Same own-ledger trading income as Analytics Total Income (before est. tax). */
export function ownLedgerIncomeTotals(
  txns: Array<Record<string, unknown>>,
): { total: number; byYear: Record<string, number>; byMonth: Record<string, number> } {
  const { breakdown, months } = buildIncomeCalendar(txns as WheelTransaction[], null, {
    omitLongOptionFlow: true,
  });
  const byYear: Record<string, number> = {};
  const byMonth: Record<string, number> = {};
  for (const month of months) {
    const year = month.month.slice(0, 4);
    if (year.length !== 4) continue;
    byYear[year] = (byYear[year] ?? 0) + month.total;
    byMonth[month.month] = (byMonth[month.month] ?? 0) + month.total;
  }
  return { total: breakdown.total, byYear, byMonth };
}

/** Re-attribute one surviving line to its income category. Mirrors the
 * accounting done while the calendar is built, so an unfiltered pass
 * reproduces the original day sums exactly. */
function addIncomeLineToDayTotals(day: IncomeDayBucket, line: IncomeLine): void {
  if (!line.incomeAccounted) return;
  switch (line.kind) {
    case "sto":
      day.premiums += line.amount;
      break;
    case "stc":
      day.stcCredits += line.amount;
      break;
    case "btc":
      day.btcCost -= line.amount;
      break;
    case "bto":
      day.btoCost -= line.amount;
      break;
    case "asgn":
      day.assignIncome += line.amount;
      break;
    case "sell":
      day.stockSellIncome += line.amount;
      break;
    default:
      break;
  }
}

/** Narrows income rows to a single ledger type code (STO / ASG / …) and
 * recomputes day, month and headline sums from the surviving lines. Runs after
 * the calendar is built so the avg-cost replay behind assignment P/L still
 * sees every transaction. Deployed capital is left alone — collateral is a
 * property of the puts written, not of the row type being inspected. */
function applyIncomeTypeFilter(
  breakdown: IncomeBreakdown,
  months: IncomeMonthBucket[],
  kind: AnalyticsTypeFilter,
): { breakdown: IncomeBreakdown; months: IncomeMonthBucket[] } {
  if (kind === "all") return { breakdown, months };

  const kept: IncomeMonthBucket[] = [];
  for (const month of months) {
    const days: IncomeDayBucket[] = [];
    for (const day of month.days) {
      const lines = day.lines.filter((line) => line.kind === kind);
      if (!lines.length) continue;
      const next = emptyDay(day.date);
      next.lines = lines;
      for (const line of lines) addIncomeLineToDayTotals(next, line);
      next.total = cashIncome(next);
      days.push(next);
    }
    if (!days.length) continue;
    const nextMonth: IncomeMonthBucket = {
      ...month,
      premiums: 0,
      stcCredits: 0,
      assignIncome: 0,
      stockSellIncome: 0,
      btcCost: 0,
      btoCost: 0,
      total: 0,
      days,
    };
    for (const day of days) {
      nextMonth.premiums += day.premiums;
      nextMonth.stcCredits += day.stcCredits;
      nextMonth.assignIncome += day.assignIncome;
      nextMonth.stockSellIncome += day.stockSellIncome;
      nextMonth.btcCost += day.btcCost;
      nextMonth.btoCost += day.btoCost;
      nextMonth.total += day.total;
    }
    kept.push(nextMonth);
  }

  const nextBreakdown: IncomeBreakdown = {
    premiums: kept.reduce((sum, m) => sum + m.premiums, 0),
    stcCredits: kept.reduce((sum, m) => sum + m.stcCredits, 0),
    assignIncome: kept.reduce((sum, m) => sum + m.assignIncome, 0),
    stockSellIncome: kept.reduce((sum, m) => sum + m.stockSellIncome, 0),
    btcCost: kept.reduce((sum, m) => sum + m.btcCost, 0),
    btoCost: kept.reduce((sum, m) => sum + m.btoCost, 0),
    total: 0,
  };
  nextBreakdown.total = cashIncome(nextBreakdown);

  return { breakdown: nextBreakdown, months: kept };
}

/** Distinct option/stock transaction years available, newest-to-oldest left
 * to right — powers the standalone year selector (no dependency on native
 * Analytics year chips). */
function buildYearOptions(txns: WheelTransaction[], forecastEnabled: boolean): number[] {
  const years = new Set<number>();
  for (const txn of txns) {
    if (txn.instrument_type !== "option" && txn.instrument_type !== "stock") continue;
    const year = parseYear(txn.trade_date);
    if (year != null) years.add(year);
  }
  const nowYear = currentCalendarYear();
  years.add(nowYear);
  if (forecastEnabled) {
    for (let year = nowYear; year <= forecastMaxYear(); year += 1) {
      years.add(year);
    }
  }
  const sorted = [...years].sort((a, b) => b - a);
  return forecastEnabled ? sorted : sorted.filter((year) => year <= nowYear);
}

/** Coerce invalid stored values to a chip that exists; All is always offered. */
function normalizeAnalyticsYear(year: number | "all", years: number[]): number | "all" {
  if (year === "all") return "all";
  if (years.includes(year)) return year;
  return years.includes(currentCalendarYear()) ? currentCalendarYear() : years.at(0) ?? currentCalendarYear();
}

/** Sum income components for all months in `months` up to and including `throughMonth`. */
function cumulativeIncomeBreakdown(
  months: IncomeMonthBucket[],
  throughMonth: string,
): IncomeBreakdown {
  let premiums = 0;
  let stcCredits = 0;
  let assignIncome = 0;
  let stockSellIncome = 0;
  let btcCost = 0;
  let btoCost = 0;

  for (const month of months) {
    if (month.month > throughMonth) continue;
    premiums += month.premiums;
    stcCredits += month.stcCredits;
    assignIncome += month.assignIncome;
    stockSellIncome += month.stockSellIncome;
    btcCost += month.btcCost;
    btoCost += month.btoCost;
  }

  const parts = { premiums, stcCredits, assignIncome, stockSellIncome, btcCost, btoCost };
  return { ...parts, total: cashIncome(parts) };
}

/** Active Analytics(L) row filters — shown on Total Income so a stuck ticker
 * cannot look like a broken total (Electron keeps its own localStorage). */
function incomeFilterScopeSuffix(): string {
  const parts: string[] = [];
  if (analyticsTickerFilter) parts.push(analyticsTickerFilter);
  if (analyticsTypeFilter !== "all") parts.push(analyticsTypeFilter.toUpperCase());
  return parts.length ? ` · ${parts.join(" · ")}` : "";
}

/** Top Total Income KPI: cumulative income through the selected month across
 * the full ticker/type-filtered history (same window as Cash / Own Capital).
 * Year chips only scope the monthly section below — not this total. */
function displayIncomeBreakdown(
  _breakdown: IncomeBreakdown,
  months: IncomeMonthBucket[],
  selectedMonthKey: string | null,
  selectedYear: number | "all",
  _forecastEnabled = true,
): { breakdown: IncomeBreakdown; scopeLabel: string } {
  const cm = currentMonthKey();
  const throughMonth =
    selectedMonthKey && selectedMonthKey <= cm ? selectedMonthKey : cm;
  const cumulative = cumulativeIncomeBreakdown(months, throughMonth);
  const filterSuffix = incomeFilterScopeSuffix();
  if (selectedYear === "all" && !selectedMonthKey) {
    return { breakdown: cumulative, scopeLabel: `Up to current month${filterSuffix}` };
  }
  return {
    breakdown: cumulative,
    scopeLabel: `through ${monthLabel(throughMonth)}${filterSuffix}`,
  };
}

/** Small income-component sub-details shown as
 * stacked, right-aligned lines to the right of the big Total Income
 * number, so the components of income are visible without needing
 * their own separate KPI cards. */
function renderIncomeKpiDetails(
  breakdown: IncomeBreakdown,
  cumulativeMarginCost = 0,
): string {
  const assignCls =
    breakdown.assignIncome < 0 ? "sfe-neg" : breakdown.assignIncome > 0 ? "roi-strong" : "";
  const stockSellCls =
    breakdown.stockSellIncome < 0
      ? "sfe-neg"
      : breakdown.stockSellIncome > 0
        ? "roi-strong"
        : "";
  const btoLine =
    breakdown.btoCost > 0
      ? `<span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">BTO cost <b>-${fmtUsdWhole(breakdown.btoCost)}</b></span>`
      : "";
  return `
    <div class="sfe-income-kpi-details">
      <span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold sfe-income-kpi-premiums">Premiums <b>${fmtUsdWhole(breakdown.premiums)}</b></span>
      ${
        breakdown.stcCredits > 0
          ? `<span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold">STC <b>${fmtUsdWhole(breakdown.stcCredits)}</b></span>`
          : ""
      }
      <span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold ${assignCls}">Call Assgn. <b>${fmtUsdWhole(breakdown.assignIncome)}</b></span>
      ${
        breakdown.stockSellIncome !== 0
          ? `<span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold ${stockSellCls}">Stock sales <b>${fmtUsdWhole(breakdown.stockSellIncome)}</b></span>`
          : ""
      }
      <span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">BTC cost <b>-${fmtUsdWhole(breakdown.btcCost)}</b></span>
      ${btoLine}
      <span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">Tot. margin cost <b>-${fmtUsdWhole(cumulativeMarginCost)}</b></span>
    </div>
  `;
}

function updateIncomeKpiDetails(
  card: HTMLElement,
  breakdown: IncomeBreakdown,
  cumulativeMarginCost: number,
): void {
  const details = card.querySelector(".sfe-income-kpi-details");
  if (details) {
    details.outerHTML = renderIncomeKpiDetails(breakdown, cumulativeMarginCost);
  }
  const valueEl = card.querySelector<HTMLElement>(".sfe-income-kpi-row > .kpi-v");
  if (valueEl) valueEl.textContent = fmtUsdWhole(breakdown.total);
}

function updateIncomeKpiPeriod(card: HTMLElement, scopeLabel?: string): void {
  let period = card.querySelector<HTMLElement>(".sfe-income-kpi-period");
  if (!scopeLabel) {
    period?.remove();
    return;
  }
  if (!period) {
    period = document.createElement("span");
    period.className = "sfe-kpi-period sfe-income-kpi-period";
    const title = card.querySelector(".kpi-k");
    title?.insertAdjacentElement("afterend", period);
  }
  period.textContent = scopeLabel;
}

function cumulativePeriodTip(isForecastView: boolean): string {
  return isForecastView
    ? "Cumulative since the beginning of the tracked period through the selected forecast month, including that month."
    : "Cumulative since the beginning of the tracked period through the selected month, including this month.";
}

function updateKpiHint(card: HTMLElement, tip: string): void {
  card.querySelector<HTMLElement>(".hint")?.setAttribute("data-tip", tip);
}

function renderTotalIncomeKpi(
  breakdown: IncomeBreakdown,
  scopeLabel?: string,
  cumulativeMarginCost = 0,
): HTMLElement {
  const card = document.createElement("div");
  card.id = TOTAL_INCOME_KPI_ID;
  card.className = `kpi ${TOTAL_INCOME_KPI_CLASS}`;
  card.setAttribute("data-sfe-injected", "true");
  card.dataset.grossIncome = String(breakdown.total);
  card.dataset.marginCost = String(cumulativeMarginCost);

  const tip =
    "Trading income through the selected month: STO premiums − BTC/BTO + assignment P/L + stock-sale P/L (+ STC). Total margin cost is shown as a line item but is not subtracted from this headline. Estimated tax paid lives on the Tax Due card."

  const periodHtml = scopeLabel
    ? `<span class="sfe-kpi-period sfe-income-kpi-period">${scopeLabel}</span>`
    : "";

  card.innerHTML =
    `<span class="kpi-k">Total Income` +
    `<span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span></span>` +
    periodHtml +
    `<div class="sfe-income-kpi-row">` +
    `<span class="kpi-v roi-strong">${fmtUsdWhole(breakdown.total)}</span>` +
    renderIncomeKpiDetails(breakdown, cumulativeMarginCost) +
    `</div>`;

  return card;
}

function renderStockHoldingsKpi(summary: StockOpenSummary): HTMLElement {
  const card = document.createElement("div");
  card.id = STOCK_HOLDINGS_KPI_ID;
  card.className = "kpi";
  card.setAttribute("data-sfe-injected", "true");
  card.innerHTML =
    `<span class="kpi-k">Stock Positions | Shares` +
    `<span class="hint" tabindex="0" role="img" data-tip="Net long tickers you hold and total shares across all of them.">i</span></span>` +
    `<div class="sfe-stock-holdings-kpi-row">` +
    `<span class="kpi-v sfe-stock-holdings-count">${fmtNum(summary.count)}</span>` +
    `<span class="sfe-stock-holdings-sep">|</span>` +
    `<span class="kpi-v sfe-stock-holdings-shares">${fmtNum(summary.quantity)}</span>` +
    `</div>`;
  return card;
}

function renderStockCostBasisKpi(
  summary: StockOpenSummary,
  tip = "What you paid: shares × average buy price per ticker.",
): HTMLElement {
  const card = document.createElement("div");
  card.id = STOCK_COST_BASIS_KPI_ID;
  card.className = "kpi";
  card.setAttribute("data-sfe-injected", "true");
  card.innerHTML =
    `<span class="kpi-k">Cost basis` +
    `<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(tip)}">i</span></span>` +
    `<span class="kpi-v">${fmtUsdWhole(summary.cost_basis)}</span>`;
  return card;
}

function renderStockMarketValueKpi(
  summary: StockOpenSummary,
  tip = "Current value of your shares from live stock quotes and chain marks.",
): HTMLElement {
  const card = document.createElement("div");
  card.id = STOCK_MARKET_VALUE_KPI_ID;
  card.className = "kpi";
  card.setAttribute("data-sfe-injected", "true");
  card.innerHTML =
    `<span class="kpi-k">Market value` +
    `<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(tip)}">i</span></span>` +
    `<span class="kpi-v">${fmtUsdWhole(summary.market_value)}</span>`;
  return card;
}

function renderStockPlKpi(
  summary: StockOpenSummary,
  tip = "Unrealized profit/loss on your open shares: market value − cost basis (priced shares only).",
): HTMLElement {
  const card = document.createElement("div");
  card.id = STOCK_PL_KPI_ID;
  card.className = "kpi";
  card.setAttribute("data-sfe-injected", "true");
  const cls = summary.open_pl < 0 ? "roi-loss" : "roi-strong";
  const pct = fmtPct(summary.open_pl_pct);
  card.innerHTML =
    `<span class="kpi-k">Total P/L` +
    `<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(tip)}">i</span></span>` +
    `<span class="kpi-v ${cls}">${fmtUsdWhole(summary.open_pl)}</span>` +
    (pct ? `<span class="kpi-sub ${cls}">${pct}</span>` : "");
  return card;
}

/** Robinhood margin used from the latest daily sample, when history is loaded. */
function rhCurrentMarginUsed(monthKey?: string | null): number | null {
  const key = monthKey ?? currentMonthKey();
  const summary = cachedMarginCostSummary(key);
  if (!summary?.days.length) return null;
  const used = summary.currentMarginUsed;
  return Number.isFinite(used) ? used : null;
}

function rhCurrentAnnualRatePct(monthKey?: string | null): number | null {
  const key = monthKey ?? currentMonthKey();
  const summary = cachedMarginCostSummary(key);
  if (!summary) return null;
  const rate = summary.currentAnnualRatePct;
  return rate != null && Number.isFinite(rate) ? rate : null;
}

function rhCurrentDailyInterest(monthKey?: string | null): number | null {
  const key = monthKey ?? currentMonthKey();
  const summary = cachedMarginCostSummary(key);
  if (!summary?.days.length) return null;
  const daily = summary.currentDailyInterest;
  return Number.isFinite(daily) ? daily : null;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function marginCostHistoryModalHtml(summary: MarginCostMonthSummary): string {
  const rows = summary.days.length
    ? summary.days
        .map(
          (day) => `
      <tr>
        <td>${escapeHtml(day.date)}${day.filled ? ' <span class="muted">est.</span>' : ""}</td>
        <td class="num">${escapeHtml(fmtUsd(day.marginUsed))}</td>
        <td class="num">${escapeHtml(fmtUsd(day.dailyInterest))}</td>
        <td class="num">${escapeHtml(fmtUsd(day.total))}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="4" class="muted">No Robinhood margin samples recorded this month yet. Refresh the Robinhood snapshot to start tracking.</td></tr>`;

  return `
    <p class="muted small sfe-margin-cost-history-summary">
      Accrued ${escapeHtml(fmtUsd(summary.accrued))} through day ${summary.daysElapsed}
      · predicted ${escapeHtml(fmtUsd(summary.predictedMonthCost))}/mo
      (${escapeHtml(fmtUsd(summary.currentDailyInterest))}/day × ${summary.daysRemaining} days left)
    </p>
    <div class="sfe-margin-cost-history-scroll">
      <table class="sfe-margin-cost-history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th class="num">Margin used</th>
            <th class="num">Interest</th>
            <th class="num">Total</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

async function openMarginCostHistoryModal(monthKey?: string): Promise<void> {
  const key = monthKey ?? selectedIncomeMonthKey ?? currentMonthKey();
  const summary =
    cachedMarginCostSummary(key) ?? (await fetchMarginCostMonthSummary(key));
  if (summary) storeMarginCostSummary(summary);
  const bodyHtml = summary
    ? marginCostHistoryModalHtml(summary)
    : `<p class="muted">Could not load margin cost history.</p>`;
  const label = monthLabel(key);
  await contentDialog({
    title: `Margin cost · ${label}`,
    message:
      "Daily Robinhood margin used and interest from snapshot updates. Total is the running month cost; remaining days are projected at the latest daily rate.",
    bodyHtml,
    closeLabel: "Close",
    modalClass: "sfe-margin-cost-history-modal",
  });
}

function bindMarginUsedHistoryButton(card: HTMLElement): void {
  const btn = card.querySelector<HTMLButtonElement>("[data-action='margin-cost-history']");
  if (!btn || btn.dataset.bound === "1") return;
  btn.dataset.bound = "1";
  btn.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    const wrap = btn.closest<HTMLElement>(".sfe-income-month-detail");
    void openMarginCostHistoryModal(wrap?.dataset.month);
  });
}

function marginCostHistoryBtnHtml(): string {
  return (
    `<button type="button" class="sfe-margin-cost-history-btn" data-action="margin-cost-history" title="Margin cost history this month" aria-label="Margin cost history this month">` +
    `<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M2 2.5h12a.5.5 0 0 1 .5.5v10a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5zm1 2v1.5h10V4.5H3zm0 3V9h6V7.5H3zm0 3v1.5h8V10.5H3z"/></svg>` +
    `</button>`
  );
}

function renderCashFlowKpi(
  cashAdded: number,
  withdrawn: number,
  throughLabel: string,
): HTMLElement {
  const card = document.createElement("div");
  card.id = CASH_ADDED_KPI_ID;
  card.className = "kpi sfe-cash-flow-kpi";
  card.setAttribute("data-sfe-injected", "true");
  card.innerHTML =
    `<span class="kpi-k">Cash` +
    `<span class="hint" tabindex="0" role="img" data-tip="">i</span></span>` +
    `<span class="sfe-kpi-period sfe-cash-period">through ${throughLabel}</span>` +
    `<span class="sfe-cash-added-label">Total Added</span>` +
    `<span class="kpi-v sfe-cash-added-total">${fmtUsdWhole(cashAdded)}</span>` +
    `<span class="sfe-cash-withdrawn-line">` +
    `<span class="sfe-cash-withdrawn-label">Total Withdrawals</span>` +
    `<span class="sfe-cash-withdrawn-value">${withdrawn ? `-${fmtUsdWhole(withdrawn)}` : fmtUsdWhole(0)}</span>` +
    `</span>`;
  return card;
}

function renderCostCardHeader(total: number, tip: string, throughLabel: string): string {
  return (
    `<span class="kpi-k">Total Cost<span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span></span>` +
    `<span class="sfe-kpi-period sfe-cost-period">through ${throughLabel}</span>` +
    `<span class="kpi-v sfe-cost-total-val">${fmtUsd(total)}</span>`
  );
}

/** Month-detail Cost card header (total = margin interest + per-trade fees). */
function renderMonthCostCardHeader(expensesTotal: number): string {
  const tip = dataTipParagraphs(
    "Per-trade fees plus margin financing for this month. Margin used is from Robinhood (cash debit) when snapshot history is available.",
    "The current month and later forecast months use a full-month cost at this month's latest daily margin rate.",
    "Own Capital deducts Cost once.",
  );
  return (
    `<div class="sfe-expenses-card-header sfe-month-margin-card-header">` +
    `<span class="kpi-k">Cost` +
    `<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(tip)}">i</span></span>` +
    `<span class="kpi-v sfe-expense-total-val">${fmtUsd(expensesTotal)}</span>` +
    `</div>`
  );
}

function renderMonthMarginDetailsBlock(
  fallbackMarginUsed: number,
  monthKey?: string,
  useRhHistory = true,
): string {
  const key = monthKey ?? currentMonthKey();
  const rateKey = isPastAnalyticsMonthKey(key) ? key : currentMonthKey();
  const rhUsed = useRhHistory ? rhCurrentMarginUsed(rateKey) : null;
  const marginUsed = rhUsed ?? (useRhHistory ? fallbackMarginUsed : Math.max(0, fallbackMarginUsed));
  const cls = marginUsed > 0 ? "sfe-neg" : marginUsed < 0 ? "roi-strong" : "";
  const daily = useRhHistory ? rhCurrentDailyInterest(rateKey) : null;
  const ratePct = useRhHistory ? rhCurrentAnnualRatePct(rateKey) : null;
  const dailyForecast = isPastAnalyticsMonthKey(key) ? null : forecastMarginCostFromCurrentDaily();
  const forecastMo =
    dailyForecast != null
      ? dailyForecast
      : useRhHistory
        ? rhPredictedMarginCostForMonth(key) ?? predictedMarginCostAtApr(Math.max(0, marginUsed))
        : predictedMarginCostAtApr(Math.max(0, marginUsed));

  const interestSubline =
    daily != null
      ? `${fmtUsd(daily)}/day${ratePct != null ? ` @ ${ratePct.toFixed(2)}%` : ""}`
      : ratePct != null
        ? `${ratePct.toFixed(2)}%`
        : null;

  return (
    `<div class="sfe-month-roc-line sfe-month-margin-line">` +
    `<span class="sfe-month-roc-line-k">Margin</span>` +
    `<div class="sfe-month-margin-details">` +
    `<div class="sfe-month-margin-detail-row">` +
    `<span class="sfe-month-roc-line-k">Used</span>` +
    `<span class="kpi-v ${cls}">${fmtUsdWhole(marginUsed)}</span>` +
    `</div>` +
    `<div class="sfe-month-margin-forecast-group">` +
    `<div class="sfe-month-margin-detail-row">` +
    `<span class="sfe-month-roc-line-k">Forecast</span>` +
    `<span class="kpi-v sfe-margin-pred-cost">${fmtUsd(forecastMo)}</span>` +
    `</div>` +
    (interestSubline
      ? `<div class="sfe-month-margin-rh-rate">${escapeHtml(interestSubline)}</div>`
      : "") +
    `</div>` +
    `</div>` +
    `</div>`
  );
}

function renderExpensesKpi(
  totals: ReturnType<typeof aggregateExpenses>,
  throughLabel: string,
): HTMLElement {
  const card = document.createElement("div");
  card.id = EXPENSES_KPI_ID;
  card.className = "kpi sfe-cost-kpi";
  card.setAttribute("data-sfe-injected", "true");
  const tip =
    "Margin interest plus per-trade fees, deducted from Own Capital at month-end.";
  card.innerHTML =
    renderCostCardHeader(totals.total, tip, throughLabel) +
    `<div class="sfe-cost-breakdown">
      <div class="sfe-cost-line">
        <span class="sfe-cost-line-k">tot. margin</span>
        <span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(totals.margin)}</span>
      </div>
      <div class="sfe-cost-line">
        <span class="sfe-cost-line-k">tot. per trade</span>
        <span class="sfe-cost-line-v-group">
          <span class="kpi-v sfe-cost-line-v">${fmtUsd(totals.perTrade)}</span>
          <span class="sfe-cost-per-trade-meta">×${totals.tradeCount} trades</span>
        </span>
      </div>
    </div>`;
  return card;
}

function updateExpensesKpi(
  card: HTMLElement,
  totals: ReturnType<typeof aggregateExpenses>,
  throughLabel: string,
): void {
  let periodEl = card.querySelector<HTMLElement>(".sfe-cost-period");
  if (!periodEl) {
    periodEl = document.createElement("span");
    periodEl.className = "sfe-kpi-period sfe-cost-period";
    card.querySelector(":scope > .kpi-k")?.insertAdjacentElement("afterend", periodEl);
  }
  periodEl.textContent = `through ${throughLabel}`;
  const marginEl = card.querySelector<HTMLElement>(".sfe-cost-line:nth-child(1) .sfe-cost-line-v");
  const perTradeEl = card.querySelector<HTMLElement>(".sfe-cost-line:nth-child(2) .sfe-cost-line-v");
  const perTradeMetaEl = card.querySelector<HTMLElement>(".sfe-cost-line:nth-child(2) .sfe-cost-per-trade-meta");
  const totalEl = card.querySelector<HTMLElement>(".sfe-cost-total-val");
  if (marginEl) marginEl.textContent = fmtUsdWhole(totals.margin);
  if (perTradeEl) perTradeEl.textContent = fmtUsd(totals.perTrade);
  if (perTradeMetaEl) perTradeMetaEl.textContent = `×${totals.tradeCount} trades`;
  if (totalEl) totalEl.textContent = fmtUsd(totals.total);
}

interface AnalyticsTaxPerformanceSummary {
  monthTaxOwed: number;
  monthTaxPaid: number;
  monthW2TaxPaid: number;
  monthEstimatedTaxPaid: number;
  monthRemainingTax: number;
  totalTaxOwed: number;
  w2TaxPaid: number;
  estimatedTaxPaid: number;
  remainingTax: number;
}

function renderMonthTaxesCardContent(
  summary: AnalyticsTaxPerformanceSummary | null,
  monthLabelText: string,
): string {
  const tip =
    `Federal tax for ${monthLabelText}, using the same monthly Tax-tab calculation. The headline is this month’s tax owed minus W-2 withholding and estimated payments.`;
  const manageBtn = taxEstPaymentsEditBtn(`Manage tax payments for ${monthLabelText}`);
  const header =
    `<div class="sfe-month-taxes-head">` +
    `<span class="kpi-k sfe-month-taxes-head-label">Tax Due` +
    `<span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span></span>` +
    `<span class="sfe-month-taxes-this-month-mark">this month</span>` +
    `</div>`;
  if (!summary) {
    return (
      header +
      `<span class="kpi-v sfe-month-taxes-balance">—</span>` +
      `<div class="sfe-cost-breakdown"><span class="sfe-cost-line-k">Loading Tax tab…</span></div>`
    );
  }
  const balanceCls = summary.monthRemainingTax > 0 ? "sfe-neg" : "roi-strong";
  return (
    header +
    `<span class="kpi-v sfe-month-taxes-balance ${balanceCls}">${fmtUsdWhole(summary.monthRemainingTax)}</span>` +
    `<div class="sfe-cost-breakdown">` +
    `<div class="sfe-cost-line"><span class="sfe-cost-line-k">tax owed</span><span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(summary.monthTaxOwed)}</span></div>` +
    `<div class="sfe-cost-line"><span class="sfe-cost-line-k">W-2 paid</span><span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(summary.monthW2TaxPaid)}</span></div>` +
    `<div class="sfe-cost-line sfe-month-taxes-est-line">` +
    `<span class="sfe-cost-line-k sfe-month-taxes-est-label">est. paid${manageBtn}</span>` +
    `<span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(summary.monthEstimatedTaxPaid)}</span>` +
    `</div>` +
    `</div>`
  );
}

function applyEstimatedTaxToMonthIncomeCard(
  _detail: HTMLElement,
  _estimatedTaxPaid: number,
): void {
  /* Income cards no longer show or subtract estimated tax. Tax Due still does. */
}

async function syncMonthTaxesCard(detail: HTMLElement, monthKey: string): Promise<void> {
  const card = detail.querySelector<HTMLElement>("[data-sfe-month-taxes]");
  if (!card) return;
  const [year, month] = monthKey.split("-").map(Number);
  const label = monthLabel(monthKey);
  try {
    const taxModule = (await import("./tax")) as unknown as {
      loadTaxPerformanceSummary: (
        taxYear: number,
        throughMonthIndex: number,
      ) => Promise<AnalyticsTaxPerformanceSummary | null>;
      openEstimatedTaxPaymentsModal: (taxYear: number, monthIndex: number) => void;
    };
    const summary = await taxModule.loadTaxPerformanceSummary(year, month - 1);
    if (detail.dataset.month !== monthKey || !detail.contains(card)) return;
    card.innerHTML = renderMonthTaxesCardContent(summary, label);
    applyEstimatedTaxToMonthIncomeCard(detail, summary?.monthEstimatedTaxPaid ?? 0);
    card.querySelector("[data-sfe-open-est-payments]")?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      taxModule.openEstimatedTaxPaymentsModal(year, month - 1);
    });
  } catch {
    if (detail.dataset.month !== monthKey || !detail.contains(card)) return;
    card.innerHTML =
      `<div class="sfe-month-taxes-head">` +
      `<span class="kpi-k sfe-month-taxes-head-label">Tax Due</span>` +
      `<span class="sfe-month-taxes-this-month-mark">this month</span>` +
      `</div>` +
      `</div>` +
      `<span class="kpi-v">—</span>` +
      `<div class="sfe-cost-breakdown"><span class="sfe-cost-line-k">Tax estimate unavailable</span></div>`;
    applyEstimatedTaxToMonthIncomeCard(detail, 0);
  }
}

function renderTaxesKpiContent(
  summary: AnalyticsTaxPerformanceSummary | null,
  scopeLabel: string,
): string {
  const tip =
    `Cumulative federal tax estimate through ${scopeLabel}, using the same brackets, W-2 withholding, estimated payments, and FIFO trading income as the Tax tab. The headline is tax owed minus tax paid.`;
  const periodHtml = `<span class="sfe-kpi-period sfe-taxes-period">through ${scopeLabel}</span>`;
  if (!summary) {
    return (
      `<span class="kpi-k">Tax Due<span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span></span>` +
      periodHtml +
      `<span class="kpi-v sfe-taxes-balance">—</span>` +
      `<div class="sfe-cost-breakdown"><span class="sfe-cost-line-k">Loading Tax tab…</span></div>`
    );
  }

  const balanceCls = summary.remainingTax > 0 ? "sfe-neg" : "roi-strong";
  const estPaymentsBtn = taxEstPaymentsEditBtn(`Manage tax payments through ${scopeLabel}`);
  return (
    `<span class="kpi-k">Tax Due<span class="hint" tabindex="0" role="img" data-tip="${tip}">i</span></span>` +
    periodHtml +
    `<span class="kpi-v sfe-taxes-balance ${balanceCls}">${fmtUsdWhole(summary.remainingTax)}</span>` +
    `<div class="sfe-cost-breakdown">` +
    `<div class="sfe-cost-line"><span class="sfe-cost-line-k">tax owed</span><span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(summary.totalTaxOwed)}</span></div>` +
    `<div class="sfe-cost-line"><span class="sfe-cost-line-k">W-2 paid</span><span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(summary.w2TaxPaid)}</span></div>` +
    `<div class="sfe-cost-line sfe-taxes-est-line">` +
    `<span class="sfe-cost-line-k sfe-taxes-est-label">est. paid${estPaymentsBtn}</span>` +
    `<span class="kpi-v sfe-cost-line-v">${fmtUsdWhole(summary.estimatedTaxPaid)}</span>` +
    `</div>` +
    `</div>`
  );
}

function renderTaxesKpi(scopeLabel: string): HTMLElement {
  const card = document.createElement("div");
  card.id = TAXES_KPI_ID;
  card.className = "kpi sfe-cost-kpi sfe-taxes-kpi";
  card.setAttribute("data-sfe-injected", "true");
  card.innerHTML = renderTaxesKpiContent(null, scopeLabel);
  return card;
}

let taxesKpiRequestId = 0;

async function syncTaxesKpi(host: HTMLElement, monthKey: string): Promise<void> {
  const [yearRaw, monthRaw] = monthKey.split("-").map(Number);
  const year = Number.isFinite(yearRaw) ? yearRaw : currentCalendarYear();
  const monthIndex = Number.isFinite(monthRaw) ? Math.max(0, Math.min(11, monthRaw - 1)) : 0;
  const normalizedMonthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  const scopeLabel = monthLabel(normalizedMonthKey);
  const requestId = ++taxesKpiRequestId;

  const card = upsertKpiCard(
    host,
    TAXES_KPI_ID,
    () => renderTaxesKpi(scopeLabel),
    (existing) => {
      existing.classList.add("sfe-cost-kpi", "sfe-taxes-kpi");
      existing.innerHTML = renderTaxesKpiContent(null, scopeLabel);
    },
  );
  card.dataset.taxScope = normalizedMonthKey;

  try {
    const taxModule = (await import("./tax")) as unknown as {
      loadTaxPerformanceSummary: (
        taxYear: number,
        throughMonthIndex: number,
      ) => Promise<AnalyticsTaxPerformanceSummary | null>;
    };
    const summary = await taxModule.loadTaxPerformanceSummary(year, monthIndex);
    if (
      requestId !== taxesKpiRequestId ||
      !card.isConnected ||
      card.dataset.taxScope !== normalizedMonthKey
    ) {
      return;
    }
    card.innerHTML = renderTaxesKpiContent(summary, scopeLabel);
    applyEstimatedTaxToTotalIncomeKpi(summary?.estimatedTaxPaid ?? 0);
    card.querySelector("[data-sfe-open-est-payments]")?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      void (async () => {
        const taxModule = (await import("./tax")) as unknown as {
          openEstimatedTaxPaymentsModal: (taxYear: number, monthIndex: number) => void;
        };
        taxModule.openEstimatedTaxPaymentsModal(year, monthIndex);
      })();
    });
  } catch {
    if (
      requestId !== taxesKpiRequestId ||
      !card.isConnected ||
      card.dataset.taxScope !== normalizedMonthKey
    ) {
      return;
    }
    card.innerHTML =
      `<span class="kpi-k">Tax Due</span>` +
      `<span class="sfe-kpi-period sfe-taxes-period">through ${scopeLabel}</span>` +
      `<span class="kpi-v">—</span>` +
      `<div class="sfe-cost-breakdown"><span class="sfe-cost-line-k">Tax estimate unavailable</span></div>`;
    applyEstimatedTaxToTotalIncomeKpi(0);
  }
}

function applyEstimatedTaxToTotalIncomeKpi(_estimatedTaxPaid: number): void {
  /* Income cards no longer show or subtract estimated tax. Tax Due still does. */
}

function renderOwnCapitalKpi(total: number, throughLabel: string): HTMLElement {
  const card = document.createElement("div");
  card.id = OWN_CAPITAL_KPI_ID;
  card.className = "kpi";
  card.setAttribute("data-sfe-injected", "true");
  const cls = total >= 0 ? "roi-strong" : "roi-loss";
  card.innerHTML =
    `<span class="kpi-k">Total Own Capital` +
    `<span class="hint" tabindex="0" role="img" data-tip="">i</span></span>` +
    `<span class="sfe-kpi-period sfe-own-capital-period">through ${throughLabel} (EOM)</span>` +
    `<span class="kpi-v ${cls}">${fmtUsdWhole(total)}</span>`;
  return card;
}

function updateForecastIncomeKpi(
  card: HTMLElement,
  income: number,
  throughLabel: string,
  cumulativeMarginCost: number,
): void {
  card.dataset.grossIncome = String(income);
  card.dataset.marginCost = String(cumulativeMarginCost);
  const valueEl = card.querySelector(".kpi-v");
  if (valueEl) valueEl.textContent = fmtUsdWhole(income);
  const row = card.querySelector(".sfe-income-kpi-row");
  if (!(row instanceof HTMLElement)) return;
  row.classList.add("sfe-income-kpi-row-forecast");

  let detailsHost = row.querySelector(".sfe-income-kpi-details");
  if (!detailsHost) {
    detailsHost = document.createElement("div");
    detailsHost.className = "sfe-income-kpi-details sfe-forecast-income-kpi-details";
    row.appendChild(detailsHost);
  }
  detailsHost.innerHTML = `
    <span class="sfe-income-kpi-detail sfe-forecast-income-detail">Through ${throughLabel} incl. forecast</span>
    <span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">Tot. margin cost <b>-${fmtUsdWhole(cumulativeMarginCost)}</b></span>
  `;
}

function clearForecastIncomeKpiLayout(card: HTMLElement): void {
  card.querySelector(".sfe-income-kpi-row")?.classList.remove("sfe-income-kpi-row-forecast");
}

function applyForecastKpiTone(card: HTMLElement, on: boolean): void {
  card.classList.toggle("sfe-forecast-tone", on);
}

function upsertKpiCard(
  host: Element,
  id: string,
  render: () => HTMLElement,
  update: (card: HTMLElement) => void,
): HTMLElement {
  const existing = document.getElementById(id);
  if (!existing) {
    const card = render();
    host.appendChild(card);
    return card;
  }
  if (existing.parentElement !== host) host.appendChild(existing);
  update(existing);
  return existing;
}

/** Collapsible wrapper for cumulative Total Income / capital / stock KPI cards. */
function ensureTotalPerformanceSection(
  body: HTMLElement,
  yearRow: HTMLElement,
): { kpiHost: HTMLElement; stockKpiHost: HTMLElement } {
  let section = body.querySelector<HTMLDetailsElement>(TOTAL_PERFORMANCE_SECTION_SEL);
  if (!section) {
    section = document.createElement("details");
    section.className = "collapsible sfe-total-performance";
    section.dataset.sfeCollapsible = ANALYTICS_TOTAL_PERFORMANCE_SECTION_ID;
    section.dataset.sfeCollapsibleDefault = "open";
    section.open = isCollapsibleOpen(
      ANALYTICS_COLLAPSIBLE_SCOPE,
      ANALYTICS_TOTAL_PERFORMANCE_SECTION_ID,
      true,
    );
    section.innerHTML = `
      <summary class="sfe-total-performance-summary">
        <h2 class="insight-h">Total Performance</h2>
        <span class="hint" tabindex="0" role="img" data-tip="${TOTAL_PERFORMANCE_TIP}">i</span>
      </summary>
      <div class="sfe-total-performance-body"></div>
    `;
    section
      .querySelector(".hint")
      ?.addEventListener("click", (event) => event.stopPropagation());
  } else if (!section.classList.contains("collapsible")) {
    section.classList.add("collapsible");
  }

  section.dataset.sfeCollapsible = ANALYTICS_TOTAL_PERFORMANCE_SECTION_ID;
  section.dataset.sfeCollapsibleDefault = "open";
  section.open = isCollapsibleOpen(
    ANALYTICS_COLLAPSIBLE_SCOPE,
    ANALYTICS_TOTAL_PERFORMANCE_SECTION_ID,
    true,
  );
  if (section.dataset.sfeCollapsibleBound !== "1") {
    section.dataset.sfeCollapsibleBound = "1";
    section.addEventListener("toggle", () => {
      setCollapsibleOpen(
        ANALYTICS_COLLAPSIBLE_SCOPE,
        ANALYTICS_TOTAL_PERFORMANCE_SECTION_ID,
        section.open,
      );
    });
  }

  if (section.previousElementSibling !== yearRow) {
    yearRow.insertAdjacentElement("afterend", section);
  }

  const sectionBody = section.querySelector(".sfe-total-performance-body");
  if (!(sectionBody instanceof HTMLElement)) {
    throw new Error("Total Performance body missing");
  }

  // Migrate KPI rows that lived outside the collapsible before this change.
  for (const sel of [".sfe-inject-kpi-row", ".sfe-stock-kpi-row"] as const) {
    const orphan = body.querySelector(sel);
    if (orphan instanceof HTMLElement && !sectionBody.contains(orphan)) {
      sectionBody.appendChild(orphan);
    }
  }

  let kpiHost = sectionBody.querySelector(".sfe-inject-kpi-row");
  if (!(kpiHost instanceof HTMLElement)) {
    kpiHost = document.createElement("div");
    kpiHost.className = "sfe-inject-kpi-row kpi-row";
    sectionBody.prepend(kpiHost);
  }

  let stockKpiHost = sectionBody.querySelector(".sfe-stock-kpi-row");
  if (!(stockKpiHost instanceof HTMLElement)) {
    stockKpiHost = document.createElement("div");
    stockKpiHost.className = "sfe-stock-kpi-row kpi-row";
    sectionBody.appendChild(stockKpiHost);
  } else if (stockKpiHost.previousElementSibling !== kpiHost) {
    kpiHost.insertAdjacentElement("afterend", stockKpiHost);
  }

  return { kpiHost, stockKpiHost };
}

function chartMonthShortLabel(monthKey: string, multiYear: boolean): string {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  const mon = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return multiYear ? `${mon} '${String(year).slice(2)}` : mon;
}

function currentShareAvgByBookTicker(
  txns: WheelTransaction[],
): Map<string, { qty: number; avg: number }> {
  const { snapshots } = replayShareAvgCost(txns);
  const avgs = new Map<string, { qty: number; avg: number }>();
  for (const txn of sortForAvgCostReplay(txns)) {
    if (txn.id == null || !txn.ticker) continue;
    const snap = snapshots.get(txn.id);
    if (!snap) continue;
    avgs.set(shareAvgPositionKey(txn), {
      qty: Math.round(snap.sharesAfter),
      avg: snap.avgAfter,
    });
  }
  return avgs;
}

function openShortCallContracts(
  txns: WheelTransaction[],
): Array<{ ticker: string; strike: number; exp: string; contracts: number; accountId: string }> {
  const open = new Map<string, number>();
  const meta = new Map<
    string,
    { ticker: string; strike: number; exp: string; accountId: string }
  >();
  for (const txn of txns) {
    if (txn.instrument_type !== "option") continue;
    const side = String(txn.side ?? "").toLowerCase();
    if (!side.startsWith("c")) continue;
    const key = contractKey(txn);
    if (!key) continue;
    const qty = txnContracts(txn);
    if (qty <= 0) continue;
    const code = txnCode(txn);
    const prev = open.get(key) ?? 0;
    if (
      txn.close_reason === "assigned" ||
      txn.close_reason === "expired" ||
      code === "OEXP" ||
      code === "BTC"
    ) {
      open.set(key, Math.max(0, prev - qty));
    } else if (code === "STO" || (txn.action === "sell" && code !== "STC")) {
      open.set(key, prev + qty);
      meta.set(key, {
        ticker: txn.ticker ?? "",
        strike: Number(txn.strike) || 0,
        exp: String(txn.exp_date ?? "").slice(0, 10),
        accountId: shareAvgBookKey(txn),
      });
    }
  }
  const calls: Array<{
    ticker: string;
    strike: number;
    exp: string;
    contracts: number;
    accountId: string;
  }> = [];
  for (const [key, contracts] of open) {
    if (contracts <= 0) continue;
    const info = meta.get(key);
    if (!info?.ticker || !info.exp || !(info.strike > 0)) continue;
    calls.push({ ...info, contracts });
  }
  calls.sort((a, b) => a.exp.localeCompare(b.exp) || a.ticker.localeCompare(b.ticker));
  return calls;
}

/** If open short calls expiring in a month are assigned, P/L is (strike − avg) × shares.
 * Negative when the strike is below average cost. Shares are only used once. */
function forecastOpenCallAssignByMonth(txns: WheelTransaction[]): Map<string, number> {
  const totals = new Map<string, number>();
  if (!txns.length) return totals;
  const remaining = currentShareAvgByBookTicker(txns);
  for (const call of openShortCallContracts(txns)) {
    const month = call.exp.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) continue;
    const held = remaining.get(`${call.accountId}|${call.ticker}`);
    const shares = Math.round(call.contracts * 100);
    const onHand = held?.qty ?? 0;
    const avg = held?.avg ?? 0;
    const used = onHand > 0 ? Math.min(shares, onHand) : 0;
    const income = callAssignIncomeAtAvg(call.strike, shares, avg, used);
    if (held) held.qty = Math.max(0, onHand - used);
    if (!income) continue;
    totals.set(month, (totals.get(month) ?? 0) + income);
  }
  return totals;
}

function buildIncomeChartPoints(
  months: IncomeMonthBucket[],
  selectedYear: number | "all",
  forecastEnabled: boolean,
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  ledger: Record<string, OwnCapitalEntry>,
  stockCostBasis: number,
  localLedgerAnalytics: boolean,
): IncomeChartMonthPoint[] {
  const displayMonths = withForecastMonths(months, selectedYear, forecastEnabled);
  const chronological = [...displayMonths].sort((a, b) => a.month.localeCompare(b.month));
  if (!chronological.length) return [];

  const newest = chronological[chronological.length - 1]!.month;
  const openMonth = currentMonthKey();
  const timeline = chronological.some((month) => month.month >= openMonth)
    ? projectPerformanceChartTimeline(
        ownCapitalByMonth,
        ledger,
        newest < openMonth ? openMonth : newest,
        localLedgerAnalytics ? undefined : predictedTaxWithdrawalsCache,
        localLedgerAnalytics,
      )
    : ownCapitalByMonth;
  const firstYear = chronological[0]!.month.slice(0, 4);
  const multiYear = chronological.some((month) => month.month.slice(0, 4) !== firstYear);
  const openCallAssignByMonth = forecastOpenCallAssignByMonth(lastIncomeTxnsForTrace);
  let lastClosedSpx: number | null = null;
  for (const month of chronological) {
    if (month.month >= openMonth) break;
    const value = spxMonthlyReturn(month.month);
    if (value != null) lastClosedSpx = value;
  }

  return chronological.map((month) => {
    const forecast = month.month >= openMonth;
    const point = timeline.get(month.month);
    const expenseEntryRaw = ledgerEntryForMonth(month.month, ledger, timeline);
    const expenseEntry = localLedgerAnalytics
      ? {
          ...expenseEntryRaw,
          perTradeFee: 0,
        }
      : expenseEntryRaw;
    const ownCapitalValue = point?.ownCapital ?? 0;
    const margin = forecast || localLedgerAnalytics
      ? Number(point?.effectiveMarginCost ?? expenseEntry.marginCost) || 0
      : displayEffectiveMarginCost(
          expenseEntry,
          ownCapitalValue,
          stockCostBasis,
          point?.effectiveMarginCost,
          month.month,
        );
    const prevKey = prevMonthKey(month.month);
    const prevOwnCapital = !forecast
      ? timeline.get(prevKey)?.ownCapital ?? 0
      : prevKey < openMonth
        ? realOwnCapitalEom(prevKey, ownCapitalByMonth)
        : timeline.get(prevKey)?.ownCapital ?? 0;
    const targetIncome =
      prevOwnCapital > 0 ? (readTargetRocPct(month.month, ledger) / 100) * prevOwnCapital : 0;
    const income = forecast ? Number(point?.income) || targetIncome : cashIncome(month);
    const netIncome = forecast ? income : income - margin;
    const rocPct = prevOwnCapital > 0 ? (netIncome / prevOwnCapital) * 100 : null;
    return {
      month: month.month,
      label: month.label,
      shortLabel: chartMonthShortLabel(month.month, multiYear),
      premiums: forecast ? targetIncome + Math.abs(margin) : month.premiums,
      assignIncome:
        month.assignIncome + (forecast ? openCallAssignByMonth.get(month.month) ?? 0 : 0),
      btcCost: forecast ? 0 : Math.abs(month.btcCost),
      marginCost: Math.abs(margin),
      income,
      ownCapital: ownCapitalValue > 0 ? ownCapitalValue : forecast && prevOwnCapital > 0
        ? plannedOwnCapitalEom({
            baseCapitalBom: prevOwnCapital,
            targetIncomeNet: income,
            cashAdded: expenseEntry.cashAdded,
            withdrawn: Number(expenseEntry.withdrawn) || 0,
            stockLendingIncome: localLedgerAnalytics
              ? Number(expenseEntry.stockLendingIncome) || 0
              : 0,
            perTradeFees: localLedgerAnalytics ? 0 : perTradeExpenseTotal(expenseEntry, 0),
            taxCashOutflow: localLedgerAnalytics ? 0 : taxCashOutflowForMonthKey(month.month),
          })
        : ownCapitalValue,
      rocPct,
      spxPct: forecast ? lastClosedSpx : spxMonthlyReturn(month.month),
      forecast,
    };
  });
}

function syncTotalPerformanceIncomeChart(
  sectionBody: HTMLElement,
  stockKpiHost: HTMLElement,
  points: IncomeChartMonthPoint[],
  selectedMonth: string | null,
  onSelectMonth: ((monthKey: string) => void) | null,
): void {
  let section = sectionBody.querySelector<HTMLDetailsElement>(".sfe-total-performance-chart");
  if (!section) {
    section = document.createElement("details");
    section.className = "sfe-total-performance-chart";
    section.dataset.sfeCollapsible = ANALYTICS_INCOME_CHART_SECTION_ID;
    section.dataset.sfeCollapsibleDefault = "open";
    section.innerHTML =
      `<summary class="sfe-total-performance-chart-summary">` +
      `<span class="sfe-total-performance-chart-title">Performance Graph</span>` +
      `<span class="hint" tabindex="0" role="img" data-tip="${incomeChartSectionTip()}">i</span>` +
      `</summary>` +
      `<div class="sfe-total-performance-chart-body"></div>`;
    section
      .querySelector(".hint")
      ?.addEventListener("click", (event) => event.stopPropagation());
  }

  const titleEl = section.querySelector(".sfe-total-performance-chart-title");
  if (titleEl) titleEl.textContent = "Performance Graph";
  section.dataset.sfeCollapsible = ANALYTICS_INCOME_CHART_SECTION_ID;
  section.dataset.sfeCollapsibleDefault = "open";
  section.open = isCollapsibleOpen(
    ANALYTICS_COLLAPSIBLE_SCOPE,
    ANALYTICS_INCOME_CHART_SECTION_ID,
    true,
  );
  if (section.dataset.sfeCollapsibleBound !== "1") {
    section.dataset.sfeCollapsibleBound = "1";
    section.addEventListener("toggle", () => {
      setCollapsibleOpen(
        ANALYTICS_COLLAPSIBLE_SCOPE,
        ANALYTICS_INCOME_CHART_SECTION_ID,
        section.open,
      );
    });
  }

  if (section.parentElement !== sectionBody || section.previousElementSibling !== stockKpiHost) {
    stockKpiHost.insertAdjacentElement("afterend", section);
  }

  const chartBody = section.querySelector<HTMLElement>(".sfe-total-performance-chart-body");
  if (!chartBody) return;
  mountIncomeByMonthChart(chartBody, points, {
    selectedMonth,
    onSelectMonth,
  });
}

function renderScopeBadgeHtml(
  badge: AnalyticsPerformanceScopeBadge,
  hostClass: string,
): string {
  if (badge === "Fact + Forecast") {
    return (
      `<span class="${hostClass}" aria-label="Fact + Forecast">` +
      `<span class="sfe-total-performance-scope-badge sfe-total-performance-scope-badge-fact">Fact</span>` +
      `<span class="sfe-total-performance-scope-badge sfe-total-performance-scope-badge-forecast">Forecast</span>` +
      `</span>`
    );
  }
  const tone = badge === "Forecast" ? "forecast" : "fact";
  return (
    `<span class="${hostClass}" aria-label="${badge}">` +
    `<span class="sfe-total-performance-scope-badge sfe-total-performance-scope-badge-${tone}">${badge}</span>` +
    `</span>`
  );
}

function syncTotalPerformanceScopeBadge(
  body: HTMLElement,
  badge: AnalyticsPerformanceScopeBadge,
): void {
  const summary = body.querySelector<HTMLElement>(
    `${TOTAL_PERFORMANCE_SECTION_SEL} > .sfe-total-performance-summary`,
  );
  if (!summary) return;

  let host = summary.querySelector<HTMLElement>(".sfe-total-performance-scope-badges");
  if (!host) {
    host = document.createElement("span");
    host.className = "sfe-total-performance-scope-badges";
    summary.querySelector(".hint")?.insertAdjacentElement("beforebegin", host);
  }

  host.outerHTML = renderScopeBadgeHtml(badge, "sfe-total-performance-scope-badges");
}

function syncMonthDetailScopeBadge(detail: HTMLElement, badge: AnalyticsPerformanceScopeBadge): void {
  let head = detail.querySelector<HTMLElement>(".sfe-income-month-detail-head");
  if (!head) {
    head = document.createElement("div");
    head.className = "sfe-income-month-detail-head";
    detail.prepend(head);
  }
  head.innerHTML = renderScopeBadgeHtml(badge, "sfe-total-performance-scope-badges");
}

/** Future-year note: prior-year tax balance assumed paid Mar 30 (Cash Ops withdrawal). */
function totalPerformancePriorYearTaxForecastNote(
  selectedYear: number | "all",
): string | null {
  if (selectedYear === "all" || selectedYear <= currentCalendarYear()) return null;
  const priorYear = selectedYear - 1;
  return `All due ${priorYear} year taxes forecasted to be paid on Mar 30`;
}

function syncTotalPerformanceTaxForecastNote(
  body: HTMLElement,
  selectedYear: number | "all",
): void {
  const summary = body.querySelector<HTMLElement>(
    `${TOTAL_PERFORMANCE_SECTION_SEL} > .sfe-total-performance-summary`,
  );
  if (!summary) return;

  const noteText = totalPerformancePriorYearTaxForecastNote(selectedYear);
  let note = summary.querySelector<HTMLElement>(".sfe-total-performance-tax-note");
  if (!noteText) {
    note?.remove();
    return;
  }
  if (!note) {
    note = document.createElement("span");
    note.className = "sfe-total-performance-tax-note";
  }
  note.textContent = noteText;
  const title = summary.querySelector(".insight-h");
  if (title && note.previousElementSibling !== title) {
    title.insertAdjacentElement("afterend", note);
  } else if (!note.isConnected) {
    summary.appendChild(note);
  }
}

function incomeTxnTableId(localLedgerAnalytics: boolean): TableColumnId {
  return localLedgerAnalytics ? "analytics-income-txn-local" : "analytics-income-txn";
}

function getIncomeTxnColumns(localLedgerAnalytics = false): ColumnState[] {
  const tableId = incomeTxnTableId(localLedgerAnalytics);
  return loadTableColumns(tableId, getTableColumnDefaults(tableId));
}

function syncAnalyticsIncomeColumnPicker(
  panel: HTMLElement,
  onApply: () => void,
  localLedgerAnalytics = false,
): void {
  const reset = panel.querySelector<HTMLElement>(".sfe-income-year-actions .sfe-reset-filters");
  if (!reset) return;
  const tableId = incomeTxnTableId(localLedgerAnalytics);
  ensureColumnPickerBesideReset(reset, {
    tableId,
    title: tableColumnTitle(tableId),
    defaults: getTableColumnDefaults(tableId),
    onApply,
  });
  panel.querySelector(".sfe-analytics-plus-header-actions .sfe-column-settings-btn")?.remove();
}

/** An estimated price is marked so it never reads as a measured fill. */
function renderIncomeUnderlyingCell(value: number | null | undefined, estimated: boolean): string {
  if (!(Number(value) > 0)) return `<td class="num">—</td>`;
  const text = `${estimated ? "≈" : ""}${fmtUsd(Number(value))}`;
  const title = estimated
    ? "Estimated — the fill minute was unavailable, so the session close stands in"
    : "Underlying share price recorded with this contract";
  return `<td class="num" title="${title}">${text}</td>`;
}

function renderIncomeLineCell(
  key: string,
  line: IncomeLine,
  amountClass: string,
  amountTitle: string,
  amountText: string,
  linkedIdsText: string,
): string {
  switch (key) {
    case "type":
      return `<td class="sfe-col-type">${renderTxnNoteBadge({
        label: line.label,
        className: `sfe-txn-kind sfe-txn-kind-${line.kind}`,
        note: line.note,
        txnId: line.txnId,
        cashFlowId: line.cashFlowId,
      })}</td>`;
    case "system":
      return `<td class="sfe-col-system sfe-mark-col" data-col="system">${brokerMarkInnerHtml({
        brokerCode: line.brokerCode ?? "",
        brokerColor: line.brokerColor ?? "",
        brokerTitle: line.brokerTitle || line.system || "",
        accountShort: line.accountShort ?? "",
        accountTitle: line.accountTitle || line.account || "",
      })}</td>`;
    case "account":
      return `<td class="sfe-col-account sfe-acct-col" data-col="account">${accountShortInnerHtml({
        brokerCode: line.brokerCode ?? "",
        brokerColor: line.brokerColor ?? "",
        brokerTitle: line.brokerTitle || line.system || "",
        accountShort: line.accountShort || "",
        accountTitle: line.accountTitle || line.account || "",
      })}</td>`;
    case "broker_mark":
      return brokerMarkCellHtml({
        brokerCode: line.brokerCode ?? "",
        brokerColor: line.brokerColor ?? "",
        brokerTitle: line.brokerTitle ?? "",
        accountShort: line.accountShort ?? "",
        accountTitle: line.accountTitle ?? "",
      });
    case "account_short":
      return accountShortCellHtml({
        brokerCode: line.brokerCode ?? "",
        brokerColor: line.brokerColor ?? "",
        brokerTitle: line.brokerTitle ?? "",
        accountShort: line.accountShort ?? "",
        accountTitle: line.accountTitle ?? "",
      });
    case "ticker":
      return `<td class="sfe-col-ticker">${line.ticker}</td>`;
    case "side":
      return `<td>${line.side}</td>`;
    case "strike":
      return `<td class="num">${line.strike}</td>`;
    case "openPop":
      return line.openPop != null && line.openPop > 0
        ? `<td class="num" title="Probability of profit the contract was sold at (1 − |delta|)">${(line.openPop * 100).toFixed(1)}%</td>`
        : `<td class="num">—</td>`;
    case "openPx":
      return renderIncomeUnderlyingCell(line.underlyingOpen, line.underlyingOpenEstimated === true);
    case "closePx":
      return renderIncomeUnderlyingCell(
        line.underlyingClose,
        line.underlyingCloseEstimated === true,
      );
    case "qty":
      return `<td class="num">${line.qty}</td>`;
    case "open":
      return `<td class="sfe-col-open">${line.open}</td>`;
    case "exp":
      return `<td class="sfe-col-exp">${line.exp}</td>`;
    case "credit":
      return `<td class="num">${line.credit}</td>`;
    case "btc":
      return `<td class="num">${line.btc === "—" ? "—" : `−${line.btc}`}</td>`;
    case "cost": {
      if (
        line.kind === "buy" &&
        line.side === "stock" &&
        line.holdingAvgAfter != null &&
        line.holdingAvgAfter > 0
      ) {
        const before =
          line.holdingAvgBefore != null && line.holdingAvgBefore > 0
            ? fmtUsd(line.holdingAvgBefore)
            : "—";
        return `<td class="num sfe-income-holding-avg" title="Holding avg before → after this buy">${before} → ${fmtUsd(line.holdingAvgAfter)}</td>`;
      }
      return `<td class="num">${line.avg}</td>`;
    }
    case "symbol":
      return `<td class="sfe-col-symbol">${line.symbol}</td>`;
    case "txnId":
      return `<td class="sfe-col-txn-id">${line.txnId != null ? `#${line.txnId}` : "—"}</td>`;
    case "linkedId":
      return `<td class="sfe-col-txn-id">${linkedIdsText || "—"}</td>`;
    case "link": {
      // Call assignments can explain their cost basis, so make those clickable.
      const traceable =
        line.linkStatus === "ledger" &&
        line.kind === "asgn" &&
        line.side === "call" &&
        line.txnId != null;
      if (traceable) {
        return `<td><button type="button" class="sfe-link-status sfe-link-status-${line.linkStatus} sfe-link-status-trace" data-assign-trace="${line.txnId}" title="Show the opening call and the put assignments that built this average cost">${line.linkStatus}</button></td>`;
      }
      return `<td><span class="sfe-link-status sfe-link-status-${line.linkStatus}">${line.linkStatus}</span></td>`;
    }
    case "pl":
      if (!shouldShowIncomeLinePl(line)) {
        return `<td class="num"${
          isCashHistoryIncomeLine(line)
            ? ' title="Cash movement — not included in income."'
            : ""
        }>—</td>`;
      }
      return `<td class="num ${amountClass}"${amountTitle}>${amountText}</td>`;
    default:
      return `<td>—</td>`;
  }
}

function incomeDayCollapsibleScope(localLedgerAnalytics: boolean): string {
  return localLedgerAnalytics ? ANALYTICS_L_INCOME_DAY_SCOPE : ANALYTICS_INCOME_DAY_SCOPE;
}

const INCOME_DAYS_EXPAND_ICON = `<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 6.1 8 11.1 13 6.1" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const INCOME_DAYS_COLLAPSE_ICON = `<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 9.9 8 4.9 13 9.9" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function incomeDaySections(host: ParentNode): HTMLDetailsElement[] {
  return [...host.querySelectorAll<HTMLDetailsElement>(":scope > details.sfe-income-day")];
}

function paintIncomeDaysToggle(host: HTMLElement, button: HTMLButtonElement): void {
  const days = incomeDaySections(host);
  const allOpen = days.length > 0 && days.every((day) => day.open);
  button.hidden = days.length === 0;
  const toolbar = button.closest<HTMLElement>(".sfe-income-days-toolbar");
  if (toolbar) toolbar.hidden = button.hidden;
  button.innerHTML = allOpen ? INCOME_DAYS_COLLAPSE_ICON : INCOME_DAYS_EXPAND_ICON;
  const label = allOpen ? "Collapse all days" : "Expand all days";
  button.setAttribute("aria-label", label);
  button.setAttribute("aria-pressed", allOpen ? "true" : "false");
  button.title = label;
  button.setAttribute("data-tip", label);
}

function buildIncomeDaysHost(
  month: IncomeMonthBucket,
  expandDays = false,
  localLedgerAnalytics = false,
  toggleHost?: HTMLElement,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "sfe-income-days-wrap";
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "small ghost sfe-income-days-toggle";
  if (toggleHost) {
    toggleHost.appendChild(toggle);
  } else {
    const toolbar = document.createElement("div");
    toolbar.className = "sfe-income-days-toolbar";
    toolbar.appendChild(toggle);
    wrap.appendChild(toolbar);
  }
  const daysHost = document.createElement("div");
  daysHost.className = "sfe-income-days";
  const dayScope = incomeDayCollapsibleScope(localLedgerAnalytics);
  const txnColumns = getIncomeTxnColumns(localLedgerAnalytics);
  const txnColgroup = renderTableColgroup(txnColumns, { lockWidths: true });
  const txnHeadRow = renderTableHeadRow(txnColumns);
  ensureTxnNoteClickBinding();

  daysHost.addEventListener("click", (event) => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>("[data-assign-trace]");
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    const txnId = Number(trigger.dataset.assignTrace);
    if (!(txnId > 0)) return;
    const trace = buildCallAssignTrace(lastIncomeTxnsForTrace, txnId);
    if (!trace) {
      showToast("No cost-basis trace available for this row.", "error");
      return;
    }
    void import("./analyticsAssignTrace").then(({ openAssignTraceModal }) => {
      openAssignTraceModal(trace);
    });
  });

  for (const day of [...month.days].reverse()) {
    const dayDetails = document.createElement("details");
    dayDetails.className = "collapsible sfe-income-day";
    dayDetails.dataset.sfeCollapsible = day.date;
    dayDetails.dataset.sfeCollapsibleDefault = expandDays ? "open" : "closed";
    dayDetails.open = isCollapsibleOpen(dayScope, day.date, expandDays);

    const cashHistoryTotal = day.lines
      .filter(isCashHistoryIncomeLine)
      .reduce((sum, line) => sum + line.amount, 0);
    const chips = [
      day.premiums ? `Premiums ${fmtUsd(day.premiums)}` : null,
      day.stcCredits ? `STC ${fmtUsd(day.stcCredits)}` : null,
      day.btcCost ? `BTC −${fmtUsd(day.btcCost)}` : null,
      day.btoCost ? `BTO −${fmtUsd(day.btoCost)}` : null,
      day.assignIncome ? `Call Assgn. ${fmtUsd(day.assignIncome)}` : null,
      day.stockSellIncome ? `Stock sales ${fmtUsd(day.stockSellIncome)}` : null,
      cashHistoryTotal ? `Cash ${fmtUsd(cashHistoryTotal)}` : null,
    ]
      .filter(Boolean)
      .join(" · ");

    const dayTotal = cashIncome(day);
    const dayTotalCls = incomeHeadlineCls(dayTotal);

    const lineRows = day.lines
      .map((line) => {
        const amountClass =
          line.amount < 0 ? "sfe-neg" : line.amount > 0 ? "roi-strong" : "";
        const txnAttr =
          line.txnId != null ? ` data-transaction-id="${line.txnId}"` : "";
        const linkedIdsText = line.linkedTxnIds.map((id) => `#${id}`).join(", ");
        const linkedAttr =
          line.linkedTxnIds.length
            ? ` data-linked-transaction-ids="${line.linkedTxnIds.join(",")}"`
            : "";
        const showLineAmount = shouldShowIncomeLinePl(line);
        const amountText = showLineAmount ? fmtUsd(line.amount) : "—";
        const amountTitle = showLineAmount
          ? ""
          : isCashHistoryIncomeLine(line)
            ? ' title="Cash movement — not included in income."'
            : ' title="Display only — excluded from income to prevent double accounting."';
        const columns = visibleTableColumns(getIncomeTxnColumns(localLedgerAnalytics));
        const cells = columns
          .map((column) =>
            renderIncomeLineCell(
              column.key,
              line,
              amountClass,
              amountTitle,
              amountText,
              linkedIdsText,
            ),
          )
          .join("");
        return `
          <tr${txnAttr}${linkedAttr} data-link-status="${line.linkStatus}">
            ${cells}
          </tr>`;
      })
      .join("");

    dayDetails.innerHTML = `
      <summary class="sfe-income-day-summary">
        <span class="sfe-income-summary-row">
          <span class="sfe-income-day-date">
            ${day.date}
            <span class="sfe-income-day-chips muted small">${chips}</span>
          </span>
          <span class="sfe-income-day-total ${dayTotalCls}">${fmtUsd(dayTotal)}</span>
        </span>
      </summary>
      <div class="sfe-income-txn-wrap">
        ${
          day.lines.length
            ? `<table class="sfe-income-txn-table recs" data-sfe-no-sticky-head>
                ${txnColgroup}
                <thead><tr>${txnHeadRow}</tr></thead>
                <tbody>${lineRows}</tbody>
              </table>`
            : `<p class="sfe-income-empty muted small">No transactions on this day.</p>`
        }
      </div>
    `;
    daysHost.appendChild(dayDetails);
  }

  bindDetailsCollapsiblePersistence(daysHost, dayScope);
  paintIncomeDaysToggle(daysHost, toggle);
  toggle.addEventListener("click", () => {
    const days = incomeDaySections(daysHost);
    if (!days.length) return;
    const open = !days.every((day) => day.open);
    for (const day of days) day.open = open;
    paintIncomeDaysToggle(daysHost, toggle);
  });
  daysHost.addEventListener(
    "toggle",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLDetailsElement) || !target.classList.contains("sfe-income-day")) return;
      paintIncomeDaysToggle(daysHost, toggle);
    },
    true,
  );
  wrap.append(daysHost);
  return wrap;
}

function fmtMetric(value: number, negate = false): string {
  const shown = negate && value ? `−${fmtUsdWhole(value)}` : fmtUsdWhole(value);
  return shown;
}

function updateExpenseTotalInCard(card: HTMLElement | null): void {
  if (!card) return;
  const monthKey = card.closest<HTMLElement>(".sfe-income-month-detail")?.dataset.month;
  const marginField = card.querySelector<HTMLElement>('[data-sfe-expense-part="margin"]');
  const usePredicted = card.querySelector<HTMLInputElement>(".sfe-own-capital-margin-pred")?.checked === true;
  const margin =
    usePredicted && marginField?.dataset.effectiveMargin != null
      ? Number(marginField.dataset.effectiveMargin) || 0
      : ((monthKey ? rhAccruedMarginCostForMonth(monthKey) : null) ??
          (Number(card.querySelector<HTMLInputElement>(".sfe-own-capital-margin-cost")?.value) || 0));
  const fee = Number(card.querySelector<HTMLInputElement>(".sfe-own-capital-per-trade-fee")?.value) || 0;
  const count = Number(
    card.querySelector<HTMLElement>('[data-sfe-expense-part="per-trade"]')?.dataset.tradeCount,
  );
  const tradeCount = Number.isFinite(count) ? Math.max(0, Math.round(count)) : 0;
  const totalEl = card.querySelector<HTMLElement>(".sfe-expense-total-val");
  if (totalEl) totalEl.textContent = fmtUsd(margin + fee * tradeCount);
}

function renderExpensesSubsections(
  entry: Pick<OwnCapitalEntry, "marginCost" | "perTradeFee" | "tradeCount" | "usePredictedMarginCost">,
  month: IncomeMonthBucket | undefined,
  editable: boolean,
  effectiveMarginCost?: number,
  marginUsed?: number,
  monthKey?: string,
  opts: { hidePerTrade?: boolean; useRhMarginHistory?: boolean } = {},
): string {
  const normalized = normalizeOwnCapitalEntry(entry);
  const key = monthKey ?? currentMonthKey();
  const autoForecast = monthUsesAutomaticMarginForecast(key);
  const usePredicted = autoForecast || normalized.usePredictedMarginCost === true;
  const manualMargin = normalized.marginCost ?? 0;
  const displayMargin = autoForecast
    ? forecastMarginCostFromCurrentDaily() ?? effectiveMarginCost ?? manualMargin
    : usePredicted
      ? effectiveMarginCost ?? manualMargin
      : opts.useRhMarginHistory === false
        ? manualMargin
        : rhAccruedMarginCostForMonth(key) ?? manualMargin;
  const fmtMargin = fmtUsdWhole;
  const perTradeFee = opts.hidePerTrade ? 0 : normalized.perTradeFee ?? 0;
  const tradeCount = month ? countTradesInMonth(month) : Math.max(0, Math.round(Number(normalized.tradeCount) || 0));
  const perTradeTotal = opts.hidePerTrade ? 0 : perTradeExpenseTotal(normalized, tradeCount);
  const pastMonth = isPastAnalyticsMonthKey(key);
  const futureMonth = key > currentMonthKey();
  const showPredToggle = editable || futureMonth;
  const predToggleTitle = autoForecast
    ? "Current and later months use a full-month forecast at this month's daily margin cost"
    : pastMonth
      ? "Use Forecast is only available for the current and future months"
      : futureMonth
        ? "Use the current month's margin cost in this forecast"
        : `Use forecast margin interest (Robinhood daily interest this month when available, otherwise margin used @ ${(MARGIN_APR * 100).toFixed(1)}% APR ÷ 12)`;
  const predLocked = pastMonth || autoForecast;
  const predToggle = showPredToggle
    ? `<label class="sfe-margin-pred-toggle${predLocked ? " sfe-margin-pred-toggle-disabled" : ""}" title="${escapeHtml(predToggleTitle)}">
        <input type="checkbox" class="sfe-own-capital-margin-pred"${usePredicted ? " checked" : ""}${predLocked ? " disabled" : ""} />
        <span>Use Forecast</span>
      </label>`
    : usePredicted
      ? `<span class="sfe-margin-pred-badge">Use Forecast</span>`
      : "";

  const marginField = editable
    ? `<div class="sfe-month-card-field${usePredicted ? " sfe-margin-cost-predicted" : ""}" data-sfe-expense-part="margin" data-sfe-month-field="margin-cost" data-effective-margin="${displayMargin}">
        <div class="sfe-month-card-field-view">
          <span class="kpi-v sfe-month-card-static-val">${fmtMargin(displayMargin)}</span>
          ${monthCardEditBtn("Edit margin expense")}
        </div>
        <div class="sfe-month-card-field-edit" hidden>
          <div class="sfe-month-card-prefix-row">
            <span class="sfe-month-card-input-prefix">$</span>
            <input type="number" step="0.01" min="0" class="sfe-month-card-input sfe-own-capital-margin-cost" value="${manualMargin}"${usePredicted ? " disabled" : ""} />
          </div>
        </div>
      </div>`
    : `<span class="kpi-v">${fmtMargin(displayMargin)}</span>`;

  const marginValueRow = editable
    ? `<div class="sfe-month-roc-row sfe-margin-cost-row">${marginField}${predToggle}</div>`
    : predToggle
      ? `<div class="sfe-month-roc-row sfe-margin-cost-row">${marginField}${predToggle}</div>`
      : marginField;

  const perTradeView = `${fmtUsd(perTradeFee)}×${tradeCount}`;
  const perTradeField = editable
    ? `<div class="sfe-month-roc-row">
        <div class="sfe-month-card-field" data-sfe-expense-part="per-trade" data-sfe-month-field="per-trade" data-trade-count="${tradeCount}">
          <div class="sfe-month-card-field-view">
            <span class="kpi-v sfe-month-card-static-val sfe-expense-per-trade-view">${perTradeView}</span>
            ${monthCardEditBtn("Edit per trade fee")}
          </div>
          <div class="sfe-month-card-field-edit" hidden>
            <div class="sfe-expense-per-trade-edit-row">
              <div class="sfe-month-card-prefix-row">
                <span class="sfe-month-card-input-prefix">$</span>
                <input type="number" step="0.01" min="0" class="sfe-month-card-input sfe-own-capital-per-trade-fee" value="${perTradeFee}" />
              </div>
              <span class="sfe-expense-per-trade-times" aria-hidden="true">×</span>
              <span class="kpi-v sfe-expense-trade-count">${tradeCount}</span>
            </div>
          </div>
        </div>
        <span class="kpi-v sfe-expense-per-trade-amt">${fmtUsd(perTradeTotal)}</span>
      </div>`
    : `<span class="kpi-v sfe-expense-per-trade-summary">${fmtUsd(perTradeTotal)}<span class="sfe-expense-per-trade-meta"> · ${tradeCount} trades</span></span>`;

  const marginDetails =
    marginUsed != null && Number.isFinite(marginUsed)
      ? renderMonthMarginDetailsBlock(marginUsed, monthKey, opts.useRhMarginHistory !== false)
      : "";

  const perTradeLine = opts.hidePerTrade
    ? ""
    : `<div class="sfe-month-roc-line">
        <span class="sfe-month-roc-line-k">Per trade</span>
        ${perTradeField}
      </div>`;

  return `
    <div class="sfe-month-roc-lines">
      ${perTradeLine}
      ${marginDetails}
      <div class="sfe-month-roc-line">
        <span class="sfe-month-roc-line-k sfe-margin-cost-line-k">Margin cost${marginCostHistoryBtnHtml()}</span>
        ${marginValueRow}
      </div>
    </div>
  `;
}

function monthCardEditBtn(
  ariaLabel: string,
  hidden = false,
  extraAttrs = "",
  extraClass = "",
): string {
  if (hidden) return "";
  const classes = ["sfe-month-card-edit-btn", extraClass].filter(Boolean).join(" ");
  return (
    `<button type="button" class="${classes}" aria-label="${ariaLabel}" title="${ariaLabel}"${extraAttrs ? ` ${extraAttrs}` : ""}>` +
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">` +
    `<path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>` +
    `</svg></button>`
  );
}

function taxEstPaymentsEditBtn(ariaLabel: string): string {
  return (
    `<button type="button" class="sfe-month-card-edit-btn" data-sfe-open-est-payments title="Manage tax payments" aria-label="${escapeHtml(ariaLabel)}">` +
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">` +
    `<path fill="currentColor" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.986 8.986a1 1 0 0 1-.446.261l-3.25 1.083a.25.25 0 0 1-.316-.316l1.083-3.25a1 1 0 0 1 .261-.446l8.986-8.986z"/>` +
    `</svg></button>`
  );
}

function setMonthCardFieldEditing(field: HTMLElement, editing: boolean): void {
  const view = field.querySelector<HTMLElement>(".sfe-month-card-field-view");
  const edit = field.querySelector<HTMLElement>(".sfe-month-card-field-edit");
  if (!view || !edit) return;
  view.hidden = editing;
  edit.hidden = !editing;
  if (editing) {
    const input =
      edit.querySelector<HTMLInputElement>(".sfe-own-capital-per-trade-fee") ??
      edit.querySelector<HTMLInputElement>("input");
    input?.focus();
    input?.select();
  }
}

function closeAllMonthCardFieldEdits(wrap: HTMLElement): void {
  for (const field of wrap.querySelectorAll<HTMLElement>(".sfe-month-card-field")) {
    setMonthCardFieldEditing(field, false);
  }
}

function syncMonthCardFieldView(field: HTMLElement): void {
  if (field.dataset.sfeMonthField === "per-trade") {
    const feeInput = field.querySelector<HTMLInputElement>(".sfe-own-capital-per-trade-fee");
    const viewMain = field.querySelector<HTMLElement>(".sfe-expense-per-trade-view");
    const viewAmt = field.querySelector<HTMLElement>(".sfe-expense-per-trade-amt");
    const tradeCount = Number(field.dataset.tradeCount) || 0;
    const fee = Number(feeInput?.value) || 0;
    if (viewMain) viewMain.textContent = `${fmtUsd(fee)}×${tradeCount}`;
    if (viewAmt) viewAmt.textContent = fmtUsd(fee * tradeCount);
    updateExpenseTotalInCard(field.closest(".sfe-month-card-expenses"));
    return;
  }

  const input = field.querySelector<HTMLInputElement>(".sfe-month-card-field-edit input");
  const viewVal = field.querySelector<HTMLElement>(".sfe-month-card-static-val");
  if (!input || !viewVal) return;
  if (input.classList.contains("sfe-target-roc-pct")) {
    const pct = Number(input.value);
    viewVal.textContent = fmtPct(Number.isFinite(pct) ? pct : 0);
    return;
  }
  viewVal.textContent = fmtUsdWhole(Number(input.value) || 0);
  if (field.dataset.sfeMonthField === "margin-cost") {
    updateExpenseTotalInCard(field.closest(".sfe-month-card-expenses"));
  }
}

function saveMonthCardFieldInput(
  input: HTMLInputElement,
  wrap: HTMLElement,
  month: string,
  onSaveOwnCapital: (month: string, entry: Partial<OwnCapitalEntry>) => void,
): void {
  if (input.classList.contains("sfe-target-roc-pct")) {
    const pct = Number(input.value);
    if (!Number.isFinite(pct) || pct < 0) return;
    writeTargetRocPctLocal(month, pct);
    onSaveOwnCapital(month, { targetRocPct: pct });
    return;
  }
  if (
    input.classList.contains("sfe-own-capital-margin-cost") ||
    input.classList.contains("sfe-own-capital-per-trade-fee")
  ) {
    onSaveOwnCapital(month, readExpenseFieldsFromWrap(wrap));
    return;
  }
  if (
    input.classList.contains("sfe-own-capital-cash-added") ||
    input.classList.contains("sfe-own-capital-withdrawn")
  ) {
    onSaveOwnCapital(month, readOwnCapitalFieldsFromWrap(wrap));
  }
}

function wireMarginPredToggle(
  host: HTMLElement,
  onPatchOwnCapitalLedger: (month: string, entry: Partial<OwnCapitalEntry>) => void,
): void {
  host.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.classList.contains("sfe-own-capital-margin-pred")) return;
    const wrap = input.closest<HTMLElement>(".sfe-income-month-detail");
    const month = wrap?.dataset.month ?? selectedIncomeMonthKey;
    if (!wrap || !month) return;
    if (input.disabled || isPastAnalyticsMonthKey(month)) {
      input.checked = !input.checked;
      return;
    }
    const enabled = input.checked;
    void (async () => {
      const { ok, usePredictedMarginCost } = await saveAnalyticsMarginPredToggle(month, enabled);
      if (!ok) {
        input.checked = !enabled;
        showToast("Could not update margin setting.", "error");
        return;
      }
      onPatchOwnCapitalLedger(month, {
        usePredictedMarginCost: usePredictedMarginCost === true,
      });
    })();
  });
}

function wireMonthCardFieldEditors(
  host: HTMLElement,
  onReset: () => void,
  onSaveOwnCapital: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  _onPatchOwnCapitalLedger: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  ledger: Record<string, OwnCapitalEntry> = {},
  onTargetRocSaved?: () => void | Promise<void>,
): void {
  host.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>(".sfe-month-card-edit-btn");
    if (!btn) return;
    const field = btn.closest<HTMLElement>(".sfe-month-card-field");
    const wrap = btn.closest<HTMLElement>(".sfe-income-month-detail");
    if (!field || !wrap) return;
    if (
      field.dataset.sfeMonthField === "margin-cost" &&
      wrap.querySelector<HTMLInputElement>(".sfe-own-capital-margin-pred")?.checked
    ) {
      return;
    }
    event.preventDefault();
    closeAllMonthCardFieldEdits(wrap);
    setMonthCardFieldEditing(field, true);
  });

  host.addEventListener("keydown", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.closest(".sfe-month-card-field-edit")) return;
    if (event.key === "Escape") {
      event.preventDefault();
      onReset();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      input.blur();
    }
  });

  host.addEventListener(
    "focusout",
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.closest(".sfe-month-card-field-edit")) return;
      const field = input.closest<HTMLElement>(".sfe-month-card-field");
      if (!field) return;
      requestAnimationFrame(() => {
        if (field.contains(document.activeElement)) return;
        syncMonthCardFieldView(field);
        setMonthCardFieldEditing(field, false);
        const wrap = input.closest<HTMLElement>(".sfe-income-month-detail");
        const month = wrap?.dataset.month ?? selectedIncomeMonthKey;
        if (wrap && month) saveMonthCardFieldInput(input, wrap, month, onSaveOwnCapital);
      });
    },
    true,
  );
}

/** Per-month target ROC % (return on own capital at month start).
 * Source of truth is `/own-capital` (`targetRocPct`); localStorage is a cache.
 * Months without an explicit saved % inherit the nearest prior non-default. */
const TARGET_ROC_PCT_STORAGE_KEY = "sfe-target-roc-pct-by-month";
const LEGACY_TARGET_INCOME_PCT_STORAGE_KEY = "sfe-target-income-pct";
const DEFAULT_TARGET_ROC_PCT = 6;

function isDefaultTargetRocPct(value: number | undefined): boolean {
  return value != null && Number.isFinite(value) && value === DEFAULT_TARGET_ROC_PCT;
}

function readTargetRocLedger(): Record<string, number> {
  try {
    const raw = localStorage.getItem(TARGET_ROC_PCT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    for (const [month, value] of Object.entries(parsed)) {
      if (!/^\d{4}-\d{2}$/.test(month)) continue;
      const n = Number(value);
      if (Number.isFinite(n) && n >= 0) out[month] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function legacyDefaultTargetRocPct(): number {
  try {
    const raw = localStorage.getItem(LEGACY_TARGET_INCOME_PCT_STORAGE_KEY);
    const n = raw != null ? Number(raw) : NaN;
    if (Number.isFinite(n) && n >= 0) return n;
  } catch {
    /* ignore */
  }
  return DEFAULT_TARGET_ROC_PCT;
}

/** Value a new month should copy — nearest prior month with an explicit
 * non-default stored entry (server ledger or local cache), else legacy/default.
 * Stored 6% is the product fallback, not a year-over-year plan, so a later
 * default stamp must not hide an earlier saved target (e.g. 7%). */
function inheritedTargetRocPct(
  month: string,
  ledger: Record<string, OwnCapitalEntry>,
): number {
  let current = prevMonthKey(month);
  for (let depth = 0; depth < 240; depth++) {
    const fromLedger = ledger[current]?.targetRocPct;
    if (fromLedger != null && Number.isFinite(fromLedger) && !isDefaultTargetRocPct(fromLedger)) {
      return fromLedger;
    }
    const fromLocal = readTargetRocLedger()[current];
    if (fromLocal != null && Number.isFinite(fromLocal) && !isDefaultTargetRocPct(fromLocal)) {
      return fromLocal;
    }
    current = prevMonthKey(current);
  }
  return legacyDefaultTargetRocPct();
}

function readTargetRocPct(month: string, ledger: Record<string, OwnCapitalEntry> = {}): number {
  const fromLedger = ledger[month]?.targetRocPct;
  if (fromLedger != null && Number.isFinite(fromLedger) && !isDefaultTargetRocPct(fromLedger)) {
    return fromLedger;
  }
  const local = readTargetRocLedger()[month];
  if (local != null && Number.isFinite(local) && !isDefaultTargetRocPct(local)) {
    return local;
  }
  const inherited = inheritedTargetRocPct(month, ledger);
  if (!isDefaultTargetRocPct(inherited)) return inherited;
  if (fromLedger != null && Number.isFinite(fromLedger)) return fromLedger;
  if (local != null && Number.isFinite(local)) return local;
  return inherited;
}

function writeTargetRocPctLocal(month: string, pct: number): void {
  try {
    const cache = readTargetRocLedger();
    cache[month] = pct;
    localStorage.setItem(TARGET_ROC_PCT_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore */
  }
}

function monthsFromThroughEoy(startMonth: string): string[] {
  const keys = [startMonth];
  const [yearRaw, monthRaw] = startMonth.split("-").map(Number);
  if (!Number.isFinite(yearRaw) || !Number.isFinite(monthRaw)) return keys;
  for (let m = monthRaw + 1; m <= 12; m += 1) {
    keys.push(`${yearRaw}-${String(m).padStart(2, "0")}`);
  }
  return keys;
}

/** Persist month plan fields the same way as the web month card (local cache + proxy ledger). */
export async function saveAnalyticsMonthSettings(
  month: string,
  patch: {
    targetRocPct?: number;
    marginCost?: number;
    usePredictedMarginCost?: boolean;
    perTradeFee?: number;
    cashAdded?: number;
    withdrawn?: number;
    /** When true, also write target ROC for later months in the same year through December. */
    propagateTargetRocToEoy?: boolean;
    /** When true, also write cash added for later months through December. */
    propagateCashAddedToEoy?: boolean;
    /** When true, also write withdrawn for later months through December. */
    propagateWithdrawnToEoy?: boolean;
    /** When true, also write margin + per-trade fee for later months through December. */
    propagateExpensesToEoy?: boolean;
  },
): Promise<{ ok: boolean; targetRocPct?: number }> {
  const targetRocPct =
    patch.targetRocPct === undefined
      ? undefined
      : Number.isFinite(patch.targetRocPct) && patch.targetRocPct >= 0
        ? patch.targetRocPct
        : undefined;

  const propagateTargetRoc = Boolean(patch.propagateTargetRocToEoy && targetRocPct !== undefined);
  const propagateCashAdded = Boolean(patch.propagateCashAddedToEoy && patch.cashAdded !== undefined);
  const propagateWithdrawn = Boolean(patch.propagateWithdrawnToEoy && patch.withdrawn !== undefined);
  const propagateExpenses = Boolean(
    patch.propagateExpensesToEoy &&
      (patch.marginCost !== undefined ||
        patch.perTradeFee !== undefined ||
        patch.usePredictedMarginCost !== undefined),
  );
  const monthKeys =
    propagateTargetRoc || propagateCashAdded || propagateWithdrawn || propagateExpenses
      ? monthsFromThroughEoy(month)
      : [month];

  const ledger = await fetchOwnCapitalLedger();
  let ok = true;
  for (const key of monthKeys) {
    const prev = ledger[key] ?? normalizeOwnCapitalEntry(undefined);
    const isStartMonth = key === month;
    const nextPatch: Partial<OwnCapitalEntry> = {};
    if (targetRocPct !== undefined && (isStartMonth || propagateTargetRoc)) {
      nextPatch.targetRocPct = targetRocPct;
    }
    if (isStartMonth || propagateExpenses) {
      if (patch.marginCost !== undefined) nextPatch.marginCost = Math.max(0, Number(patch.marginCost) || 0);
      if (patch.usePredictedMarginCost !== undefined) {
        nextPatch.usePredictedMarginCost = patch.usePredictedMarginCost;
      }
      if (patch.perTradeFee !== undefined) nextPatch.perTradeFee = Math.max(0, Number(patch.perTradeFee) || 0);
    }
    if (patch.cashAdded !== undefined && (isStartMonth || propagateCashAdded)) {
      nextPatch.cashAdded = Number(patch.cashAdded) || 0;
    }
    if (patch.withdrawn !== undefined && (isStartMonth || propagateWithdrawn)) {
      nextPatch.withdrawn = Number(patch.withdrawn) || 0;
    }
    if (!Object.keys(nextPatch).length) continue;
    if (nextPatch.targetRocPct !== undefined) {
      writeTargetRocPctLocal(key, nextPatch.targetRocPct);
    }
    const merged = mergeOwnCapitalEntry(prev, nextPatch);
    const saved = await saveOwnCapitalEntry(key, merged, { quiet: true });
    if (!saved) ok = false;
    else ledger[key] = merged;
  }

  return { ok, targetRocPct };
}

/** Toggle forecast margin for one month (persisted in own-capital.json on the proxy). */
export async function saveAnalyticsMarginPredToggle(
  month: string,
  enabled: boolean,
): Promise<{ ok: boolean; usePredictedMarginCost?: boolean }> {
  const result = await saveAnalyticsMonthSettings(month, { usePredictedMarginCost: enabled });
  if (!result.ok) return { ok: false };
  const ledger = await fetchOwnCapitalLedger({ bustCache: true });
  return {
    ok: true,
    usePredictedMarginCost: ledger[month]?.usePredictedMarginCost === true,
  };
}

/** @deprecated Prefer saveAnalyticsMonthSettings. */
export async function saveAnalyticsTargetRocPct(
  month: string,
  pctRaw: number,
): Promise<{ ok: boolean; targetRocPct: number }> {
  const result = await saveAnalyticsMonthSettings(month, { targetRocPct: pctRaw });
  return {
    ok: result.ok,
    targetRocPct: result.targetRocPct ?? DEFAULT_TARGET_ROC_PCT,
  };
}

const TARGET_ROC_MODAL_ID = "sfe-target-roc-edit-modal";

export function openTargetRocEditModal(options: {
  monthKey: string;
  targetRocPct: number;
  baseCapital: number;
  onSaved?: () => void | Promise<void>;
}): void {
  const { monthKey, targetRocPct, baseCapital, onSaved } = options;
  const label = monthLabel(monthKey);
  const year = monthKey.slice(0, 4);
  const eoyLabel = `${year}-12`;

  let overlay = document.getElementById(TARGET_ROC_MODAL_ID);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = TARGET_ROC_MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-target-roc-edit-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-target-roc-edit-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-target-roc-edit-title"></h3>
          <button type="button" class="sfe-modal-close" data-action="target-roc-edit-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small sfe-target-roc-edit-lead" data-target-roc-edit-lead></p>
        <form class="sfe-target-roc-edit-form" data-target-roc-edit-form>
          <label class="sfe-modal-field sfe-target-roc-edit-field">
            <span>Target return</span>
            <span class="sfe-target-roc-edit-input-row">
              <input type="number" name="target_roc_pct" step="0.1" min="0" required class="sfe-target-roc-edit-input" data-target-roc-edit-input />
              <span class="sfe-target-roc-edit-suffix" aria-hidden="true">%</span>
            </span>
          </label>
          <p class="muted small sfe-target-roc-edit-aside" data-target-roc-edit-aside></p>
          <label class="sfe-target-roc-edit-propagate">
            <input type="checkbox" name="propagate_eoy" data-target-roc-edit-propagate />
            <span class="muted small sfe-target-roc-edit-propagate-text" data-target-roc-edit-propagate-label></span>
          </label>
          <div class="sfe-modal-actions">
            <button type="button" class="ghost small" data-action="target-roc-edit-cancel">Cancel</button>
            <button type="submit" class="small sfe-btn-primary">Save</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        closeTargetRocEditModal();
        return;
      }
      const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
      if (action === "target-roc-edit-close" || action === "target-roc-edit-cancel") {
        closeTargetRocEditModal();
      }
    });
  }

  const title = overlay.querySelector("#sfe-target-roc-edit-title");
  const lead = overlay.querySelector("[data-target-roc-edit-lead]");
  const form = overlay.querySelector<HTMLFormElement>("[data-target-roc-edit-form]");
  const input = overlay.querySelector<HTMLInputElement>("[data-target-roc-edit-input]");
  const aside = overlay.querySelector("[data-target-roc-edit-aside]");
  const propagateLabel = overlay.querySelector("[data-target-roc-edit-propagate-label]");
  const propagateInput = overlay.querySelector<HTMLInputElement>("[data-target-roc-edit-propagate]");
  if (!title || !lead || !form || !input || !aside || !propagateLabel || !propagateInput) return;

  title.textContent = `Target return · ${label}`;
  lead.textContent =
    baseCapital > 0
      ? `Goal return on base capital (${fmtUsdWhole(baseCapital)}) for ${label}.`
      : `Goal return on base capital for ${label}.`;
  input.value = String(targetRocPct);
  propagateInput.checked = false;
  propagateLabel.textContent = `Propagate till end of year (through ${eoyLabel})`;

  const syncAside = (): void => {
    const pct = Number(input.value);
    const income =
      baseCapital > 0 && Number.isFinite(pct) && pct >= 0 ? (pct / 100) * baseCapital : 0;
    aside.textContent = income > 0 ? `Target income → ${fmtUsdWhole(income)}` : "Target income → —";
  };
  syncAside();
  input.oninput = syncAside;

  form.onsubmit = async (event) => {
    event.preventDefault();
    const pct = Number(input.value);
    if (!Number.isFinite(pct) || pct < 0) {
      showToast("Enter a valid target return.", "error");
      return;
    }
    const targetRoc = pct;
    const propagateTargetRocToEoy = propagateInput.checked;
    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    try {
      const { ok } = await saveAnalyticsMonthSettings(monthKey, {
        targetRocPct: targetRoc,
        propagateTargetRocToEoy,
      });
      if (!ok) {
        showToast("Could not save target return.", "error");
        return;
      }
      showToast(
        propagateTargetRocToEoy
          ? `Saved target return through ${eoyLabel}.`
          : `Saved target return for ${label}.`,
        "success",
      );
      closeTargetRocEditModal();
      await onSaved?.();
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  };

  overlay.classList.add("sfe-modal-open");
  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });
}

function closeTargetRocEditModal(): void {
  document.getElementById(TARGET_ROC_MODAL_ID)?.classList.remove("sfe-modal-open");
}

function wireTargetRocEditModal(
  host: HTMLElement,
  ledger: Record<string, OwnCapitalEntry>,
  onSaved: () => void | Promise<void>,
): void {
  if (host.dataset.targetRocModalBound === "1") return;
  host.dataset.targetRocModalBound = "1";
  host.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "[data-sfe-open-target-roc-modal]",
    );
    if (!btn) return;
    event.preventDefault();
    const wrap = btn.closest<HTMLElement>(".sfe-income-month-detail");
    const monthKey = wrap?.dataset.month ?? selectedIncomeMonthKey;
    if (!monthKey || !wrap) return;
    const targetRocPct = readTargetRocPct(monthKey, ledger);
    const baseCapital = Number(btn.dataset.baseCapital) || 0;
    openTargetRocEditModal({ monthKey, targetRocPct, baseCapital, onSaved });
  });
}

/** Same stacked income components as the Total Income KPI. */
function renderMonthIncomeDetails(
  month: IncomeMonthBucket,
  marginCost: number,
  forecastMargin = false,
): string {
  const assignCls = month.assignIncome < 0 ? "sfe-neg" : month.assignIncome > 0 ? "roi-strong" : "";
  const stockSellCls =
    month.stockSellIncome < 0
      ? "sfe-neg"
      : month.stockSellIncome > 0
        ? "roi-strong"
        : "";
  const btoLine =
    month.btoCost > 0
      ? `<span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">BTO cost <b>-${fmtUsdWhole(month.btoCost)}</b></span>`
      : "";
  const stcLine =
    month.stcCredits > 0
      ? `<span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold">STC <b>${fmtUsdWhole(month.stcCredits)}</b></span>`
      : "";
  return `
    <div class="sfe-income-kpi-details">
      <span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold sfe-income-kpi-premiums">Premiums <b>${fmtUsdWhole(month.premiums)}</b></span>
      ${stcLine}
      <span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold ${assignCls}">Call Assgn. <b>${fmtUsdWhole(month.assignIncome)}</b></span>
      ${
        month.stockSellIncome !== 0
          ? `<span class="sfe-income-kpi-detail sfe-income-kpi-detail-bold ${stockSellCls}">Stock sales <b>${fmtUsdWhole(month.stockSellIncome)}</b></span>`
          : ""
      }
      <span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">BTC cost <b>-${fmtUsdWhole(month.btcCost)}</b></span>
      ${btoLine}
      <span class="sfe-income-kpi-detail sfe-neg sfe-income-kpi-detail-bold">Margin cost ${forecastMargin ? marginForecastBadgeHtml() : ""}<b>-${fmtUsdWhole(marginCost)}</b></span>
    </div>
`;
}

/** Track shows 0–125% so overachievement extends past the 100% goal mark. */
const TARGET_PROGRESS_SCALE_MAX = 125;
const TARGET_PROGRESS_GOAL_TRACK_PCT = (100 / TARGET_PROGRESS_SCALE_MAX) * 100;
const TARGET_PROGRESS_SEGMENTS = [
  { widthPct: 48, cls: "sfe-target-progress-seg-red" },
  { widthPct: 16, cls: "sfe-target-progress-seg-orange" },
  { widthPct: 16, cls: "sfe-target-progress-seg-yellow" },
  { widthPct: 20, cls: "sfe-target-progress-seg-over" },
] as const;

function targetProgressState(progressPct: number | null): {
  fillTrackWidth: number;
  overfillTrackWidth: number;
  fillCls: string;
  isOver: boolean;
  goalReached: boolean;
} {
  if (progressPct == null || !Number.isFinite(progressPct)) {
    return {
      fillTrackWidth: 0,
      overfillTrackWidth: 0,
      fillCls: "sfe-target-progress-fill-idle",
      isOver: false,
      goalReached: false,
    };
  }
  const capped = Math.min(progressPct, TARGET_PROGRESS_SCALE_MAX);
  const fillTrackWidth = (Math.min(capped, 100) / TARGET_PROGRESS_SCALE_MAX) * 100;
  const overfillTrackWidth =
    progressPct > 100
      ? ((Math.min(progressPct, TARGET_PROGRESS_SCALE_MAX) - 100) /
          TARGET_PROGRESS_SCALE_MAX) *
        100
      : 0;
  const isOver = progressPct > 100;
  const goalReached = progressPct >= 100;

  if (progressPct >= 100) {
    return {
      fillTrackWidth,
      overfillTrackWidth,
      fillCls: "sfe-target-progress-fill-done",
      isOver,
      goalReached,
    };
  }
  if (progressPct >= 80) {
    return {
      fillTrackWidth,
      overfillTrackWidth: 0,
      fillCls: "sfe-target-progress-fill-yellow",
      isOver: false,
      goalReached: false,
    };
  }
  if (progressPct >= 60) {
    return {
      fillTrackWidth,
      overfillTrackWidth: 0,
      fillCls: "sfe-target-progress-fill-orange",
      isOver: false,
      goalReached: false,
    };
  }
  return {
    fillTrackWidth,
    overfillTrackWidth: 0,
    fillCls: "sfe-target-progress-fill-red",
    isOver: false,
    goalReached: false,
  };
}

function targetProgressLabelAlign(thumbLeft: number): string {
  if (thumbLeft >= 88) return "sfe-target-progress-marker-label-end";
  if (thumbLeft <= 12) return "sfe-target-progress-marker-label-start";
  return "";
}

export function renderTargetProgressIndicator(progressPct: number | null): string {
  const { fillTrackWidth, overfillTrackWidth, fillCls, goalReached } =
    targetProgressState(progressPct);
  const ariaNow = progressPct != null ? Math.round(Math.max(0, progressPct)) : 0;
  const ariaLabel =
    progressPct != null
      ? `Income ${Math.round(progressPct)}% of target; completion mark is 100%`
      : "Income progress toward target";
  const segmentsHtml = TARGET_PROGRESS_SEGMENTS.map(
    (seg) =>
      `<div class="sfe-target-progress-seg ${seg.cls}" style="width: ${seg.widthPct}%"></div>`,
  ).join("");
  const overfillHtml =
    overfillTrackWidth > 0
      ? `<div class="sfe-target-progress-overfill" style="width: ${overfillTrackWidth}%; left: ${TARGET_PROGRESS_GOAL_TRACK_PCT}%"></div>`
      : "";
  const goalCls = goalReached ? "sfe-target-progress-goal-reached" : "";
  const showThumb = fillTrackWidth > 0 || overfillTrackWidth > 0;
  const thumbLeft =
    overfillTrackWidth > 0
      ? fillTrackWidth + overfillTrackWidth
      : fillTrackWidth;
  const actualMarkerHtml =
    showThumb && progressPct != null
      ? `<span class="sfe-target-progress-marker sfe-target-progress-marker-actual ${fillCls}" style="left: ${thumbLeft}%">` +
        `<span class="sfe-target-progress-marker-label ${targetProgressLabelAlign(thumbLeft)}">${Math.round(progressPct)}%</span>` +
        `<span class="sfe-target-progress-marker-line" aria-hidden="true"></span>` +
        `</span>`
      : "";
  return (
    `<div class="sfe-target-progress">` +
    `<div class="sfe-target-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="125" aria-valuenow="${ariaNow}" aria-label="${ariaLabel}">` +
    `<div class="sfe-target-progress-inner">` +
    `<div class="sfe-target-progress-segments">${segmentsHtml}</div>` +
    `<div class="sfe-target-progress-fill ${fillCls}" style="width: ${fillTrackWidth}%"></div>` +
    overfillHtml +
    `</div>` +
    `<span class="sfe-target-progress-marker sfe-target-progress-marker-target ${goalCls}" style="left: ${TARGET_PROGRESS_GOAL_TRACK_PCT}%" title="100% target">` +
    `<span class="sfe-target-progress-marker-line" aria-hidden="true"></span>` +
    `</span>` +
    actualMarkerHtml +
    `</div>` +
    `</div>`
  );
}

function buildMonthDetail(
  month: IncomeMonthBucket,
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  ledger: Record<string, OwnCapitalEntry>,
  forecastView: boolean,
  projectedTimeline: Map<string, OwnCapitalPoint>,
  expandIncomeDays = false,
  stockCostBasis = 0,
  readOnlyOwnCapitalEdits = false,
  localLedgerAnalytics = false,
): HTMLElement {
  const detail = document.createElement("div");
  detail.className = "sfe-income-month-detail";
  detail.dataset.month = month.month;
  if (forecastView) detail.classList.add("sfe-forecast-month-detail");

  const timeline = forecastView ? projectedTimeline : ownCapitalByMonth;
  const ownCapitalPoint = timeline.get(month.month);
  const expenseEntryRaw = ledgerEntryForMonth(month.month, ledger, timeline);
  // Analytics(L): broker fees are already in trade income — never invent SF $0.06/trade.
  const expenseEntry = localLedgerAnalytics
    ? {
        ...expenseEntryRaw,
        perTradeFee: 0,
        usePredictedMarginCost:
          monthUsesAutomaticMarginForecast(month.month) ||
          (!isPastAnalyticsMonthKey(month.month) && expenseEntryRaw.usePredictedMarginCost !== false),
      }
    : expenseEntryRaw;
  const ownCapitalValue = ownCapitalPoint?.ownCapital ?? 0;
  const targetPct = readTargetRocPct(month.month, ledger);
  const prevOwnCapital = forecastView
    ? baseCapitalForMonth(month.month, ownCapitalByMonth, projectedTimeline)
    : timeline.get(prevMonthKey(month.month))?.ownCapital ?? 0;
  const targetIncomeDollar = prevOwnCapital > 0 ? (targetPct / 100) * prevOwnCapital : 0;
  const cashAdded = ownCapitalPoint?.cashAdded ?? 0;
  const withdrawn = ownCapitalPoint?.withdrawn ?? 0;
  const effectiveMargin = localLedgerAnalytics
    ? Number(ownCapitalPoint?.effectiveMarginCost ?? expenseEntry.marginCost) || 0
    : displayEffectiveMarginCost(
        expenseEntry,
        ownCapitalValue,
        stockCostBasis,
        ownCapitalPoint?.effectiveMarginCost,
        month.month,
      );
  const grossIncome = forecastView
    ? (ownCapitalPoint?.income ?? targetIncomeDollar)
    : cashIncome(month);
  const income = forecastView ? grossIncome : grossIncome - effectiveMargin;
  const roc = prevOwnCapital > 0 ? (income / prevOwnCapital) * 100 : null;
  const ownCapitalCls = ownCapitalValue < 0 ? "sfe-neg" : "roi-strong";
  const expenses = expenseTotalForMonth(
    expenseEntry,
    forecastView ? undefined : month,
    effectiveMargin,
  );
  const monthTaxCashForEom = localLedgerAnalytics
    ? 0
    : taxCashOutflowForMonthKey(month.month);
  const ledgerTaxWithdrawn = localLedgerAnalytics
    ? Math.max(0, Number(ownCapitalPoint?.taxWithdrawn) || 0)
    : 0;
  const perTradeFees = localLedgerAnalytics
    ? 0
    : perTradeExpenseTotal(
        expenseEntry,
        forecastView
          ? Math.max(0, Math.round(Number(expenseEntry.tradeCount) || 0))
          : countTradesInMonth(month),
      );
  const stockLendingIncome = localLedgerAnalytics
    ? Number(ownCapitalPoint?.stockLendingIncome) || 0
    : 0;
  const plannedOwnCapital = plannedOwnCapitalEom({
    baseCapitalBom: prevOwnCapital,
    targetIncomeNet: targetIncomeDollar,
    cashAdded,
    withdrawn,
    stockLendingIncome,
    perTradeFees,
    taxCashOutflow: monthTaxCashForEom,
  });
  const planDelta = ownCapitalValue - plannedOwnCapital;
  const planDeltaCls = planDelta >= 0 ? "roi-strong" : "sfe-neg";
  const progressPct = forecastView
    ? targetIncomeDollar > 0
      ? 100
      : null
    : targetIncomeDollar > 0
      ? (income / targetIncomeDollar) * 100
      : null;
  const realizedPerfCls = rocPerfCls(roc, targetPct);
  const incomeCardCls = forecastView
    ? "sfe-month-card kpi sfe-total-income-kpi sfe-month-card-income sfe-month-card-combined sfe-forecast-tone"
    : "sfe-month-card kpi sfe-total-income-kpi sfe-month-card-income sfe-month-card-combined";
  const cardToneCls = forecastView ? " sfe-forecast-tone" : "";
  const realizedLabel = forecastView ? "Expected" : "Realized";
  const incomeLabel = forecastView ? "Income (forecast)" : "Income";
  const eomTaxTip = "IRS tax cash paid (estimated, balance-due, and extension)";
  const incomeTip = forecastView
    ? dataTipParagraphs(
        "Forecast income is the net target ROC on base capital (prior EOM) — the same after-margin basis as Realized.",
        `Own Capital deducts ${eomTaxTip} once. Predicted margin is shown on Cost; it is already inside this net target.`,
        "ROC still uses income before estimated tax.",
      )
    : localLedgerAnalytics
      ? dataTipParagraphs(
          "Monthly income = premiums (STO) − BTC + assignment P/L + stock-sale P/L − margin financing cost.",
          "Cross-month wheel legs: STO premium books in the open month; call assign P/L books in the assign/close month (option exp date). Opens and closes link only via History(L) ledger links (open↔close, wheel put↔call) — not FIFO.",
          "Long-option BTO/STC (non-wheel mistakes) appear in the day table but are excluded from this wheel-income total — matches RH options P/L excl. long round-trips.",
          "Call Assgn. P/L = (call strike − average share cost on the assign date) × shares, booked in the assign month (option exp date). Each put assign blends its strike into that average as it happens, alongside outright share buys — never FIFO.",
          `Own Capital also deducts ${eomTaxTip} once. ROC still uses income before estimated tax.`,
        )
      : dataTipParagraphs(
          "Monthly income = premiums (STO) − BTC/BTO + assignment P/L + stock-sale P/L − margin financing cost.",
          "Per-trade fees remain in Cost.",
          `Own Capital also deducts ${eomTaxTip} once. ROC still uses income before estimated tax.`,
        );
  const predictedTaxWd = Math.max(0, Number(predictedTaxWithdrawalsCache.get(month.month)) || 0);
  const ownCapitalTip = localLedgerAnalytics
    ? dataTipParagraphs(
        "Own Capital (EOM) = prior EOM + trading income + cash added − withdrawals (including Tax rows in History cash) − margin cost.",
        "Cash operations settle at month-end. Mark IRS ACH outflows as Tax in History(L); forecast tax rows are auto-created from the Tax tab for 2027+.",
        "Base Capital (BOM) is the prior month’s EOM — the ROC denominator.",
        "Plan is Base + target income (after margin) + cash − withdrawals − tax. The delta is Realized $ minus Target $.",
      )
    : dataTipParagraphs(
        `Own Capital (EOM) is the end-of-month balance: Base Capital + gross trading income + cash added − withdrawals − margin cost − per-trade fees − ${eomTaxTip}.`,
        "Cash operations (Added / Withdrawn) settle into this EOM — they do not change Base Capital (BOM) used for this month’s ROC.",
        "From 2027 onward, predicted tax paid from trading is included in Withdrawn (payment-log dates; default Mar 30) rather than a separate IRS tax-cash line.",
        "Base Capital (BOM), shown below, is the prior month’s EOM balance and is the denominator for Target and Realized ROC.",
        "Plan is Base + target income (after margin) + cash − withdrawals − per-trade fees − tax. Margin is already inside the target, so it is not subtracted again.",
        "Margin and tax cash are each counted once — do not also enter those IRS payments as a Cash withdrawal.",
      );
  const planHtml = forecastView
    ? `<span class="sfe-month-card-planned sfe-forecast-plan">Forecast EOM at target ROC</span>`
    : `<span class="sfe-month-card-planned" title="Plan EOM = Base + target income after margin + cash − withdrawals − fees − tax">Plan: ${fmtUsdWhole(plannedOwnCapital)} <span class="${planDeltaCls}">${fmtUsdSignedParen(planDelta)}</span></span>`;
  const taxCashLineHtml =
    localLedgerAnalytics && ledgerTaxWithdrawn > 0
      ? `<span class="sfe-month-card-tax-cash-line">Tax (cash ledger) <span class="sfe-neg">−${fmtUsdWhole(ledgerTaxWithdrawn)}</span></span>`
      : !forecastView && monthTaxCashForEom > 0 && predictedTaxWd <= 0 && !localLedgerAnalytics
        ? `<span class="sfe-month-card-tax-cash-line">IRS tax cash <span class="sfe-neg">−${fmtUsdWhole(monthTaxCashForEom)}</span></span>`
        : "";

  detail.innerHTML = `
    <div class="sfe-month-detail-groups">
      <div class="sfe-month-card-row sfe-month-card-row-shaded${forecastView ? " sfe-forecast-card-row" : ""}">
        <div class="${incomeCardCls}" data-sfe-month-income data-trading-income="${income}" data-estimated-tax-paid="0">
          <span class="kpi-k">${incomeLabel}<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(incomeTip)}">i</span></span>
          <div class="sfe-income-kpi-row">
            <span class="kpi-v ${incomeHeadlineCls(income)}">${fmtUsdWhole(income)}</span>
            ${
              forecastView
                ? renderForecastIncomeDetails(effectiveMargin)
                : renderMonthIncomeDetails(
                    month,
                    effectiveMargin,
                    !isPastAnalyticsMonthKey(month.month),
                  )
            }
          </div>
          ${renderTargetProgressIndicator(progressPct)}
        </div>
        <div class="sfe-month-card kpi sfe-month-card-own-capital${cardToneCls}">
          <span class="kpi-k">Own Capital<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(ownCapitalTip)}">i</span></span>
          <span class="kpi-v ${ownCapitalCls}">${fmtUsdWhole(ownCapitalValue)}</span>
          <span class="sfe-month-card-base-line">
            <span class="sfe-month-card-base-label">Base Capital (BOM)</span>
            <span class="sfe-month-card-base-capital-val">${prevOwnCapital > 0 ? fmtUsdWhole(prevOwnCapital) : "—"}</span>
          </span>
          ${taxCashLineHtml}
          ${planHtml}
        </div>
        <div class="sfe-month-card kpi sfe-month-card-editable sfe-month-card-return-on-capital${cardToneCls}">
          <span class="kpi-k">Return On Own Capital<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(dataTipParagraphs(
            "Target and Realized use the same basis: net monthly income after margin ÷ base capital (prior EOM).",
            "Target $ = base × target %. Realized $ is this month’s income after margin.",
            "Plan EOM uses that same target $ — margin is not deducted a second time.",
          ))}">i</span></span>
          <div class="sfe-month-roc-lines">
            <div class="sfe-month-roc-line">
              <span class="sfe-month-roc-line-k">Target</span>
              <div class="sfe-month-roc-row sfe-month-roc-target-row">
                <span class="sfe-month-roc-target-pct-wrap">
                  <span class="kpi-v sfe-month-card-static-val sfe-target-roc-display">${fmtRocPct(targetPct)}</span>
                  ${monthCardEditBtn(
                    "Edit target return",
                    false,
                    `data-sfe-open-target-roc-modal data-base-capital="${prevOwnCapital}"`,
                    "sfe-target-roc-edit-btn",
                  )}
                </span>
                <span class="kpi-v sfe-month-roc-amt">${targetIncomeDollar ? fmtUsdWhole(targetIncomeDollar) : "—"}</span>
              </div>
            </div>
            <div class="sfe-month-roc-line">
              <span class="sfe-month-roc-line-k">${realizedLabel}</span>
              <div class="sfe-month-roc-row">
                <span class="kpi-v sfe-month-roc-pct${realizedPerfCls ? ` ${realizedPerfCls}` : ""}">${roc != null ? fmtRocPct(roc) : "—"}</span>
                <span class="kpi-v sfe-month-roc-amt${realizedPerfCls ? ` ${realizedPerfCls}` : ""}">${roc != null ? fmtUsdWhole(income) : "—"}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="sfe-month-card kpi sfe-month-card-editable sfe-month-card-cash-operations${cardToneCls}">
          <span class="kpi-k">Cash<span class="hint" tabindex="0" role="img" data-tip="${escapeHtml(
            dataTipParagraphs(
              "Cash deposited or withdrawn during this month. Both settle at month-end to Own Capital (EOM) and do not change this month's ROC base.",
              "From 2027 onward, forecast months may include predicted tax paid from the trading account as withdrawals (dates mirrored from Tax payment logs; default Mar 30).",
            ),
          )}">i</span></span>
          <div class="sfe-month-roc-lines">
            <div class="sfe-month-roc-line">
              <span class="sfe-month-roc-line-k">Added</span>
              <div class="sfe-month-card-field" data-sfe-month-field="cash-added">
                <div class="sfe-month-card-field-view">
                  <span class="kpi-v sfe-month-card-static-val">${fmtUsdWhole(ownCapitalPoint?.cashAdded ?? 0)}</span>
                  ${monthCardEditBtn("Edit cash added", readOnlyOwnCapitalEdits)}
                </div>
                <div class="sfe-month-card-field-edit" hidden>
                  <div class="sfe-month-card-prefix-row">
                    <span class="sfe-month-card-input-prefix">$</span>
                    <input type="number" step="0.01" class="sfe-month-card-input sfe-own-capital-cash-added" value="${
                      ownCapitalPoint?.cashAdded ?? 0
                    }" />
                  </div>
                </div>
              </div>
            </div>
            <div class="sfe-month-roc-line">
              <span class="sfe-month-roc-line-k">Withdrawn</span>
              <div class="sfe-month-card-field" data-sfe-month-field="withdrawals">
                <div class="sfe-month-card-field-view">
                  <span class="kpi-v sfe-month-card-static-val">${fmtUsdWhole(ownCapitalPoint?.withdrawn ?? 0)}</span>
                  ${monthCardEditBtn("Edit withdrawals", readOnlyOwnCapitalEdits)}
                </div>
                <div class="sfe-month-card-field-edit" hidden>
                  <div class="sfe-month-card-prefix-row">
                    <span class="sfe-month-card-input-prefix">$</span>
                    <input type="number" step="0.01" class="sfe-month-card-input sfe-own-capital-withdrawn" value="${
                      ownCapitalPoint?.withdrawn ?? 0
                    }" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="sfe-month-card kpi sfe-month-card-editable sfe-month-card-expenses sfe-month-card-margin${cardToneCls}" data-sfe-month-margin>
          ${renderMonthCostCardHeader(expenses)}
          ${renderExpensesSubsections(
            expenseEntry,
            forecastView ? undefined : month,
            !readOnlyOwnCapitalEdits,
            effectiveMargin,
            stockCostBasis - ownCapitalValue,
            month.month,
            {
              hidePerTrade: localLedgerAnalytics,
              useRhMarginHistory:
                !localLedgerAnalytics || monthUsesAutomaticMarginForecast(month.month),
            },
          )}
        </div>
        <div class="sfe-month-card kpi sfe-month-card-taxes${cardToneCls}" data-sfe-month-taxes>
          ${renderMonthTaxesCardContent(null, monthLabel(month.month))}
        </div>
      </div>
    </div>
  `;
  syncMonthDetailScopeBadge(
    detail,
    analyticsMonthScopeBadge(month.month, forecastView),
  );
  void syncMonthTaxesCard(detail, month.month);
  const marginCard = detail.querySelector<HTMLElement>("[data-sfe-month-margin]");
  if (marginCard) bindMarginUsedHistoryButton(marginCard);
  if (forecastView) {
    const note = document.createElement("p");
    note.className = "sfe-forecast-empty muted small";
    note.textContent = "Forecast from target ROC — no transactions yet.";
    detail.appendChild(note);
  } else {
    detail.appendChild(buildIncomeDaysHost(month, expandIncomeDays, localLedgerAnalytics));
  }

  return detail;
}

/** Every day on record as one bucket, for the All years view. */
function mergeIncomeMonths(months: IncomeMonthBucket[]): IncomeMonthBucket {
  const merged: IncomeMonthBucket = {
    month: "all",
    label: "Up to current month",
    premiums: 0,
    stcCredits: 0,
    assignIncome: 0,
    stockSellIncome: 0,
    btcCost: 0,
    btoCost: 0,
    total: 0,
    deployedCapital: 0,
    days: [],
  };
  for (const month of months) {
    merged.premiums += month.premiums;
    merged.stcCredits += month.stcCredits;
    merged.assignIncome += month.assignIncome;
    merged.stockSellIncome += month.stockSellIncome;
    merged.btcCost += month.btcCost;
    merged.btoCost += month.btoCost;
    merged.total += month.total;
    merged.deployedCapital += month.deployedCapital;
    merged.days.push(...month.days);
  }
  merged.days.sort((a, b) => a.date.localeCompare(b.date));
  return merged;
}

/** Cheap identity for the merged view, so idle refreshes can skip a rebuild. */
function allYearsIncomeSignature(merged: IncomeMonthBucket): string {
  return `${merged.days.length}|${incomeHistoryLineCount(merged)}|${countTradesInMonth(merged)}|${Math.round(cashIncome(merged) * 100)}`;
}

/** Cheap identity for a single-month detail card — skip rebuild when unchanged. */
function monthIncomeDetailSignature(
  month: IncomeMonthBucket,
  ownCapitalPoint: OwnCapitalPoint | undefined,
  forecastView: boolean,
): string {
  const income = forecastView
    ? Math.round((ownCapitalPoint?.income ?? 0) * 100)
    : Math.round(cashIncome(month) * 100);
  return `${month.month}|${month.days.length}|${incomeHistoryLineCount(month)}|${countTradesInMonth(month)}|${income}|${Math.round((ownCapitalPoint?.ownCapital ?? 0) * 100)}`;
}

/** Keep an All years detail card in place unless the underlying data moved. */
function patchAllYearsIncomeDetail(
  detailHost: HTMLElement,
  months: IncomeMonthBucket[],
  expandIncomeDays: boolean,
  localLedgerAnalytics: boolean,
): void {
  const existing = detailHost.querySelector<HTMLElement>(".sfe-income-all-years-detail");
  if (existing?.dataset.signature === allYearsIncomeSignature(mergeIncomeMonths(months))) return;
  detailHost.replaceChildren(
    buildAllYearsIncomeDetail(months, expandIncomeDays, localLedgerAnalytics),
  );
}

/**
 * All years view: every trade on record, with the income totals that go with
 * them. Own capital, ROC and margin are month-relative, so they are left to the
 * per-month cards rather than restated against a span of years.
 */
function buildAllYearsIncomeDetail(
  months: IncomeMonthBucket[],
  expandIncomeDays: boolean,
  localLedgerAnalytics: boolean,
): HTMLElement {
  const merged = mergeIncomeMonths(months);
  const detail = document.createElement("div");
  detail.className = "sfe-income-month-detail sfe-income-all-years-detail";
  detail.dataset.month = merged.month;
  detail.dataset.signature = allYearsIncomeSignature(merged);

  const tradeCount = countTradesInMonth(merged);
  const monthsWithActivity = months.filter(monthHasTradingActivity).length;
  const header = document.createElement("div");
  header.className = "sfe-income-all-years-head";
  header.innerHTML =
    `<span class="sfe-income-all-years-title">Up to current month</span>` +
    `<span class="muted small">${tradeCount.toLocaleString("en-US")} trades across ` +
    `${monthsWithActivity.toLocaleString("en-US")} months · income ${fmtUsd(cashIncome(merged))}</span>`;
  detail.appendChild(header);
  detail.appendChild(buildIncomeDaysHost(merged, expandIncomeDays, localLedgerAnalytics, header));
  return detail;
}

function ensureSelectedIncomeMonth(
  months: IncomeMonthBucket[],
  selectedYear: number | "all",
  forecastEnabled: boolean,
  localLedgerAnalytics = false,
): void {
  // All spans years, so no single month can represent it.
  if (selectedYear === "all") {
    selectedIncomeMonthKey = null;
    return;
  }
  const displayMonths = withForecastMonths(months, selectedYear, forecastEnabled);
  if (!displayMonths.length) return;
  const currentMonth = currentMonthKey();
  const selectable = displayMonths.filter(
    (month) =>
      !isInactiveLocalLedgerMonthChip(
        month.month,
        month,
        localLedgerAnalytics,
        forecastEnabled,
        selectedYear,
      ),
  );
  const validKeys = new Set(selectable.map((month) => month.month));
  if (!selectedIncomeMonthKey || !validKeys.has(selectedIncomeMonthKey)) {
    selectedIncomeMonthKey = validKeys.has(currentMonth)
      ? currentMonth
      : (selectable[0]?.month ?? displayMonths[0].month);
  }
}

function renderMonthlyIncomeSection(
  months: IncomeMonthBucket[],
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  ledger: Record<string, OwnCapitalEntry>,
  onSaveOwnCapital: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  onPatchOwnCapitalLedger: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  selectedYear: number | "all",
  forecastEnabled: boolean,
  projectedTimeline: Map<string, OwnCapitalPoint>,
  onSelectMonth: (monthKey: string) => void,
  onMonthDetailChange: () => void,
  expandIncomeDays = false,
  stockCostBasis = 0,
  readOnlyOwnCapitalEdits = false,
  localLedgerAnalytics = false,
  onTargetRocSaved: () => void | Promise<void> = () => {},
): HTMLElement {
  const host = document.createElement("div");
  host.className = "sfe-income-months";
  host.setAttribute("data-sfe-injected", "true");

  const allYears = selectedYear === "all";
  host.dataset.allYears = allYears ? "true" : "false";
  const displayMonths = withForecastMonths(months, selectedYear, forecastEnabled);

  if (!displayMonths.length) {
    host.innerHTML = `<p class="sfe-income-empty">No income events in this scope.</p>`;
    return host;
  }

  ensureSelectedIncomeMonth(months, selectedYear, forecastEnabled, localLedgerAnalytics);

  const selectorRow = document.createElement("div");
  selectorRow.className = "sfe-income-month-selector";
  for (const month of displayMonths) {
    const inactive =
      allYears ||
      isInactiveLocalLedgerMonthChip(
        month.month,
        month,
        localLedgerAnalytics,
        forecastEnabled,
        selectedYear,
      );
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = monthChipClassName(
      month.month,
      month.month === selectedIncomeMonthKey,
      inactive,
    );
    chip.dataset.month = month.month;
    chip.textContent = month.label;
    if (allYears) {
      chip.disabled = true;
      chip.setAttribute("aria-disabled", "true");
      chip.setAttribute("data-tip", "Pick a single year to open a month");
      chip.setAttribute("aria-label", `${month.label} (month view needs a year)`);
    } else if (inactive) {
      chip.disabled = true;
      chip.setAttribute("aria-disabled", "true");
      chip.setAttribute("data-tip", "No trades this month");
      chip.setAttribute("aria-label", `${month.label} (no trades)`);
    } else {
      const tip = monthChipTipLabel(month.month, forecastEnabled, selectedYear);
      if (tip) {
        chip.setAttribute("data-tip", tip);
        chip.setAttribute("aria-label", `${month.label} (${tip})`);
      }
    }
    selectorRow.appendChild(chip);
  }

  const detailHost = document.createElement("div");
  detailHost.className = "sfe-income-month-detail-host";

  const renderSelected = () => {
    if (allYears) {
      detailHost.replaceChildren(
        buildAllYearsIncomeDetail(displayMonths, expandIncomeDays, localLedgerAnalytics),
      );
      for (const chip of selectorRow.querySelectorAll<HTMLElement>(".sfe-income-month-chip")) {
        chip.classList.remove("active");
      }
      return;
    }
    const month =
      displayMonths.find((m) => m.month === selectedIncomeMonthKey) ?? displayMonths[0];
    const forecastView =
      forecastEnabled &&
      selectedIncomeMonthKey != null &&
      isForecastMonthChip(selectedIncomeMonthKey, forecastEnabled, selectedYear);
    detailHost.replaceChildren(
      buildMonthDetail(
        month,
        ownCapitalByMonth,
        ledger,
        forecastView,
        projectedTimeline,
        expandIncomeDays,
        stockCostBasis,
        readOnlyOwnCapitalEdits,
        localLedgerAnalytics,
      ),
    );
    for (const chip of selectorRow.querySelectorAll<HTMLElement>(".sfe-income-month-chip")) {
      chip.classList.toggle("active", chip.dataset.month === month.month);
    }
  };
  renderSelected();
  wireTargetRocEditModal(detailHost, ledger, onTargetRocSaved);
  wireMarginPredToggle(detailHost, onPatchOwnCapitalLedger);
  if (!readOnlyOwnCapitalEdits) {
    wireMonthCardFieldEditors(
      detailHost,
      renderSelected,
      onSaveOwnCapital,
      onPatchOwnCapitalLedger,
      ledger,
      onTargetRocSaved,
    );
  }

  selectorRow.addEventListener("click", (event) => {
    const chip = (event.target as HTMLElement).closest<HTMLElement>(".sfe-income-month-chip");
    const month = chip?.dataset.month;
    if (!month || month === selectedIncomeMonthKey) return;
    if (chip?.classList.contains("sfe-month-chip--inactive") || chip?.matches(":disabled")) return;
    selectedIncomeMonthKey = month;
    onSelectMonth(month);
  });

  host.appendChild(selectorRow);
  host.appendChild(detailHost);

  return host;
}

/** `data-year` value for the All chip; every other chip carries a real year. */
const ANALYTICS_ALL_YEARS = "all";

/** Calendar scope for a year chip — All means every year, which reads as null. */
function incomeCalendarScopeYear(selectedYear: number | "all"): number | null {
  return selectedYear === "all" ? null : selectedYear;
}

function analyticsAllYearChipHtml(selected: number | "all"): string {
  return (
    `<button type="button" class="${yearChipClassName("all", selected === "all")}"` +
    ` data-year="${ANALYTICS_ALL_YEARS}" data-tip="Through the current month. Pick a year to open a month."` +
    ` aria-label="Up to current month">All</button>`
  );
}

/** Self-contained year selector — no dependency on native Analytics ever
 * having been opened. Rebuilt when the year list changes; otherwise patched
 * in place so feed refreshes do not remount the row. */
function renderYearSelector(
  years: number[],
  selected: number | "all",
  onSelect: (year: number | "all") => void,
  forecastEnabled: boolean,
  onForecastChange: (enabled: boolean) => void,
  tickerFilter?: { value: string; onChange: (value: string) => void },
  typeFilter?: { value: AnalyticsTypeFilter; onChange: (value: AnalyticsTypeFilter) => void },
): HTMLElement {
  const row = document.createElement("div");
  row.className = "sfe-income-year-row";
  row.setAttribute("data-sfe-injected", "true");

  const yearChips = [analyticsAllYearChipHtml(selected)]
    .concat(
      years.map((year) => {
        const tip = yearChipTipLabel(year);
        const tipAttr = tip ? ` data-tip="${tip}" aria-label="${year} (${tip})"` : "";
        return `<button type="button" class="${yearChipClassName(year, year === selected)}" data-year="${year}"${tipAttr}>${year}</button>`;
      }),
    )
    .join("");
  const tickerField = tickerFilter
    ? `<div class="sfe-income-ticker-filter${tickerFilter.value ? " is-filtered" : ""}">` +
      `<label class="muted small" for="${ANALYTICS_TICKER_FILTER_INPUT_ID}">Ticker</label>` +
      `<input type="text" id="${ANALYTICS_TICKER_FILTER_INPUT_ID}" class="filter-input-compact"` +
      ` placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escapeHtml(tickerFilter.value)}" />` +
      `</div>`
    : "";
  const typeField = typeFilter
    ? `<div class="sfe-income-type-filter">` +
      `<label class="muted small" for="${ANALYTICS_TYPE_FILTER_INPUT_ID}">Type</label>` +
      `<select id="${ANALYTICS_TYPE_FILTER_INPUT_ID}" class="filter-input-compact">` +
      ANALYTICS_TYPE_FILTER_OPTIONS.map(
        (option) =>
          `<option value="${option.value}"${option.value === typeFilter.value ? " selected" : ""}>` +
          `${option.label}</option>`,
      ).join("") +
      `</select>` +
      `</div>`
    : "";
  row.innerHTML =
    tickerField +
    `<span class="muted small sfe-income-year-label">Year</span>` +
    `<div class="year-chips sfe-income-year-chips">${yearChips}</div>` +
    `<div class="sfe-income-year-trailing">` +
    typeField +
    `<label class="sfe-toggle sfe-income-forecast-toggle">` +
    `<input type="checkbox" class="sfe-income-forecast-input"${forecastEnabled ? " checked" : ""} />` +
    `Forecast</label>` +
    `<div class="sfe-income-year-actions"></div>` +
    `</div>`;

  row.addEventListener("click", (event) => {
    const chip = (event.target as HTMLElement).closest<HTMLElement>(".year-chip");
    if (!chip || chip.hasAttribute("disabled")) return;
    const year = chip.dataset.year;
    if (!year) return;
    onSelect(year === ANALYTICS_ALL_YEARS ? "all" : Number(year));
  });

  const forecastInput = row.querySelector<HTMLInputElement>(".sfe-income-forecast-input");
  forecastInput?.addEventListener("change", () => {
    onForecastChange(forecastInput.checked);
  });

  if (tickerFilter) {
    const input = row.querySelector<HTMLInputElement>(`#${ANALYTICS_TICKER_FILTER_INPUT_ID}`);
    input?.addEventListener("input", () => {
      tickerFilter.onChange(sanitizeTickerFilterValue(input.value).toUpperCase());
    });
    guardTickerFilterInputs(row);
  }

  if (typeFilter) {
    const select = row.querySelector<HTMLSelectElement>(`#${ANALYTICS_TYPE_FILTER_INPUT_ID}`);
    select?.addEventListener("change", () => {
      typeFilter.onChange(select.value as AnalyticsTypeFilter);
    });
  }

  return row;
}

/** Patch an existing year row when the year list is unchanged. */
function softSyncYearSelector(
  row: HTMLElement,
  years: number[],
  selected: number | "all",
  forecastEnabled: boolean,
  wantTickerFilter = false,
): boolean {
  // A row built for the other Analytics surface lacks the ticker/type fields,
  // so it has to be rebuilt rather than patched.
  const hasTickerFilter = Boolean(row.querySelector(".sfe-income-ticker-filter"));
  if (hasTickerFilter !== wantTickerFilter) return false;
  const typeSelect = row.querySelector<HTMLSelectElement>(
    `#${ANALYTICS_TYPE_FILTER_INPUT_ID}`,
  );
  if (typeSelect && typeSelect.value !== analyticsTypeFilter) {
    typeSelect.value = analyticsTypeFilter;
  }
  // Patched rows keep their old input node, so pull it back to the saved filter
  // unless the user is mid-edit in it.
  const tickerInput = row.querySelector<HTMLInputElement>(
    `#${ANALYTICS_TICKER_FILTER_INPUT_ID}`,
  );
  if (
    tickerInput &&
    document.activeElement !== tickerInput &&
    tickerInput.value !== analyticsTickerFilter
  ) {
    tickerInput.value = analyticsTickerFilter;
  }
  tickerInput
    ?.closest(".sfe-income-ticker-filter")
    ?.classList.toggle("is-filtered", Boolean(analyticsTickerFilter));
  const chips = [...row.querySelectorAll<HTMLElement>(".sfe-income-year-chips [data-year]")];
  const wanted: Array<number | "all"> = ["all", ...years];
  if (
    chips.length !== wanted.length ||
    chips.some((chip, index) => {
      const key = chip.dataset.year === ANALYTICS_ALL_YEARS ? "all" : Number(chip.dataset.year);
      return key !== wanted[index];
    })
  ) {
    return false;
  }
  for (const chip of chips) {
    if (chip.dataset.year === ANALYTICS_ALL_YEARS) {
      chip.className = yearChipClassName("all", selected === "all");
      continue;
    }
    const year = Number(chip.dataset.year);
    chip.className = yearChipClassName(year, year === selected);
    const tip = yearChipTipLabel(year);
    if (tip) {
      chip.setAttribute("data-tip", tip);
      chip.setAttribute("aria-label", `${year} (${tip})`);
    } else {
      chip.removeAttribute("data-tip");
      chip.removeAttribute("aria-label");
    }
  }
  const forecastInput = row.querySelector<HTMLInputElement>(".sfe-income-forecast-input");
  if (forecastInput && forecastInput.checked !== forecastEnabled) {
    forecastInput.checked = forecastEnabled;
  }
  return true;
}

/**
 * Patch month chips + selected-month detail when the chip key list matches.
 * Returns false when the host must be fully replaced.
 */
function softPatchMonthlyIncomeSection(
  host: HTMLElement,
  months: IncomeMonthBucket[],
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  ledger: Record<string, OwnCapitalEntry>,
  onSaveOwnCapital: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  onPatchOwnCapitalLedger: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  selectedYear: number | "all",
  forecastEnabled: boolean,
  projectedTimeline: Map<string, OwnCapitalPoint>,
  expandIncomeDays: boolean,
  stockCostBasis: number,
  readOnlyOwnCapitalEdits: boolean,
  localLedgerAnalytics: boolean,
  onTargetRocSaved: () => void | Promise<void> = () => {},
): boolean {
  const selectorRow = host.querySelector<HTMLElement>(".sfe-income-month-selector");
  const detailHost = host.querySelector<HTMLElement>(".sfe-income-month-detail-host");
  if (!selectorRow || !detailHost) return false;

  // Switching into or out of All swaps the whole detail card, so rebuild.
  const allYears = selectedYear === "all";
  if ((host.dataset.allYears === "true") !== allYears) return false;

  const displayMonths = withForecastMonths(months, selectedYear, forecastEnabled);
  if (!displayMonths.length) return false;

  ensureSelectedIncomeMonth(months, selectedYear, forecastEnabled, localLedgerAnalytics);

  const chips = [
    ...selectorRow.querySelectorAll<HTMLButtonElement>(".sfe-income-month-chip[data-month]"),
  ];
  if (
    chips.length !== displayMonths.length ||
    chips.some((chip, index) => chip.dataset.month !== displayMonths[index]?.month)
  ) {
    return false;
  }

  for (let i = 0; i < displayMonths.length; i += 1) {
    const month = displayMonths[i]!;
    const chip = chips[i]!;
    const inactive =
      allYears ||
      isInactiveLocalLedgerMonthChip(
        month.month,
        month,
        localLedgerAnalytics,
        forecastEnabled,
        selectedYear,
      );
    chip.className = monthChipClassName(
      month.month,
      month.month === selectedIncomeMonthKey,
      inactive,
    );
    chip.textContent = month.label;
    chip.disabled = inactive;
    if (allYears) {
      chip.setAttribute("aria-disabled", "true");
      chip.setAttribute("data-tip", "Pick a single year to open a month");
      chip.setAttribute("aria-label", `${month.label} (month view needs a year)`);
    } else if (inactive) {
      chip.setAttribute("aria-disabled", "true");
      chip.setAttribute("data-tip", "No trades this month");
      chip.setAttribute("aria-label", `${month.label} (no trades)`);
    } else {
      chip.removeAttribute("aria-disabled");
      const tip = monthChipTipLabel(month.month, forecastEnabled, selectedYear);
      if (tip) {
        chip.setAttribute("data-tip", tip);
        chip.setAttribute("aria-label", `${month.label} (${tip})`);
      } else {
        chip.removeAttribute("data-tip");
        chip.removeAttribute("aria-label");
      }
    }
  }

  if (allYears) {
    patchAllYearsIncomeDetail(detailHost, displayMonths, expandIncomeDays, localLedgerAnalytics);
    return true;
  }

  const month =
    displayMonths.find((m) => m.month === selectedIncomeMonthKey) ?? displayMonths[0]!;
  const forecastView =
    forecastEnabled &&
    selectedIncomeMonthKey != null &&
    isForecastMonthChip(selectedIncomeMonthKey, forecastEnabled, selectedYear);
  const timeline = forecastView ? projectedTimeline : ownCapitalByMonth;
  const ownCapitalPoint = timeline.get(month.month);
  const signature = monthIncomeDetailSignature(month, ownCapitalPoint, forecastView);
  const existingDetail = detailHost.querySelector<HTMLElement>(".sfe-income-month-detail");
  const skipDetailRebuild =
    isUserActiveInPanel(detailHost) ||
    (existingDetail?.dataset.signature === signature &&
      existingDetail.dataset.month === month.month);
  if (!skipDetailRebuild) {
    const detail = buildMonthDetail(
      month,
      ownCapitalByMonth,
      ledger,
      forecastView,
      projectedTimeline,
      expandIncomeDays,
      stockCostBasis,
      readOnlyOwnCapitalEdits,
      localLedgerAnalytics,
    );
    detail.dataset.signature = signature;
    detailHost.replaceChildren(detail);
  }
  if (!readOnlyOwnCapitalEdits) {
    wireMonthCardFieldEditors(
      detailHost,
      () => {
        softPatchMonthlyIncomeSection(
          host,
          months,
          ownCapitalByMonth,
          ledger,
          onSaveOwnCapital,
          onPatchOwnCapitalLedger,
          selectedYear,
          forecastEnabled,
          projectedTimeline,
          expandIncomeDays,
          stockCostBasis,
          readOnlyOwnCapitalEdits,
          localLedgerAnalytics,
          onTargetRocSaved,
        );
      },
      onSaveOwnCapital,
      onPatchOwnCapitalLedger,
      ledger,
      onTargetRocSaved,
    );
  }
  wireTargetRocEditModal(detailHost, ledger, onTargetRocSaved);
  return true;
}

function renderAnalyticsPlusBody(
  panel: HTMLElement,
  breakdown: IncomeBreakdown,
  months: IncomeMonthBucket[],
  /** Full ticker/type-filtered history — Total Income / Cash window. */
  incomeHistoryMonths: IncomeMonthBucket[],
  stockSummary: StockOpenSummary | undefined,
  ownCapitalByMonth: Map<string, OwnCapitalPoint>,
  years: number[],
  selectedYear: number | "all",
  onSelectYear: (year: number | "all") => void,
  forecastEnabled: boolean,
  onForecastChange: (enabled: boolean) => void,
  onSaveOwnCapital: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  onPatchOwnCapitalLedger: (month: string, entry: Partial<OwnCapitalEntry>) => void,
  ledger: Record<string, OwnCapitalEntry>,
  allMonthsByKey: Map<string, IncomeMonthBucket>,
  onSelectMonth: (monthKey: string) => void,
  onMonthDetailChange: () => void,
  expandIncomeDays = false,
  readOnlyOwnCapitalEdits = false,
  localLedgerAnalytics = false,
  preferSoft = false,
  onTickerFilterChange: (value: string) => void = () => {},
  onTypeFilterChange: (value: AnalyticsTypeFilter) => void = () => {},
  onTargetRocSaved: () => void | Promise<void> = () => {},
): void {
  const body = panel.querySelector(".sfe-analytics-plus-body");
  if (!(body instanceof HTMLElement)) return;

  const year = normalizeAnalyticsYear(selectedYear, years);

  ensureSelectedIncomeMonth(months, year, forecastEnabled, localLedgerAnalytics);

  const selectedMonth = selectedIncomeMonthKey;
  const isForecastView =
    selectedMonth != null &&
    forecastEnabled &&
    isForecastMonthChip(selectedMonth, forecastEnabled, year);
  const scopeYear = year;
  const projectedTimeline =
    isForecastView && selectedMonth
      ? ensureProjectedOwnCapitalTimeline(
          ownCapitalByMonth,
          ledger,
          selectedMonth,
          stockSummary?.cost_basis ?? 0,
          localLedgerAnalytics ? undefined : predictedTaxWithdrawalsCache,
          localLedgerAnalytics,
        )
      : ownCapitalByMonth;
  const forecastTotals =
    isForecastView && selectedMonth
      ? totalsThroughMonth(
          months,
          projectedTimeline,
          selectedMonth,
          incomeCalendarScopeYear(scopeYear),
          ledger,
          allMonthsByKey,
        )
      : null;
  const cumulativeExpenseTotals =
    forecastTotals?.expenses ??
    aggregateExpensesThroughMonth(ledger, allMonthsByKey, selectedMonth, ownCapitalByMonth);

  body.classList.toggle("sfe-forecast-scope-active", isForecastView);

  const incomeDisplay = isForecastView
    ? null
    : displayIncomeBreakdown(
        breakdown,
        incomeHistoryMonths,
        selectedMonth,
        year,
        forecastEnabled,
      );

  const existingYearRow = body.querySelector<HTMLElement>(".sfe-income-year-row");
  const existingReset = existingYearRow?.querySelector<HTMLButtonElement>(".sfe-reset-filters");
  let yearRow: HTMLElement;
  if (
    preferSoft &&
    existingYearRow &&
    softSyncYearSelector(existingYearRow, years, year, forecastEnabled, localLedgerAnalytics)
  ) {
    yearRow = existingYearRow;
  } else {
    yearRow = renderYearSelector(
      years,
      year,
      onSelectYear,
      forecastEnabled,
      onForecastChange,
      localLedgerAnalytics
        ? {
            value: analyticsTickerFilter,
            onChange: onTickerFilterChange,
          }
        : undefined,
      localLedgerAnalytics
        ? {
            value: analyticsTypeFilter,
            onChange: onTypeFilterChange,
          }
        : undefined,
    );
    if (existingYearRow) {
      existingYearRow.replaceWith(yearRow);
    } else {
      body.prepend(yearRow);
    }
  }
  const actionsHost = yearRow.querySelector(".sfe-income-year-actions");
  if (existingReset && actionsHost && !actionsHost.contains(existingReset)) {
    actionsHost.appendChild(existingReset);
  }
  syncResetButtons();
  syncAnalyticsIncomeColumnPicker(
    body.closest(".sfe-analytics-plus-panel") ?? body,
    onMonthDetailChange,
    localLedgerAnalytics,
  );

  const { kpiHost, stockKpiHost } = ensureTotalPerformanceSection(body, yearRow);
  syncTotalPerformanceScopeBadge(
    body,
    analyticsPerformanceScopeBadge(selectedMonth, year, isForecastView),
  );
  syncTotalPerformanceTaxForecastNote(body, year);
  kpiHost.classList.toggle("sfe-forecast-kpi-row", isForecastView);

  upsertKpiCard(
    kpiHost,
    TOTAL_INCOME_KPI_ID,
    () =>
      renderTotalIncomeKpi(
        incomeDisplay?.breakdown ?? breakdown,
        incomeDisplay?.scopeLabel,
        cumulativeExpenseTotals.margin,
      ),
    (card) => {
      // Always rebuild HTML so Year ↔ All soft paints cannot leave stale month totals.
      const shown =
        isForecastView && forecastTotals
          ? {
              premiums: 0,
              stcCredits: 0,
              assignIncome: 0,
              stockSellIncome: 0,
              btcCost: 0,
              btoCost: 0,
              total: forecastTotals.income,
            }
          : (incomeDisplay?.breakdown ?? breakdown);
      const scopeLabel =
        isForecastView && selectedMonth
          ? `through ${monthLabel(selectedMonth)}${incomeFilterScopeSuffix()}`
          : incomeDisplay?.scopeLabel;
      const fresh = renderTotalIncomeKpi(shown, scopeLabel, cumulativeExpenseTotals.margin);
      card.className = fresh.className;
      card.innerHTML = fresh.innerHTML;
      card.dataset.grossIncome = fresh.dataset.grossIncome ?? "";
      card.dataset.marginCost = fresh.dataset.marginCost ?? "";
      card.classList.add(TOTAL_INCOME_KPI_CLASS);
      applyForecastKpiTone(card, isForecastView);
      if (forecastTotals && selectedMonth) {
        updateForecastIncomeKpi(
          card,
          forecastTotals.income,
          monthLabel(selectedMonth),
          cumulativeExpenseTotals.margin,
        );
      }
    },
  );

  /** Own capital EOM for the selected month chip — shared by KPI row and forecast margin. */
  const selectedOwnCapitalEom = selectedMonthOwnCapitalEom(
    selectedMonth,
    isForecastView,
    ownCapitalByMonth,
    projectedTimeline,
    forecastEnabled,
  );

  // Own capital / Withdrawals / Total cash added ride in the same top row as
  // Total Income (own capital first) rather than a separate section
  // further down the page.
  if (ownCapitalByMonth.size || localLedgerAnalytics) {
    const ownCapitalTip = dataTipParagraphs(
      "End-of-month own capital for the selected month: prior EOM + trading income + cash added − withdrawals − expenses (margin + per-trade fees).",
      cumulativePeriodTip(isForecastView),
    );
    const expensesTip = dataTipParagraphs(
      "Margin interest plus per-trade fees (total cost), deducted from Own Capital at month-end.",
      cumulativePeriodTip(isForecastView),
    );

    const { cashAdded: totalCashAdded, withdrawn: totalWithdrawn, taxWithdrawn } =
      cashFlowTotalsForKpi(ownCapitalByMonth, forecastTotals, selectedMonth);
    const cashTip = buildCashOpsTip(isForecastView, taxWithdrawn, totalWithdrawn);
    const latestOwnCapital = selectedOwnCapitalEom;
    const ownCapitalThroughMonth = isForecastView && selectedMonth
      ? selectedMonth
      : resolveOwnCapitalKpiMonth(selectedMonth, forecastEnabled, ownCapitalByMonth);
    const ownCapitalThroughLabel = monthLabel(ownCapitalThroughMonth);

    upsertKpiCard(
      kpiHost,
      OWN_CAPITAL_KPI_ID,
      () => renderOwnCapitalKpi(latestOwnCapital, ownCapitalThroughLabel),
      (card) => {
        applyForecastKpiTone(card, isForecastView);
        updateKpiHint(card, ownCapitalTip);
        const labelEl = card.querySelector(":scope > .kpi-k");
        if (labelEl?.firstChild) labelEl.firstChild.textContent = "Total Own Capital";
        const valueEl = card.querySelector(".kpi-v");
        if (valueEl) {
          valueEl.textContent = fmtUsdWhole(latestOwnCapital);
          valueEl.className = `kpi-v ${latestOwnCapital >= 0 ? "roi-strong" : "roi-loss"}`;
        }
        let periodEl = card.querySelector(".sfe-own-capital-period");
        if (!periodEl) {
          periodEl = document.createElement("span");
          periodEl.className = "sfe-kpi-period sfe-own-capital-period";
        }
        periodEl.textContent = `through ${ownCapitalThroughLabel} (EOM)`;
        if (valueEl) valueEl.before(periodEl);
      },
    );
    const existingCashFlow = kpiHost.querySelector(`#${CASH_ADDED_KPI_ID}`);
    if (existingCashFlow && !existingCashFlow.querySelector(".sfe-cash-withdrawn-line")) {
      existingCashFlow.remove();
    }
    upsertKpiCard(
      kpiHost,
      CASH_ADDED_KPI_ID,
      () => renderCashFlowKpi(totalCashAdded, totalWithdrawn, ownCapitalThroughLabel),
      (card) => {
        card.classList.add("sfe-cash-flow-kpi");
        applyForecastKpiTone(card, isForecastView);
        updateKpiHint(card, cashTip);
        const labelEl = card.querySelector(":scope > .kpi-k");
        if (labelEl?.firstChild) labelEl.firstChild.textContent = "Cash";
        let periodEl = card.querySelector<HTMLElement>(".sfe-cash-period");
        if (!periodEl) {
          periodEl = document.createElement("span");
          periodEl.className = "sfe-kpi-period sfe-cash-period";
          labelEl?.insertAdjacentElement("afterend", periodEl);
        }
        periodEl.textContent = `through ${ownCapitalThroughLabel}`;
        let addedLabelEl = card.querySelector(".sfe-cash-added-label");
        if (!addedLabelEl) {
          addedLabelEl = document.createElement("span");
          addedLabelEl.className = "sfe-cash-added-label";
          card.querySelector(".sfe-cash-added-total")?.before(addedLabelEl);
        }
        addedLabelEl.textContent = "Total Added";
        const addedEl = card.querySelector(".sfe-cash-added-total");
        if (addedEl) addedEl.textContent = fmtUsdWhole(totalCashAdded);
        const withdrawnEl = card.querySelector(".sfe-cash-withdrawn-value");
        if (withdrawnEl) {
          withdrawnEl.textContent = totalWithdrawn
            ? `-${fmtUsdWhole(totalWithdrawn)}`
            : fmtUsdWhole(0);
        }
      },
    );
    kpiHost.querySelector(`#${CASH_WITHDRAWN_KPI_ID}`)?.remove();

    upsertKpiCard(
      kpiHost,
      EXPENSES_KPI_ID,
      () => renderExpensesKpi(cumulativeExpenseTotals, ownCapitalThroughLabel),
      (card) => {
        applyForecastKpiTone(card, isForecastView);
        updateKpiHint(card, expensesTip);
        updateExpensesKpi(card, cumulativeExpenseTotals, ownCapitalThroughLabel);
      },
    );
  }

  void syncTaxesKpi(kpiHost, selectedMonth ?? currentMonthKey());

  if (stockSummary) {
    stockKpiHost.hidden = false;
    stockKpiHost.classList.toggle("sfe-forecast-dim", shouldDimStockKpis(selectedMonth));

    document.getElementById("sfe-stock-count-kpi")?.remove();
    document.getElementById("sfe-stock-shares-kpi")?.remove();

    const costBasisTip = localLedgerAnalytics
      ? "Own ledger: shares × strike avg cost (assignment lots use strike; option premiums excluded)."
      : "What you paid: shares × average buy price per ticker.";
    const marketValueTip = localLedgerAnalytics
      ? "Current value of own-ledger shares from Robinhood equity quotes."
      : "Current value of your shares from live stock quotes and chain marks.";
    const stockPlTip = localLedgerAnalytics
      ? "Unrealized P/L vs strike avg cost: RH market value − own-ledger cost basis (priced shares only)."
      : "Unrealized profit/loss on your open shares: market value − cost basis (priced shares only).";

    upsertKpiCard(
      stockKpiHost,
      STOCK_HOLDINGS_KPI_ID,
      () => renderStockHoldingsKpi(stockSummary),
      (card) => {
        const countEl = card.querySelector<HTMLElement>(".sfe-stock-holdings-count");
        const sharesEl = card.querySelector<HTMLElement>(".sfe-stock-holdings-shares");
        if (countEl) countEl.textContent = fmtNum(stockSummary.count);
        if (sharesEl) sharesEl.textContent = fmtNum(stockSummary.quantity);
      },
    );
    upsertKpiCard(
      stockKpiHost,
      STOCK_COST_BASIS_KPI_ID,
      () => renderStockCostBasisKpi(stockSummary, costBasisTip),
      (card) => {
        updateKpiHint(card, costBasisTip);
        const valueEl = card.querySelector(".kpi-v");
        if (valueEl) valueEl.textContent = fmtUsdWhole(stockSummary.cost_basis);
      },
    );
    upsertKpiCard(
      stockKpiHost,
      STOCK_MARKET_VALUE_KPI_ID,
      () => renderStockMarketValueKpi(stockSummary, marketValueTip),
      (card) => {
        updateKpiHint(card, marketValueTip);
        const valueEl = card.querySelector(".kpi-v");
        if (valueEl) valueEl.textContent = fmtUsdWhole(stockSummary.market_value);
      },
    );
    upsertKpiCard(
      stockKpiHost,
      STOCK_PL_KPI_ID,
      () => renderStockPlKpi(stockSummary, stockPlTip),
      (card) => {
        updateKpiHint(card, stockPlTip);
        const valueEl = card.querySelector(".kpi-v");
        if (valueEl) {
          valueEl.textContent = fmtUsdWhole(stockSummary.open_pl);
          valueEl.className = `kpi-v ${stockSummary.open_pl < 0 ? "roi-loss" : "roi-strong"}`;
        }
        const subEl = card.querySelector(".kpi-sub");
        const pct = fmtPct(stockSummary.open_pl_pct);
        const cls = stockSummary.open_pl < 0 ? "roi-loss" : "roi-strong";
        if (subEl) {
          subEl.textContent = pct;
          subEl.className = `kpi-sub ${cls}`;
        }
      },
    );
    document.getElementById(MARGIN_USED_KPI_ID)?.remove();
  } else {
    stockKpiHost.replaceChildren();
    stockKpiHost.hidden = true;
  }

  const incomeChartPoints = buildIncomeChartPoints(
    months,
    year,
    forecastEnabled,
    ownCapitalByMonth,
    ledger,
    stockSummary?.cost_basis ?? 0,
    localLedgerAnalytics,
  );
  const performanceBody = stockKpiHost.parentElement;
  if (performanceBody) {
    syncTotalPerformanceIncomeChart(
      performanceBody,
      stockKpiHost,
      incomeChartPoints,
      selectedMonth,
      year === "all" ? null : onSelectMonth,
    );
  }

  let monthsHost = body.querySelector<HTMLElement>(".sfe-income-months");
  const stockCostBasis = stockSummary?.cost_basis ?? 0;
  if (
    preferSoft &&
    monthsHost &&
    softPatchMonthlyIncomeSection(
      monthsHost,
      months,
      ownCapitalByMonth,
      ledger,
      onSaveOwnCapital,
      onPatchOwnCapitalLedger,
      year,
      forecastEnabled,
      projectedTimeline,
      expandIncomeDays,
      stockCostBasis,
      readOnlyOwnCapitalEdits,
      localLedgerAnalytics,
      onTargetRocSaved,
    )
  ) {
    return;
  }

  const rendered = renderMonthlyIncomeSection(
    months,
    ownCapitalByMonth,
    ledger,
    onSaveOwnCapital,
    onPatchOwnCapitalLedger,
    year,
    forecastEnabled,
    projectedTimeline,
    onSelectMonth,
    onMonthDetailChange,
    expandIncomeDays,
    stockCostBasis,
    readOnlyOwnCapitalEdits,
    localLedgerAnalytics,
    onTargetRocSaved,
  );
  if (monthsHost) {
    monthsHost.replaceWith(rendered);
  } else {
    body.appendChild(rendered);
  }
}

function getAnalyticsPanel(): HTMLElement | null {
  const panel = document.querySelector(ANALYTICS_PANEL_SEL);
  return panel instanceof HTMLElement ? panel : null;
}

function findFifoHeading(): HTMLElement | null {
  const panel = getAnalyticsPanel();

  // Prefer the live site's tour anchor / first H2 in the analytics panel
  if (panel) {
    const byAnchor = panel.querySelector(
      'h2[data-tour-anchor="wheel-analytics-heading"], [data-tour-anchor="wheel-analytics-heading"]',
    );
    if (byAnchor instanceof HTMLElement && byAnchor.closest("summary") == null) {
      return byAnchor.tagName === "H2" ? byAnchor : (byAnchor.querySelector("h2") ?? byAnchor);
    }

    const firstH2 = panel.querySelector(":scope > h2");
    if (firstH2 instanceof HTMLElement) return firstH2;
  }

  const byAnchorGlobal = document.querySelector(
    'h2[data-tour-anchor="wheel-analytics-heading"]',
  );
  if (byAnchorGlobal instanceof HTMLElement && byAnchorGlobal.closest("summary") == null) {
    return byAnchorGlobal;
  }

  let best: { el: HTMLElement; len: number } | null = null;
  const scope = panel ?? document.body;

  for (const el of scope.querySelectorAll("h1,h2,h3,h4")) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.closest("summary")) continue;

    const text = normalizeText(el.textContent ?? "");
    if (!FIFO_HEADING_RE.test(text)) continue;
    if (text.length > 400) continue;

    if (!best || text.length < best.len) {
      best = { el, len: text.length };
    }
  }

  return best?.el ?? null;
}

function readFifoCollapsed(): boolean {
  migrateLegacyCollapsedFlag(
    ANALYTICS_COLLAPSIBLE_SCOPE,
    ANALYTICS_FIFO_SECTION_ID,
    FIFO_COLLAPSE_STORAGE_KEY,
    true,
  );
  return !isCollapsibleOpen(ANALYTICS_COLLAPSIBLE_SCOPE, ANALYTICS_FIFO_SECTION_ID, true);
}

function writeFifoCollapsed(collapsed: boolean): void {
  setCollapsibleOpen(ANALYTICS_COLLAPSIBLE_SCOPE, ANALYTICS_FIFO_SECTION_ID, !collapsed);
}

/**
 * Wrap Analytics content in native `<details class="collapsible">` —
 * same mechanism as Monthly statistics / Options performance on this page.
 * Idempotent; safe to call whenever the SPA rewrites the panel.
 */
function ensureFifoCollapsibleDetails(): HTMLDetailsElement | null {
  const existing = document.getElementById(FIFO_DETAILS_ID);
  if (existing instanceof HTMLDetailsElement) return existing;

  const heading = findFifoHeading();
  if (!heading) return null;

  // Already folded by us or the host page
  const parentDetails = heading.closest("details.collapsible");
  if (parentDetails instanceof HTMLDetailsElement) {
    if (!parentDetails.id) parentDetails.id = FIFO_DETAILS_ID;
    parentDetails.setAttribute(FIFO_COLLAPSE_ATTR, "true");
    return parentDetails;
  }

  if (!heading.parentElement) return null;

  const details = document.createElement("details");
  details.id = FIFO_DETAILS_ID;
  details.className = "collapsible";
  details.setAttribute(FIFO_COLLAPSE_ATTR, "true");
  details.setAttribute("data-sfe-injected", "true");
  details.open = !readFifoCollapsed();

  const summary = document.createElement("summary");
  const tourAnchor = heading.getAttribute("data-tour-anchor");
  if (tourAnchor) summary.setAttribute("data-tour-anchor", tourAnchor);

  // Match Options / Stocks performance summaries on this site
  const title = document.createElement("h2");
  title.style.display = "inline";
  title.style.margin = "0";
  title.innerHTML = heading.innerHTML;
  summary.appendChild(title);
  details.appendChild(summary);

  let sibling: ChildNode | null = heading.nextSibling;
  while (sibling) {
    const next = sibling.nextSibling;
    details.appendChild(sibling);
    sibling = next;
  }

  heading.replaceWith(details);

  details.addEventListener("toggle", () => {
    writeFifoCollapsed(!details.open);
  });

  console.info("[Stock Forge+] Analytics section wrapped as collapsible details");
  return details;
}

class FifoCollapseManager {
  ensure(): boolean {
    const ok = ensureFifoCollapsibleDetails() != null;
    if (!ok && isAnalyticsSubviewVisible()) {
      console.warn("[Stock Forge+] Could not wrap Analytics FIFO heading for collapse");
    }
    return ok;
  }
}

/** Independent of the Analytics+ tab — keeps the native "realized
 * closed-trade history (FIFO)" section on the *native* Analytics subview
 * foldable, exactly as before Analytics+ existed. Mount once at startup. */
export function mountFifoCollapse(): void {
  const collapse = new FifoCollapseManager();
  let fifoTimer: ReturnType<typeof setTimeout> | null = null;

  const onAnalyticsActivated = () => {
    // Host SPA often finishes painting after the tab click — retry wrap
    collapse.ensure();
    window.setTimeout(() => collapse.ensure(), 200);
    window.setTimeout(() => collapse.ensure(), 800);
    window.setTimeout(() => collapse.ensure(), 1600);
  };

  document.addEventListener("wfa:wheel-subview", ((event: CustomEvent<{ name?: string }>) => {
    if (event.detail?.name === "analytics") onAnalyticsActivated();
  }) as EventListener);

  document.getElementById("view-wheel-trade")?.addEventListener("click", (event) => {
    const tab = (event.target as HTMLElement).closest("[data-wheel-tab]");
    if (tab?.getAttribute("data-wheel-tab") === "analytics") onAnalyticsActivated();
  });

  collapse.ensure();
  if (isAnalyticsSubviewVisible()) onAnalyticsActivated();

  const observer = new MutationObserver(() => {
    if (fifoTimer) clearTimeout(fifoTimer);
    fifoTimer = setTimeout(() => {
      if (!isAnalyticsSubviewVisible()) return;
      const needsWrap = !document.getElementById(FIFO_DETAILS_ID) && findFifoHeading() != null;
      if (needsWrap) collapse.ensure();
    }, 250);
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export interface AnalyticsPlusControllerOptions {
  /** Panel header title under the year chips. */
  headerTitle?: string;
  /** Optional subtitle under the header (e.g. Legacy migration cutoff). */
  headerSubtitle?: string;
  /**
   * `data-wheel-view` this controller owns. Analytics+ / Analytics(L) / Legacy
   * share one DOM panel — ignore refresh/render when another tab is active.
   */
  wheelView?: "analytics" | "analytics-local" | "legacy";
  /** When true, daily income accordions start expanded (web app). */
  expandIncomeDaysByDefault?: boolean;
  /** When true, own-capital edits stay in-memory and are not saved to the proxy. */
  readOnlyOwnCapital?: boolean;
  /** Analytics(L): own-ledger income (strikes + avg cost) + proxy cash flows/tax; no SF positions, margin, or corrections. */
  localLedgerAnalytics?: boolean;
  /** Optional static/archive data source (used by the Legacy tab). */
  getTransactions?: (force?: boolean) => Promise<WheelTransaction[]>;
  getStockSummary?: (force?: boolean) => Promise<StockOpenSummary>;
  getOwnCapitalLedger?: (force?: boolean) => Promise<Record<string, OwnCapitalEntry>>;
}

/**
 * Owns all data fetching/caching and rendering for the Analytics+ panel.
 * Fully self-contained — the panel element is handed in by the web/mobile shell,
 * which also decides when to call `refresh()` (on tab activation). Year/month selection changes
 * re-render synchronously from cached data (see `render()`), so they never
 * wait on a network round trip.
 */
export class AnalyticsPlusController {
  private panel: HTMLElement | null = null;
  private refreshing = false;
  private txnsCache: WheelTransaction[] = [];
  private txnsCacheAt = 0;
  private txnsCacheValid = false;
  private stockSummaryCache: StockOpenSummary = EMPTY_STOCK_OPEN_SUMMARY;
  private stockSummaryCacheAt = 0;
  private stockSummaryCacheValid = false;
  private ownCapitalLedgerCache: Record<string, OwnCapitalEntry> = {};
  private ownCapitalLedgerCacheAt = 0;
  private ownCapitalLedgerCacheValid = false;
  private cashFlowsCache: OwnLedgerCashFlow[] = [];
  private cashFlowsCacheAt = 0;
  private cashFlowsCacheValid = false;
  private openingOwnCapital = 0;
  private marginCostHistoryCacheAt = 0;
  private marginCostHistoryCacheValid = false;
  private readonly opts: AnalyticsPlusControllerOptions;
  private readonly unsubAccountScope: () => void;
  private readonly onTaxSettingsChanged = (): void => {
    if (!this.ownsActivePanel() || !this.txnsCacheValid) return;
    void refreshForecastTaxPlans({ syncToCashLedger: this.opts.localLedgerAnalytics === true }).then(
      () => {
        if (this.opts.localLedgerAnalytics) {
          this.ownCapitalLedgerCacheValid = false;
        }
        if (this.ownsActivePanel()) this.render({ soft: true });
      },
    );
  };

  private readonly onTaxPaymentMonthFocus = (event: Event): void => {
    const detail = (event as CustomEvent<TaxPaymentMonthFocusDetail>).detail;
    if (!detail?.monthKey || !this.ownsActivePanel() || !this.txnsCacheValid) return;
    selectedIncomeMonthKey = detail.monthKey;
    if (selectedIncomeYear !== "all" && selectedIncomeYear !== detail.year) {
      selectedIncomeYear = detail.year;
    }
    this.render({ soft: true });
  };

  private readonly onLedgerChanged = (): void => {
    if (!this.ownsActivePanel()) return;
    void this.refresh();
  };

  constructor(opts: AnalyticsPlusControllerOptions = {}) {
    this.opts = opts;
    document.addEventListener(WHEEL_LEDGER_CHANGED_EVENT, this.onLedgerChanged);
    this.unsubAccountScope = subscribeAccountScope(() => {
      if (!this.opts.localLedgerAnalytics) return;
      this.stockSummaryCacheValid = false;
      if (!this.ownsActivePanel()) return;
      this.render({ soft: true });
      void this.refresh(true);
    });
  }

  /** Analytics+ / (L) / Legacy share one panel element — only the active tab may paint. */
  private expectedWheelView(): "analytics" | "analytics-local" | "legacy" {
    if (this.opts.wheelView) return this.opts.wheelView;
    if (this.opts.localLedgerAnalytics) return "analytics-local";
    return "analytics";
  }

  private ownsActivePanel(): boolean {
    if (!this.panel) return false;
    return this.panel.getAttribute("data-wheel-view") === this.expectedWheelView();
  }

  /** Idempotent — safe to call every time the tab is (re)activated, even
   * if the panel element itself was recreated by a remount. Proxy health
   * lives solely in the top toolbar (see `toolbar.ts`), not here. */
  mount(panel: HTMLElement): void {
    this.panel = panel;
    const title = this.opts.headerTitle ?? "Stock Forge+ · Wheel performance snapshot";
    const subtitle = this.opts.headerSubtitle?.trim() ?? "";
    const subtitleHtml = subtitle
      ? `<p class="muted small">${subtitle}</p>`
      : "";

    ensureTxnNoteClickBinding();
    if (!panel.querySelector(".sfe-analytics-plus-body")) {
      panel.innerHTML = `
        <div class="sfe-analytics-plus-header">
          <div class="sfe-analytics-plus-header-text">
            <h2 class="insight-h">${title}</h2>
            ${subtitleHtml}
          </div>
          <div class="sfe-analytics-plus-header-actions"></div>
        </div>
        <div class="sfe-analytics-plus-body">
          <h4 class="insight-h sfe-income-section-title">Monthly Performance</h4>
        </div>
      `;
    } else {
      const heading = panel.querySelector(".sfe-analytics-plus-header h2.insight-h");
      if (heading) heading.textContent = title;
      const header =
        panel.querySelector(".sfe-analytics-plus-header-text") ??
        panel.querySelector(".sfe-analytics-plus-header");
      if (header instanceof HTMLElement) {
        let sub = header.querySelector(".muted.small");
        if (subtitle) {
          if (!(sub instanceof HTMLElement)) {
            sub = document.createElement("p");
            sub.className = "muted small";
            header.appendChild(sub);
          }
          sub.textContent = subtitle;
        } else if (sub) {
          sub.remove();
        }
      }
    }

    const headerActions = panel.querySelector<HTMLElement>(".sfe-analytics-plus-header-actions");
    headerActions?.querySelector(".sfe-column-settings-btn")?.remove();

    syncResetButtons();
    syncAnalyticsIncomeColumnPicker(
      panel,
      () => this.render({ soft: true }),
      this.opts.localLedgerAnalytics === true,
    );
    window.removeEventListener(TAX_SETTINGS_CHANGED_EVENT, this.onTaxSettingsChanged);
    window.addEventListener(TAX_SETTINGS_CHANGED_EVENT, this.onTaxSettingsChanged);
    window.removeEventListener(TAX_PAYMENT_MONTH_FOCUS_EVENT, this.onTaxPaymentMonthFocus);
    window.addEventListener(TAX_PAYMENT_MONTH_FOCUS_EVENT, this.onTaxPaymentMonthFocus);

    // Cheap synchronous re-render from whatever's cached so the panel isn't
    // blank while `refresh()` (called by the tab manager right after mount)
    // is still in flight.
    if (this.txnsCacheValid) this.render({ soft: this.panelHasSoftContent() });
  }

  dispose(): void {
    this.unsubAccountScope();
    window.removeEventListener(TAX_SETTINGS_CHANGED_EVENT, this.onTaxSettingsChanged);
    window.removeEventListener(TAX_PAYMENT_MONTH_FOCUS_EVENT, this.onTaxPaymentMonthFocus);
    document.removeEventListener(WHEEL_LEDGER_CHANGED_EVENT, this.onLedgerChanged);
  }

  /** Drop cached wheel snapshots so the next refresh pulls fresh ledger data. */
  invalidateDataCache(): void {
    this.txnsCacheValid = false;
    this.stockSummaryCacheValid = false;
    this.ownCapitalLedgerCacheValid = false;
    this.cashFlowsCacheValid = false;
    this.marginCostHistoryCacheValid = false;
  }

  private async getTransactions(force = false): Promise<WheelTransaction[]> {
    const now = Date.now();
    if (!force && this.txnsCacheValid && now - this.txnsCacheAt < DATA_CACHE_MS) {
      return this.txnsCache;
    }

    try {
      const txns = this.opts.getTransactions
        ? await this.opts.getTransactions(force)
        : await fetchTransactions();
      this.txnsCache = txns;
      this.txnsCacheAt = now;
      this.txnsCacheValid = true;
      return txns;
    } catch {
      if (!this.txnsCacheValid) {
        showToast("Could not load Analytics+ transactions.", "error");
      }
      return this.txnsCache;
    }
  }

  private async getStockSummary(force = false): Promise<StockOpenSummary> {
    const now = Date.now();
    if (!force && this.stockSummaryCacheValid && now - this.stockSummaryCacheAt < DATA_CACHE_MS) {
      return this.stockSummaryCache;
    }

    try {
      const summary = this.opts.getStockSummary
        ? await this.opts.getStockSummary(force)
        : await fetchStockOpenSummary();
      this.stockSummaryCache = summary;
      this.stockSummaryCacheAt = now;
      this.stockSummaryCacheValid = true;
      return summary;
    } catch {
      if (!this.stockSummaryCacheValid) {
        showToast("Could not load stock performance summary.", "error");
      }
      return this.stockSummaryCache;
    }
  }

  private async getOwnCapitalLedger(force = false): Promise<Record<string, OwnCapitalEntry>> {
    const now = Date.now();
    if (!force && this.ownCapitalLedgerCacheValid && now - this.ownCapitalLedgerCacheAt < DATA_CACHE_MS) {
      return this.ownCapitalLedgerCache;
    }

    const ledger = this.opts.getOwnCapitalLedger
      ? await this.opts.getOwnCapitalLedger(force)
      : await fetchOwnCapitalLedger();
    this.ownCapitalLedgerCache = ledger;
    this.ownCapitalLedgerCacheAt = now;
    this.ownCapitalLedgerCacheValid = true;
    return ledger;
  }

  private async getCashFlows(force = false): Promise<OwnLedgerCashFlow[]> {
    if (!this.opts.localLedgerAnalytics) return [];
    const now = Date.now();
    if (!force && this.cashFlowsCacheValid && now - this.cashFlowsCacheAt < DATA_CACHE_MS) {
      return this.cashFlowsCache;
    }
    try {
      const flows = await listOwnLedgerCashFlows({ limit: 5000 }, force);
      this.cashFlowsCache = flows;
      this.cashFlowsCacheAt = now;
      this.cashFlowsCacheValid = true;
      return flows;
    } catch {
      if (!this.cashFlowsCacheValid) {
        showToast("Could not load Analytics cash history.", "error");
      }
      return this.cashFlowsCache;
    }
  }

  private async getMarginCostHistory(): Promise<void> {
    if (this.opts.readOnlyOwnCapital && !this.opts.localLedgerAnalytics) {
      marginCostHistoryByMonth.clear();
      return;
    }
    const now = Date.now();
    if (this.marginCostHistoryCacheValid && now - this.marginCostHistoryCacheAt < DATA_CACHE_MS) {
      return;
    }
    const monthKeys = new Set<string>([currentMonthKey()]);
    if (selectedIncomeMonthKey) monthKeys.add(selectedIncomeMonthKey);
    const summaries = await Promise.all(
      [...monthKeys].map((monthKey) => fetchMarginCostMonthSummary(monthKey)),
    );
    for (const summary of summaries) {
      storeMarginCostSummary(summary);
    }
    this.marginCostHistoryCacheAt = now;
    this.marginCostHistoryCacheValid = true;
  }

  private patchOwnCapitalLedger(month: string, entry: Partial<OwnCapitalEntry>): void {
    const prev = this.ownCapitalLedgerCache[month] ?? normalizeOwnCapitalEntry(undefined);
    this.ownCapitalLedgerCache = {
      ...this.ownCapitalLedgerCache,
      [month]: mergeOwnCapitalEntry(prev, entry),
    };
    this.ownCapitalLedgerCacheAt = Date.now();
    this.ownCapitalLedgerCacheValid = true;
    this.render({ soft: true });
  }

  private async saveOwnCapital(month: string, entry: Partial<OwnCapitalEntry>): Promise<void> {
    const prev = this.ownCapitalLedgerCache[month] ?? normalizeOwnCapitalEntry(undefined);
    const merged = mergeOwnCapitalEntry(prev, entry);
    const ok = this.opts.readOnlyOwnCapital
      ? true
      : await saveOwnCapitalEntry(month, merged);
    if (ok) {
      this.ownCapitalLedgerCache = {
        ...this.ownCapitalLedgerCache,
        [month]: { ...prev, ...merged },
      };
      this.ownCapitalLedgerCacheAt = Date.now();
      this.ownCapitalLedgerCacheValid = true;
    }
    this.render({ soft: true });
  }

  private panelHasSoftContent(): boolean {
    return Boolean(
      this.panel?.querySelector(".sfe-income-months") ||
        this.panel?.querySelector(".sfe-inject-kpi-row"),
    );
  }

  private persistViewSelection(): void {
    if (this.opts.localLedgerAnalytics) saveAnalyticsLocalViewSelection();
  }

  /** Background refreshes patch KPIs in place when possible; scroll and focus
   * are restored so periodic updates do not jerk the panel. */
  private render(opts: { soft?: boolean } = {}): void {
    withPreservedPanelView(this.panel, () => this.paint(opts));
  }

  /** Rebuilds the panel body from cached data plus the current year/month
   * selection — synchronous, so selector clicks feel instant. Soft mode
   * patches year chips / KPIs / month detail in place when structure matches. */
  private paint(opts: { soft?: boolean } = {}): void {
    if (!this.ownsActivePanel()) return;
    const preferSoft = opts.soft === true && this.panelHasSoftContent();
    // Year chips stay derived from the full set so narrowing to a ticker never
    // makes the selected year disappear from the row.
    const allTxns = this.opts.localLedgerAnalytics
      ? this.txnsCache.filter(analyticsTxnInScope)
      : this.txnsCache;
    const cashFlows = this.opts.localLedgerAnalytics
      ? this.cashFlowsCache.filter(analyticsCashInScope)
      : this.cashFlowsCache;
    const capitalLedger = this.opts.localLedgerAnalytics
      ? scopeOwnCapitalLedger(this.ownCapitalLedgerCache, cashFlows)
      : this.ownCapitalLedgerCache;
    const tickerFilter = this.opts.localLedgerAnalytics ? analyticsTickerFilter : "";
    const txns = tickerFilter
      ? allTxns.filter((txn) => matchesAnalyticsTickerFilter(txn, tickerFilter))
      : allTxns;
    const rawYears = buildYearOptions(allTxns, incomeForecastEnabled);
    const years = this.opts.localLedgerAnalytics
      ? filterAnalyticsLocalYears(rawYears)
      : rawYears;
    selectedIncomeYear = normalizeAnalyticsYear(selectedIncomeYear, years);
    // All is a year-spanning view, so it cannot hold a month.
    if (selectedIncomeYear === "all" && selectedIncomeMonthKey) {
      selectedIncomeMonthKey = null;
      this.persistViewSelection();
    }
    const scopeYear = selectedIncomeYear;
    const incomeOpts = this.opts.localLedgerAnalytics
      ? { omitLongOptionFlow: true as const }
      : undefined;
    const scoped = buildIncomeCalendar(txns, incomeCalendarScopeYear(scopeYear), incomeOpts);
    const { breakdown, months: typeScopedMonths } = this.opts.localLedgerAnalytics
      ? applyIncomeTypeFilter(scoped.breakdown, scoped.months, analyticsTypeFilter)
      : { breakdown: scoped.breakdown, months: scoped.months };
    const showCashHistory =
      this.opts.localLedgerAnalytics === true &&
      !tickerFilter &&
      analyticsTypeFilter === "all";
    const months = this.opts.localLedgerAnalytics
      ? attachLocalLedgerCashHistory(
          withLocalLedgerCashMonths(typeScopedMonths, capitalLedger, scopeYear),
          showCashHistory ? cashFlows : [],
        )
      : typeScopedMonths;
    // Full history (same ticker/type filter) so Total Income matches Cash / Own Capital.
    const historyScoped = buildIncomeCalendar(txns, null, incomeOpts);
    const historyTyped = this.opts.localLedgerAnalytics
      ? applyIncomeTypeFilter(historyScoped.breakdown, historyScoped.months, analyticsTypeFilter)
      : { breakdown: historyScoped.breakdown, months: historyScoped.months };
    const incomeHistoryMonths = this.opts.localLedgerAnalytics
      ? withLocalLedgerCashMonths(historyTyped.months, capitalLedger, "all")
      : historyTyped.months;
    if (this.opts.localLedgerAnalytics) {
      attachIncomeLineNotes(months, allTxns, cashFlows);
    }
    // Own capital compounds across all history, not just the selected
    // year, so its running balance is computed from every month on
    // record, then looked up per displayed month below. It stays on the
    // unfiltered set — deposits are portfolio-wide, so blending them with a
    // ticker/type-narrowed income stream would report a balance that never
    // existed.
    const { months: allTxnMonths } = buildIncomeCalendar(allTxns, null, incomeOpts);
    const allMonths = this.opts.localLedgerAnalytics
      ? withLocalLedgerCashMonths(allTxnMonths, capitalLedger, "all")
      : allTxnMonths;
    const allMonthsByKey = new Map(allMonths.map((m) => [m.month, m]));
    const ownCapitalByMonth = computeOwnCapitalTimeline(
      allMonths,
      capitalLedger,
      this.stockSummaryCache.cost_basis,
      {
        localLedger: this.opts.localLedgerAnalytics === true,
        openingBalance: this.openingOwnCapital,
        predictedTaxWithdrawals: this.opts.localLedgerAnalytics
          ? undefined
          : predictedTaxWithdrawalsCache,
        forecastEnabled: incomeForecastEnabled,
      },
    );

    renderAnalyticsPlusBody(
      this.panel,
      breakdown,
      months,
      incomeHistoryMonths,
      this.stockSummaryCache,
      ownCapitalByMonth,
      years,
      selectedIncomeYear,
      (year) => {
        selectedIncomeYear = year;
        this.persistViewSelection();
        this.render({ soft: true });
      },
      incomeForecastEnabled,
      (enabled) => {
        incomeForecastEnabled = enabled;
        if (!enabled) resetAnalyticsPlusMonthSelection(currentCalendarYear());
        this.persistViewSelection();
        this.render({ soft: true });
      },
      (month, entry) => {
        void this.saveOwnCapital(month, entry);
      },
      (month, entry) => {
        this.patchOwnCapitalLedger(month, entry);
      },
      capitalLedger,
      allMonthsByKey,
      (monthKey) => {
        selectedIncomeMonthKey = monthKey;
        this.persistViewSelection();
        if (monthKey && !cachedMarginCostSummary(monthKey)) {
          this.marginCostHistoryCacheValid = false;
          void this.getMarginCostHistory().then(() => this.render({ soft: true }));
        } else {
          this.render({ soft: true });
        }
      },
      () => {
        this.render({ soft: true });
      },
      this.opts.expandIncomeDaysByDefault ?? false,
      this.opts.readOnlyOwnCapital === true,
      this.opts.localLedgerAnalytics === true,
      preferSoft,
      (value) => {
        if (value === analyticsTickerFilter) return;
        analyticsTickerFilter = value;
        saveAnalyticsTickerFilter(value);
        this.render({ soft: true });
      },
      (value) => {
        if (value === analyticsTypeFilter) return;
        analyticsTypeFilter = value;
        saveAnalyticsTypeFilter(value);
        this.render({ soft: true });
      },
      async () => {
        this.ownCapitalLedgerCacheValid = false;
        await this.getOwnCapitalLedger(true);
        this.render({ soft: true });
      },
    );
  }

  async refresh(force = false): Promise<void> {
    if (this.refreshing) return;
    if (!this.ownsActivePanel()) return;

    this.refreshing = true;
    try {
      // Always refetch when refresh() runs — tab open and ledger-change hooks
      // both call this; month toggles re-render from cache via render() only.
      this.invalidateDataCache();
      if (this.opts.localLedgerAnalytics) {
        await refreshForecastTaxPlans({ syncToCashLedger: true });
        this.ownCapitalLedgerCacheValid = false;
      }
      const loads: Promise<unknown>[] = [
        loadSpxMonthlyReturns(),
        this.getTransactions(force),
        this.getStockSummary(force),
        this.getOwnCapitalLedger(force),
        this.getMarginCostHistory(),
      ];
      if (this.opts.localLedgerAnalytics) {
        loads.push(this.getCashFlows(force));
      }
      if (!this.opts.localLedgerAnalytics) {
        loads.push(refreshForecastTaxPlans({ syncToCashLedger: false }));
      }
      if (this.opts.localLedgerAnalytics) {
        loads.push(
          loadOwnLedgerImportStatus(true).then((status) => {
            this.openingOwnCapital = status?.settings.openingOwnCapital ?? 0;
          }),
        );
      }
      await Promise.all(loads);
      // Another analytics tab may have taken the shared panel while we loaded.
      if (this.ownsActivePanel()) this.render({ soft: this.panelHasSoftContent() });
    } finally {
      this.refreshing = false;
    }
  }
}

/**
 * Offline / audit helper: Analytics(L) own-capital roll-forward.
 * EOM = prior + trading income + cashAdded − withdrawn − tax cash − margin
 * (+ stock lending). No SF per-trade fees, predicted margin, or corrections.
 */
/** Offline audit helper: income components for one calendar month. */
export function auditIncomeMonthBreakdown(
  txns: WheelTransaction[],
  monthKey: string,
  opts?: { omitLongOptionFlow?: boolean },
): IncomeBreakdown & { month: string; lineCount: number } {
  const { months } = buildIncomeCalendar(txns, null, opts);
  const month = months.find((m) => m.month === monthKey);
  const parts = month ?? emptyIncomeMonthBucket(monthKey);
  return {
    month: monthKey,
    premiums: parts.premiums,
    stcCredits: parts.stcCredits,
    btcCost: parts.btcCost,
    btoCost: parts.btoCost,
    assignIncome: parts.assignIncome,
    stockSellIncome: parts.stockSellIncome,
    total: cashIncome(parts),
    lineCount: parts.days.reduce((n, d) => n + d.lines.length, 0),
  };
}

/** Offline audit helper: income totals for one Analytics(L) row-type filter. */
export function auditIncomeTypeFilter(
  txns: WheelTransaction[],
  kind: AnalyticsTypeFilter,
  scopeYear: number | null,
  opts?: { omitLongOptionFlow?: boolean },
): IncomeBreakdown & { lineCount: number } {
  const scoped = buildIncomeCalendar(txns, scopeYear, opts);
  const { breakdown, months } = applyIncomeTypeFilter(scoped.breakdown, scoped.months, kind);
  return {
    ...breakdown,
    lineCount: months.reduce(
      (n, month) => n + month.days.reduce((m, day) => m + day.lines.length, 0),
      0,
    ),
  };
}

/** Offline audit helper: ledger rows that never surface as an Analytics row. */
export function auditIncomeCalendarCoverage(
  txns: WheelTransaction[],
  opts?: { omitLongOptionFlow?: boolean },
): {
  ledgerRows: number;
  shownRows: number;
  kindById: Map<number, string>;
  missing: Array<{
    id: number | null;
    instrument_type: string;
    action: string;
    close_reason: string | null;
    code: string | null;
    ticker: string | null;
    trade_date: string | null;
    quantity: number | null;
  }>;
} {
  const { months } = buildIncomeCalendar(txns, null, opts);
  const shown = new Set<number>();
  const kindById = new Map<number, string>();
  for (const month of months) {
    for (const day of month.days) {
      for (const line of day.lines) {
        if (line.txnId != null) {
          shown.add(line.txnId);
          kindById.set(line.txnId, line.kind);
        }
      }
    }
  }
  const missing: ReturnType<typeof auditIncomeCalendarCoverage>["missing"] = [];
  const shownPnlSettlements = visiblePnlSettlementIds(txns);
  let ledgerRows = 0;
  for (const txn of txns) {
    if (txn.instrument_type !== "option" && txn.instrument_type !== "stock") continue;
    if (isPnlTradeHistoryRow(txn) && !(txn.id != null && shownPnlSettlements.has(txn.id))) continue;
    ledgerRows += 1;
    if (txn.id != null && shown.has(txn.id)) continue;
    missing.push({
      id: txn.id ?? null,
      instrument_type: String(txn.instrument_type),
      action: String(txn.action ?? ""),
      close_reason: txn.close_reason ?? null,
      code: txnCode(txn) || null,
      ticker: txn.ticker ?? null,
      trade_date: txn.trade_date ?? null,
      quantity: txn.quantity ?? null,
    });
  }
  return { ledgerRows, shownRows: shown.size, kindById, missing };
}

export function auditAssignIncomeEvents(
  txns: WheelTransaction[],
  monthKey?: string,
): ReturnType<typeof listAssignIncomeEvents> {
  const events = listAssignIncomeEvents(txns);
  if (!monthKey) return events;
  return events.filter((ev) => ev.date.startsWith(monthKey));
}

export function auditStockSellIncomeEvents(
  txns: WheelTransaction[],
  monthKey?: string,
): ReturnType<typeof listStockSellIncomeEvents> {
  const events = listStockSellIncomeEvents(txns);
  if (!monthKey) return events;
  return events.filter((ev) => ev.date.startsWith(monthKey));
}

export function computeAnalyticsLocalOwnCapitalAudit(
  txns: WheelTransaction[],
  ledger: Record<string, OwnCapitalEntry>,
  openingBalance = 0,
): Array<{
  month: string;
  bom: number;
  income: number;
  cashAdded: number;
  withdrawn: number;
  stockLending: number;
  taxCash: number;
  margin: number;
  perTrade: number;
  eom: number;
  expectedEom: number;
  delta: number;
}> {
  const { months: allTxnMonths } = buildIncomeCalendar(txns, null);
  const allMonths = withLocalLedgerCashMonths(allTxnMonths, ledger, "all");
  const timeline = computeOwnCapitalTimeline(allMonths, ledger, 0, {
    localLedger: true,
    openingBalance,
  });
  const rows: Array<{
    month: string;
    bom: number;
    income: number;
    cashAdded: number;
    withdrawn: number;
    stockLending: number;
    taxCash: number;
    margin: number;
    perTrade: number;
    eom: number;
    expectedEom: number;
    delta: number;
  }> = [];
  let bom = openingBalance;
  for (const monthKey of [...timeline.keys()].sort((a, b) => a.localeCompare(b))) {
    const point = timeline.get(monthKey)!;
    const taxCash = Number(point.taxWithdrawn) || 0;
    const stockLending = Number(point.stockLendingIncome) || 0;
    const margin = Number(point.effectiveMarginCost) || 0;
    const perTrade = 0;
    const expectedEom =
      bom + point.income + point.cashAdded - point.withdrawn + stockLending - margin - perTrade;
    rows.push({
      month: monthKey,
      bom,
      income: point.income,
      cashAdded: point.cashAdded,
      withdrawn: point.withdrawn,
      stockLending,
      taxCash,
      margin,
      perTrade,
      eom: point.ownCapital,
      expectedEom,
      delta: point.ownCapital - expectedEom,
    });
    bom = point.ownCapital;
  }
  return rows;
}

/** Analytics(L): own-ledger income (strikes + avg cost) + Tax tab inputs; no SF capital/positions/corrections. */
export function createOwnLedgerAnalyticsController(
  getTransactions: (force?: boolean) => Promise<WheelTransaction[]>,
): AnalyticsPlusController {
  return new AnalyticsPlusController({
    expandIncomeDaysByDefault: true,
    headerTitle: "Analytics(L) · SF+ ledger",
    headerSubtitle:
      "Trade income (net of TT fees) + imported cash flows and margin interest + optional opening balance · tax via History(L) cash ledger · no SF corrections",
    wheelView: "analytics-local",
    readOnlyOwnCapital: true,
    localLedgerAnalytics: true,
    getTransactions,
    getStockSummary: async (force) => loadOwnLedgerStockOpenSummary(force === true, scopeAllowsTicker),
    getOwnCapitalLedger: async (force) =>
      loadOwnLedgerAnalyticsCapitalLedger(force === true, { includeForecast: true }),
  });
}

/** Analytics+ panel backed by bundled CoOption anton archive tables. */
export function createLegacyAnalyticsController(): AnalyticsPlusController {
  const cutoff = getLegacyAntonCutoffDate();
  return new AnalyticsPlusController({
    headerTitle: "Legacy · CoOption wheel snapshot (anton)",
    headerSubtitle: `Migration cutoff: ${cutoff} · archive frozen as of last DB update`,
    wheelView: "legacy",
    readOnlyOwnCapital: true,
    getTransactions: async () =>
      getLegacyAntonTransactionRecords().map(normalizeTransaction),
    getStockSummary: async () => getLegacyAntonStockSummary(),
    getOwnCapitalLedger: async () => ({}),
  });
}

export interface AnalyticsPlusIncomeLine {
  date: string;
  kind: string;
  label: string;
  ticker: string;
  amount: number;
  /** False for display-only companion rows (excluded from day income total). */
  incomeAccounted?: boolean;
  /** Dim secondary text on the same row (e.g. assignment shares × strike, call avg). */
  detail?: string;
  /** put | call | stock | — */
  side?: string;
  /** Strike as stored on the income line (numeric string, or "—"). */
  strike?: string;
  contracts?: number | null;
  /** Per-share premium (options) or price (stock). */
  premium?: number | null;
  dte?: number | null;
  txnId?: number;
  linkedTxnIds?: number[];
  /** Trade date of the ledger-linked opening STO/BTO (when present). */
  openDate?: string;
  brokerCode?: string;
  brokerColor?: string;
  brokerTitle?: string;
  accountShort?: string;
  accountTitle?: string;
  accountId?: string;
  /**
   * Linked open/close counterparts via History(L) ledger links only.
   * Nested `related` is omitted on children.
   */
  related?: AnalyticsPlusIncomeLine[];
}

export interface AnalyticsPlusDayGroup {
  date: string;
  total: number;
  lines: AnalyticsPlusIncomeLine[];
}

export interface AnalyticsPlusMonthChip {
  key: string;
  label: string;
  total: number;
  chipTip?: string | null;
  forecast?: boolean;
}

export interface AnalyticsPlusMonthDetail {
  key: string;
  label: string;
  /** Trading income before the effective margin financing cost. */
  grossIncome: number;
  /** Net monthly income after the effective margin financing cost. */
  income: number;
  rocPct: number | null;
  targetRocPct: number;
  /** Target ROC % × prior-month own capital (same as web month card). */
  targetIncome: number;
  /** Prior-month own capital (BOM / ROC denominator). */
  baseCapital: number;
  ownCapitalEom: number;
  /** EOM if the month hits the net ROC target (same $ basis as Realized). */
  plannedOwnCapital: number;
  /** ownCapitalEom − plannedOwnCapital (Realized $ − Target $ when cash/fees/tax net to 0). */
  planDelta: number;
  cashAdded: number;
  withdrawn: number;
  stockLendingIncome: number;
  expenses: number;
  /** Deprecated — Analytics+ no longer applies month-end corrections. */
  ownCapitalCorrection: number;
  marginCost: number;
  /** When true, margin cost uses predicted financing from stock cost basis. */
  usePredictedMarginCost: boolean;
  /** Current and later months are locked to the daily-rate full-month forecast. */
  marginForecastAutomatic?: boolean;
  /** Margin cost applied in calculations (manual or predicted). */
  effectiveMarginCost: number;
  /** RH accrued margin for elapsed days (fact mode when Use Forecast is off). */
  accruedMarginCost: number;
  perTradeFee: number;
  tradeCount: number;
  /** Month income breakdown components (fact view; zeros in forecast). */
  premiums: number;
  stcCredits: number;
  assignIncome: number;
  stockSellIncome: number;
  btcCost: number;
  btoCost: number;
  /** Non-empty days in the month (newest first), with all ledger lines. */
  days: AnalyticsPlusDayGroup[];
  forecastView?: boolean;
}

export type { IncomeChartMonthPoint };

export interface AnalyticsPlusMobileSnapshot {
  selectedYear: number | "all";
  forecastEnabled: boolean;
  isForecastView: boolean;
  dimStockKpis: boolean;
  years: number[];
  months: AnalyticsPlusMonthChip[];
  selectedMonthKey: string | null;
  breakdown: IncomeBreakdown;
  displayBreakdown: IncomeBreakdown;
  breakdownScopeLabel: string;
  performanceScopeBadge: AnalyticsPerformanceScopeBadge;
  stockSummary: StockOpenSummary;
  ownCapitalEom: number;
  cashAddedTotal: number;
  withdrawnTotal: number;
  expensesTotal: number;
  /** Effective margin financing cost accumulated through the selected month. */
  marginCostTotal: number;
  /** Robinhood margin used when history is available; else cost basis − own capital. */
  marginUsed: number;
  /** Latest RH daily interest ($/day), when history is available. */
  rhDailyInterest: number | null;
  /** Latest RH margin APR %, when history is available. */
  rhAnnualRatePct: number | null;
  predictedMarginCost: number;
  taxSummary?: AnalyticsTaxPerformanceSummary | null;
  monthDetail: AnalyticsPlusMonthDetail | null;
  incomeChartMonths: IncomeChartMonthPoint[];
}

function resolveSelectedMonthKey(
  months: IncomeMonthBucket[],
  selectedMonthKey: string | null,
): string | null {
  if (!months.length) return null;
  const validKeys = new Set(months.map((month) => month.month));
  if (selectedMonthKey && validKeys.has(selectedMonthKey)) return selectedMonthKey;
  const currentMonth = currentMonthKey();
  return validKeys.has(currentMonth) ? currentMonth : months[0].month;
}

function toAnalyticsPlusIncomeLine(
  dayDate: string,
  line: IncomeLine,
): AnalyticsPlusIncomeLine {
  return {
    date: dayDate,
    kind: line.kind,
    label: line.label,
    ticker: line.ticker,
    amount: line.amount,
    incomeAccounted: line.incomeAccounted,
    detail: line.detail,
    side: line.side,
    strike: line.strike,
    contracts: line.contracts ?? null,
    premium: line.premium ?? null,
    dte: line.dte ?? null,
    txnId: line.txnId,
    linkedTxnIds: line.linkedTxnIds ?? [],
    openDate: line.open !== "—" ? line.open : undefined,
    brokerCode: line.brokerCode,
    brokerColor: line.brokerColor,
    brokerTitle: line.brokerTitle,
    accountShort: line.accountShort,
    accountTitle: line.accountTitle,
    accountId: line.accountId,
  };
}

function flattenAnalyticsPlusLines(
  months: IncomeMonthBucket[],
): AnalyticsPlusIncomeLine[] {
  const out: AnalyticsPlusIncomeLine[] = [];
  for (const month of months) {
    for (const day of month.days) {
      for (const line of day.lines) {
        out.push(toAnalyticsPlusIncomeLine(day.date, line));
      }
    }
  }
  return out;
}

function analyticsPlusLineIdentity(line: AnalyticsPlusIncomeLine): string {
  if (line.txnId != null) return `id:${line.txnId}`;
  return `${line.date}|${line.kind}|${line.ticker}|${line.strike}|${line.contracts ?? ""}|${line.amount}`;
}

/** Related income lines via History(L) ledger links only (no FIFO inference). */
function findRelatedAnalyticsPlusLines(
  line: AnalyticsPlusIncomeLine,
  catalog: AnalyticsPlusIncomeLine[],
): AnalyticsPlusIncomeLine[] {
  const byTxnId = new Map<number, AnalyticsPlusIncomeLine>();
  for (const row of catalog) {
    if (row.txnId != null) byTxnId.set(row.txnId, row);
  }

  const found = new Map<string, AnalyticsPlusIncomeLine>();
  const selfKey = analyticsPlusLineIdentity(line);
  const add = (other: AnalyticsPlusIncomeLine | undefined) => {
    if (!other) return;
    const key = analyticsPlusLineIdentity(other);
    if (key === selfKey) return;
    found.set(key, other);
  };

  for (const id of line.linkedTxnIds ?? []) {
    add(byTxnId.get(id));
  }

  return [...found.values()].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.kind.localeCompare(b.kind) ||
      a.ticker.localeCompare(b.ticker),
  );
}

function resolveAnalyticsPlusMonthKey(
  displayMonths: IncomeMonthBucket[],
  selectedMonthKey: string | null,
): string | null {
  if (!displayMonths.length) return null;
  const validKeys = new Set(displayMonths.map((month) => month.month));
  if (selectedMonthKey && validKeys.has(selectedMonthKey)) return selectedMonthKey;
  const currentMonth = currentMonthKey();
  return validKeys.has(currentMonth) ? currentMonth : displayMonths[0].month;
}

const ANALYTICS_OPEN_CACHE_MS = 90_000;
let forecastTaxSyncedRevision: string | null = null;
const analyticsSnapshotCache = new Map<string, { at: number; snapshot: AnalyticsPlusMobileSnapshot }>();

export function invalidateAnalyticsPlusCache(): void {
  forecastTaxSyncedRevision = null;
  analyticsSnapshotCache.clear();
  invalidateOwnLedgerGetCache();
}

function ownCapitalPlansFingerprint(plans: Record<string, OwnCapitalEntry>): string {
  return Object.keys(plans)
    .sort()
    .map((month) => {
      const entry = plans[month];
      return [
        month,
        entry?.targetRocPct ?? "",
        entry?.usePredictedMarginCost === false ? 0 : entry?.usePredictedMarginCost === true ? 1 : "",
        entry?.marginCost ?? "",
        entry?.cashAdded ?? "",
        entry?.withdrawn ?? "",
      ].join(":");
    })
    .join("|");
}

/** Ledger write stamp plus own-capital plan fingerprint. View cache keys use this. */
export async function readAnalyticsViewToken(): Promise<string> {
  const [revision, plans] = await Promise.all([
    ownLedgerReadRevision(),
    fetchOwnCapitalLedger(),
  ]);
  return `${revision}|${ownCapitalPlansFingerprint(plans)}`;
}

function analyticsSnapshotKey(
  opts: {
    selectedYear: number | "all";
    selectedMonthKey: string | null;
    forecastEnabled?: boolean;
    accountId?: string;
  },
  revision: string,
): string {
  return `${revision}|${opts.selectedYear}|${opts.selectedMonthKey ?? ""}|${opts.forecastEnabled ? 1 : 0}|${opts.accountId ?? ""}|${accountScopeGeneration()}`;
}

function analyticsSnapshotFresh(monthKey: string | null, forecast: boolean, cachedAt: number): boolean {
  const closed = Boolean(monthKey) && !forecast && monthKey! < currentMonthKey();
  return closed || Date.now() - cachedAt < ANALYTICS_OPEN_CACHE_MS;
}

/** Mobile-friendly Analytics+ snapshot built from the same logic as the desktop panel. */
export async function loadAnalyticsPlusSnapshot(
  wheelGet: <T>(path: string, force?: boolean) => Promise<T>,
  opts: {
    selectedYear: number | "all";
    selectedMonthKey: string | null;
    forecastEnabled?: boolean;
    forceLedgerRefresh?: boolean;
    source?: "stockforge" | "own_ledger";
    accountId?: string;
  },
): Promise<AnalyticsPlusMobileSnapshot> {
  const localLedgerAnalytics = opts.source === "own_ledger";
  let cacheRevision = "";
  if (localLedgerAnalytics && !opts.forceLedgerRefresh) {
    cacheRevision = await ownLedgerReadRevision().catch(() => "");
    const hit = cacheRevision ? analyticsSnapshotCache.get(analyticsSnapshotKey(opts, cacheRevision)) : undefined;
    if (hit && analyticsSnapshotFresh(opts.selectedMonthKey, opts.forecastEnabled === true, hit.at)) {
      return hit.snapshot;
    }
  }
  let txns: WheelTransaction[];
  let stockSummary: StockOpenSummary;
  let ledger: Record<string, OwnCapitalEntry>;
  let openingOwnCapital = 0;
  let cashFlows: OwnLedgerCashFlow[] = [];

  if (localLedgerAnalytics) {
    const [localTxns, localStockSummary, cashMonths, importStatus, localCashFlows, plans] =
      await Promise.all([
        loadOwnLedgerTransactionSnapshot(opts.forceLedgerRefresh === true),
        loadOwnLedgerStockOpenSummary(opts.forceLedgerRefresh === true, scopeAllowsTicker),
        loadOwnLedgerCashMonthLedger(opts.forceLedgerRefresh === true, { includeForecast: true }),
        loadOwnLedgerImportStatus(opts.forceLedgerRefresh === true),
        listOwnLedgerCashFlows({ limit: 5000 }, opts.forceLedgerRefresh === true),
        fetchOwnCapitalLedger(opts.forceLedgerRefresh ? { bustCache: true } : undefined),
      ]);
    const accountId = String(opts.accountId ?? "").trim();
    const scopedTxns = localTxns.filter(analyticsTxnInScope);
    const scopedCash = localCashFlows.filter(analyticsCashInScope);
    txns = accountId
      ? scopedTxns.filter((txn) => String(txn.account_id ?? "") === accountId)
      : scopedTxns;
    stockSummary = localStockSummary;
    cashFlows = accountId
      ? scopedCash.filter((flow) => String(flow.account_id ?? "") === accountId)
      : scopedCash;
    ledger = scopeOwnCapitalLedger(
      mergeOwnCapitalTargetRocFromPlans(
        capitalEntriesFromOwnLedgerCashMonths(cashMonths, { analyticsLocal: true }),
        plans,
      ),
      cashFlows,
    );
    openingOwnCapital = importStatus?.settings.openingOwnCapital ?? 0;
  } else {
    [txns, stockSummary, ledger] = await Promise.all([
      fetchTransactionsVia((path) => wheelGet(path, opts.forceLedgerRefresh)),
      fetchStockOpenSummaryVia((path) => wheelGet(path, opts.forceLedgerRefresh)),
      fetchOwnCapitalLedger(opts.forceLedgerRefresh ? { bustCache: true } : undefined),
    ]);
  }

  await loadSpxMonthlyReturns();
  const forecastEnabled = opts.forecastEnabled === true;
  const years = localLedgerAnalytics
    ? filterAnalyticsLocalYears(buildYearOptions(txns, forecastEnabled))
    : buildYearOptions(txns, forecastEnabled);
  const selectedYear = normalizeAnalyticsYear(opts.selectedYear, years);

  const scopeYear = selectedYear;
  const incomeOpts = localLedgerAnalytics ? { omitLongOptionFlow: true as const } : undefined;
  const { breakdown, months: scopedMonths } = buildIncomeCalendar(
    txns,
    incomeCalendarScopeYear(scopeYear),
    incomeOpts,
  );
  const months = localLedgerAnalytics
    ? attachLocalLedgerCashHistory(
        withLocalLedgerCashMonths(scopedMonths, ledger, scopeYear),
        cashFlows,
      )
    : scopedMonths;
  const historyScoped = buildIncomeCalendar(txns, null, incomeOpts);
  const historyMonths = localLedgerAnalytics
    ? withLocalLedgerCashMonths(historyScoped.months, ledger, "all")
    : historyScoped.months;
  const displayMonths = withForecastMonths(months, selectedYear, forecastEnabled);
  const selectedMonthKey = resolveAnalyticsPlusMonthKey(displayMonths, opts.selectedMonthKey);

  const marginMonthKeys = new Set<string>([currentMonthKey()]);
  if (selectedMonthKey) marginMonthKeys.add(selectedMonthKey);
  const marginSummaries = await Promise.all(
    [...marginMonthKeys].map((monthKey) => fetchMarginCostMonthSummary(monthKey)),
  );
  for (const summary of marginSummaries) {
    storeMarginCostSummary(summary);
  }

  const { months: allTxnMonths } = buildIncomeCalendar(txns, null, incomeOpts);
  const allMonths = localLedgerAnalytics
    ? withLocalLedgerCashMonths(allTxnMonths, ledger, "all")
    : allTxnMonths;
  const allMonthsByKey = new Map(allMonths.map((month) => [month.month, month]));
  if (!(localLedgerAnalytics && cacheRevision && forecastTaxSyncedRevision === cacheRevision)) {
    await refreshForecastTaxPlans({ syncToCashLedger: localLedgerAnalytics });
    if (cacheRevision) forecastTaxSyncedRevision = cacheRevision;
  }
  const ownCapitalByMonth = computeOwnCapitalTimeline(allMonths, ledger, stockSummary.cost_basis, {
    localLedger: localLedgerAnalytics,
    openingBalance: openingOwnCapital,
    predictedTaxWithdrawals: localLedgerAnalytics ? undefined : predictedTaxWithdrawalsCache,
    forecastEnabled,
  });
  const isForecastView =
    forecastEnabled &&
    selectedMonthKey != null &&
    isForecastMonthChip(selectedMonthKey, forecastEnabled, selectedYear);
  const projectedTimeline =
    isForecastView && selectedMonthKey
      ? ensureProjectedOwnCapitalTimeline(
          ownCapitalByMonth,
          ledger,
          selectedMonthKey,
          stockSummary.cost_basis,
          predictedTaxWithdrawalsCache,
        )
      : ownCapitalByMonth;
  const forecastTotals =
    isForecastView && selectedMonthKey
      ? totalsThroughMonth(
          months,
          projectedTimeline,
          selectedMonthKey,
          incomeCalendarScopeYear(scopeYear),
          ledger,
          allMonthsByKey,
        )
      : null;

  const { cashAdded, withdrawn } = cashFlowTotalsForKpi(
    ownCapitalByMonth,
    forecastTotals,
    selectedMonthKey,
  );
  const ownCapitalEom = selectedMonthOwnCapitalEom(
    selectedMonthKey,
    isForecastView,
    ownCapitalByMonth,
    projectedTimeline,
    forecastEnabled,
  );
  const expenseTotals = aggregateExpensesThroughMonth(
    ledger,
    allMonthsByKey,
    selectedMonthKey,
    ownCapitalByMonth,
  );
  const marginMonthKey = selectedMonthKey ?? currentMonthKey();
  const accountingMarginUsed = stockSummary.cost_basis - ownCapitalEom;
  const marginUsed = localLedgerAnalytics
    ? accountingMarginUsed
    : rhCurrentMarginUsed(marginMonthKey) ?? accountingMarginUsed;
  const showCurrentDailyMargin = monthUsesAutomaticMarginForecast(marginMonthKey);
  const rhRateMonth = marginMonthKey > currentMonthKey() ? currentMonthKey() : marginMonthKey;
  const rhDailyInterest = localLedgerAnalytics
    ? showCurrentDailyMargin
      ? rhCurrentDailyInterest(currentMonthKey())
      : null
    : rhCurrentDailyInterest(rhRateMonth);
  const rhAnnualRatePct = localLedgerAnalytics
    ? showCurrentDailyMargin
      ? rhCurrentAnnualRatePct(currentMonthKey())
      : null
    : rhCurrentAnnualRatePct(rhRateMonth);
  const dimStockKpis = shouldDimStockKpis(selectedMonthKey);

  const monthChips: AnalyticsPlusMonthChip[] = displayMonths.map((month) => ({
    key: month.month,
    label: month.label,
    total: cashIncome(month),
    chipTip: monthChipTipLabel(month.month, forecastEnabled, selectedYear),
    forecast: isForecastMonthChip(month.month, forecastEnabled, selectedYear),
  }));

  let monthDetail: AnalyticsPlusMonthDetail | null = null;
  if (selectedMonthKey) {
    const month =
      displayMonths.find((row) => row.month === selectedMonthKey) ??
      months.find((row) => row.month === selectedMonthKey) ??
      allMonthsByKey.get(selectedMonthKey);
    if (month) {
      const timeline = isForecastView ? projectedTimeline : ownCapitalByMonth;
      const point = timeline.get(month.month);
      const expenseEntry = ledgerEntryForMonth(month.month, ledger, timeline);
      const targetRocPct = readTargetRocPct(month.month, ledger);
      const prevOwnCapital = isForecastView
        ? baseCapitalForMonth(month.month, ownCapitalByMonth, projectedTimeline)
        : timeline.get(prevMonthKey(month.month))?.ownCapital ?? 0;
      const targetIncome = prevOwnCapital > 0 ? (targetRocPct / 100) * prevOwnCapital : 0;
      const days: AnalyticsPlusDayGroup[] = isForecastView
        ? []
        : [...month.days]
            .filter((day) => day.lines.length > 0)
            .sort((a, b) => b.date.localeCompare(a.date))
            .map((day) => {
              const catalog = flattenAnalyticsPlusLines(allMonths);
              return {
                date: day.date,
                total: cashIncome(day),
                lines: day.lines.map((line) => {
                  const plus = toAnalyticsPlusIncomeLine(day.date, line);
                  const related = findRelatedAnalyticsPlusLines(plus, catalog).map((row) => ({
                    ...row,
                    related: undefined,
                  }));
                  return related.length ? { ...plus, related } : plus;
                }),
              };
            });
      const tradeCount = isForecastView ? 0 : countTradesInMonth(month);
      const currentMonthMarginCost =
        Number(ownCapitalByMonth.get(currentMonthKey())?.effectiveMarginCost) || 0;
      const futureUsesCurrentMargin =
        localLedgerAnalytics &&
        month.month > currentMonthKey() &&
        (monthUsesAutomaticMarginForecast(month.month) ||
          expenseEntry.usePredictedMarginCost !== false);
      const effectiveMarginCost = futureUsesCurrentMargin
        ? currentMonthMarginCost
        : localLedgerAnalytics
        ? point?.effectiveMarginCost ?? (Number(expenseEntry.marginCost) || 0)
        : displayEffectiveMarginCost(
            expenseEntry,
            point?.ownCapital ?? 0,
            stockSummary.cost_basis,
            point?.effectiveMarginCost,
            month.month,
          );
      const grossIncome = isForecastView
        ? (point?.income ?? targetIncome)
        : cashIncome(month);
      const income = isForecastView ? grossIncome : grossIncome - effectiveMarginCost;
      const rocPct = prevOwnCapital > 0 ? (income / prevOwnCapital) * 100 : null;
      const perTradeFees = localLedgerAnalytics
        ? 0
        : perTradeExpenseTotal(expenseEntry, tradeCount);
      const monthTaxCashForEom = localLedgerAnalytics
        ? 0
        : taxCashOutflowForMonthKey(month.month);
      const stockLendingIncome = localLedgerAnalytics
        ? Number(point?.stockLendingIncome) || 0
        : 0;
      const plannedOwnCapital = plannedOwnCapitalEom({
        baseCapitalBom: prevOwnCapital,
        targetIncomeNet: targetIncome,
        cashAdded: point?.cashAdded ?? 0,
        withdrawn: point?.withdrawn ?? 0,
        stockLendingIncome,
        perTradeFees,
        taxCashOutflow: monthTaxCashForEom,
      });
      const ownCapitalEom = point?.ownCapital ?? 0;
      monthDetail = {
        key: month.month,
        label: month.label,
        grossIncome,
        income,
        rocPct,
        targetRocPct,
        targetIncome,
        baseCapital: prevOwnCapital,
        ownCapitalEom,
        plannedOwnCapital,
        planDelta: ownCapitalEom - plannedOwnCapital,
        cashAdded: point?.cashAdded ?? 0,
        withdrawn: point?.withdrawn ?? 0,
        stockLendingIncome,
        expenses: expenseTotalForMonth(
          expenseEntry,
          isForecastView ? undefined : month,
          effectiveMarginCost,
        ),
        ownCapitalCorrection: 0,
        marginCost: Number(expenseEntry.marginCost) || 0,
        usePredictedMarginCost: localLedgerAnalytics
          ? monthUsesAutomaticMarginForecast(month.month) ||
            (!isPastAnalyticsMonthKey(month.month) && expenseEntry.usePredictedMarginCost !== false)
          : expenseEntry.usePredictedMarginCost === true,
        marginForecastAutomatic: monthUsesAutomaticMarginForecast(month.month),
        effectiveMarginCost,
        accruedMarginCost: localLedgerAnalytics
          ? Number(expenseEntry.marginCost) || 0
          : factualMarginCostForMonth(expenseEntry, month.month),
        perTradeFee: localLedgerAnalytics ? 0 : Number(expenseEntry.perTradeFee) || 0,
        tradeCount,
        premiums: isForecastView ? 0 : month.premiums,
        stcCredits: isForecastView ? 0 : month.stcCredits,
        assignIncome: isForecastView ? 0 : month.assignIncome,
        stockSellIncome: isForecastView ? 0 : month.stockSellIncome,
        btcCost: isForecastView ? 0 : month.btcCost,
        btoCost: isForecastView ? 0 : month.btoCost,
        days,
        forecastView: isForecastView,
      };
    }
  }

  // The mobile Total Performance card is cumulative, like the other totals in
  // that section (capital, cash, and costs). Month-specific values remain in
  // `monthDetail` below.
  const totalIncomeThroughMonth =
    selectedMonthKey && selectedMonthKey <= currentMonthKey()
      ? selectedMonthKey
      : currentMonthKey();
  const incomeDisplay = isForecastView
    ? null
    : {
        breakdown: cumulativeIncomeBreakdown(historyMonths, totalIncomeThroughMonth),
        scopeLabel:
          selectedYear === "all" && !selectedMonthKey
            ? "Up to current month"
            : `through ${monthLabel(totalIncomeThroughMonth)}`,
      };
  const breakdownScopeLabel = isForecastView && selectedMonthKey
    ? `through ${monthLabel(selectedMonthKey)} incl. forecast`
    : incomeDisplay?.scopeLabel ?? "Up to current month";
  const displayBreakdown: IncomeBreakdown = isForecastView && forecastTotals
    ? {
        premiums: 0,
        stcCredits: 0,
        assignIncome: 0,
        stockSellIncome: 0,
        btcCost: 0,
        btoCost: 0,
        total: forecastTotals.income,
      }
    : incomeDisplay?.breakdown ?? breakdown;

  const snapshot: AnalyticsPlusMobileSnapshot = {
    selectedYear,
    forecastEnabled,
    isForecastView,
    dimStockKpis,
    years,
    months: monthChips,
    selectedMonthKey,
    breakdown,
    displayBreakdown,
    breakdownScopeLabel,
    performanceScopeBadge: analyticsPerformanceScopeBadge(
      selectedMonthKey,
      selectedYear,
      isForecastView,
    ),
    stockSummary,
    ownCapitalEom,
    cashAddedTotal: cashAdded,
    withdrawnTotal: withdrawn,
    expensesTotal: forecastTotals?.expenses.total ?? expenseTotals.total,
    marginCostTotal: forecastTotals?.expenses.margin ?? expenseTotals.margin,
    marginUsed,
    rhDailyInterest,
    rhAnnualRatePct,
    predictedMarginCost: monthUsesAutomaticMarginForecast(currentMonthKey())
      ? forecastMarginCostFromCurrentDaily() ?? 0
      : localLedgerAnalytics
        ? Number(ownCapitalByMonth.get(currentMonthKey())?.effectiveMarginCost) || 0
        : predictedMarginCostForMonth(marginMonthKey, stockSummary.cost_basis, ownCapitalEom),
    monthDetail,
    incomeChartMonths: buildIncomeChartPoints(
      months,
      selectedYear,
      forecastEnabled,
      ownCapitalByMonth,
      ledger,
      stockSummary.cost_basis,
      localLedgerAnalytics,
    ),
  };
  if (localLedgerAnalytics) {
    if (!cacheRevision) cacheRevision = await ownLedgerReadRevision().catch(() => "");
    if (cacheRevision) {
      analyticsSnapshotCache.set(analyticsSnapshotKey(opts, cacheRevision), {
        at: Date.now(),
        snapshot,
      });
    }
  }
  return snapshot;
}

interface FifoShortLot {
  openDate: string;
  qty: number;
  credit: number;
}

interface FifoLongLot {
  openDate: string;
  qty: number;
  debit: number;
}

type FifoReplayCloseReason = "closed" | "expired" | "assigned";

interface FifoReplayCloseEvent {
  closeDate: string;
  pl: number;
  closeReason: FifoReplayCloseReason;
}

export interface TradingIncomeTaxYearData {
  income: number[];
  fifo: number[];
}

function consumeFifoShortLots(
  lots: FifoShortLot[],
  qty: number,
): { openCredit: number } {
  let remaining = qty;
  let openCredit = 0;
  while (remaining > 0 && lots.length > 0) {
    const lot = lots[0]!;
    const take = Math.min(remaining, lot.qty);
    openCredit += (lot.credit / lot.qty) * take;
    remaining -= take;
    lot.qty -= take;
    if (lot.qty <= 0) lots.shift();
  }
  return { openCredit };
}

function consumeFifoLongLots(
  lots: FifoLongLot[],
  qty: number,
): { openDebit: number } {
  let remaining = qty;
  let openDebit = 0;
  while (remaining > 0 && lots.length > 0) {
    const lot = lots[0]!;
    const take = Math.min(remaining, lot.qty);
    openDebit += (lot.debit / lot.qty) * take;
    remaining -= take;
    lot.qty -= take;
    if (lot.qty <= 0) lots.shift();
  }
  return { openDebit };
}

function replayFifoOptionCloseEvents(
  txns: WheelTransaction[],
): FifoReplayCloseEvent[] {
  const positions = new Map<string, { short: FifoShortLot[]; long: FifoLongLot[] }>();
  const events: FifoReplayCloseEvent[] = [];
  const sorted = [...txns].sort((a, b) =>
    String(a.trade_date).localeCompare(String(b.trade_date)),
  );

  for (const txn of sorted) {
    if (txn.instrument_type !== "option") continue;
    const key = contractKey(txn);
    if (!key) continue;
    const qty = Math.abs(Number(txn.quantity) || 0);
    if (qty <= 0) continue;

    const pos = positions.get(key) ?? { short: [], long: [] };
    const date = txn.trade_date ?? "";
    const amount = Math.abs(Number(txn.total_credit) || 0);

    if (isSellToOpenTxn(txn)) {
      pos.short.push({ openDate: date, qty, credit: amount });
    } else if (isBuyToOpenTxn(txn)) {
      pos.long.push({ openDate: date, qty, debit: amount });
    } else if (isSellToCloseTxn(txn)) {
      const { openDebit } = consumeFifoLongLots(pos.long, qty);
      events.push({ closeDate: date, pl: amount - openDebit, closeReason: "closed" });
    } else if (isBuyToCloseTxn(txn)) {
      const { openCredit } = consumeFifoShortLots(pos.short, qty);
      events.push({ closeDate: date, pl: openCredit - amount, closeReason: "closed" });
    } else if (isOptionExpirationTxn(txn) && txn.action === "buy") {
      const { openCredit } = consumeFifoShortLots(pos.short, qty);
      events.push({ closeDate: date, pl: openCredit, closeReason: "expired" });
    } else if (isAssignedOptionTxn(txn) && txn.action === "buy") {
      const { openCredit } = consumeFifoShortLots(pos.short, qty);
      events.push({ closeDate: date, pl: openCredit, closeReason: "assigned" });
    }

    positions.set(key, pos);
  }

  return events;
}

function fifoIncomeFromReplay(txns: WheelTransaction[], year: number): number[] {
  const classified = classifyOptionIncomeTxns(txns);
  const months = Array.from({ length: 12 }, () => 0);
  const yearPrefix = String(year);

  for (const event of replayFifoOptionCloseEvents(classified)) {
    // Native Analytics closed-trade history includes closed + expired only.
    if (event.closeReason === "assigned") continue;
    if (!event.closeDate.startsWith(yearPrefix)) continue;
    const monthIndex = Number(event.closeDate.slice(5, 7)) - 1;
    if (monthIndex < 0 || monthIndex > 11) continue;
    months[monthIndex] += event.pl;
  }

  return months;
}

/** Own-ledger cash months → capital entries (same mapping as Analytics(L)). */
async function fetchOwnLedgerCapitalLedgerForTax(
  force = false,
): Promise<Record<string, OwnCapitalEntry>> {
  const [raw, plans] = await Promise.all([
    loadOwnLedgerCashMonthLedger(force),
    fetchOwnCapitalLedger(force ? { bustCache: true } : undefined),
  ]);
  const months: Record<string, OwnCapitalEntry> = {};
  for (const [month, entry] of Object.entries(raw)) {
    months[month] = normalizeOwnCapitalEntry({
      cashAdded: entry.cashAdded,
      withdrawn: entry.withdrawn,
      taxWithdrawn: entry.taxWithdrawn,
      marginCost: entry.marginCost,
      stockLendingIncome: entry.stockLendingIncome,
    });
  }
  return mergeOwnCapitalTargetRocFromPlans(months, plans);
}

function analyticsTxnInScope(txn: WheelTransaction): boolean {
  const accountId = String(txn.account_id ?? "").trim();
  if (accountId) return scopeAllowsAccount(accountId, txn.broker_id);
  return scopeAllowsTicker(txn.ticker);
}

function analyticsCashInScope(flow: OwnLedgerCashFlow): boolean {
  return scopeAllowsAccount(flow.account_id, flow.broker_id ?? flow.broker);
}

/** Cash totals for the current account scope. Target ROC on the base ledger is kept. */
function scopeOwnCapitalLedger(
  base: Record<string, OwnCapitalEntry>,
  flows: OwnLedgerCashFlow[],
): Record<string, OwnCapitalEntry> {
  const months: Record<string, OwnCapitalEntry> = {};
  for (const [month, entry] of Object.entries(base)) {
    months[month] = {
      ...entry,
      cashAdded: 0,
      withdrawn: 0,
      taxWithdrawn: 0,
      marginCost: 0,
      stockLendingIncome: 0,
    };
  }
  const factThrough = currentMonthKey();
  for (const flow of flows) {
    if (flow.ignored === true || !isFactCashFlow(flow)) continue;
    const date = String(flow.flow_date ?? "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const monthKey = date.slice(0, 7);
    const prev = months[monthKey] ?? normalizeOwnCapitalEntry(undefined);
    const amount = Math.abs(Number(flow.amount) || 0);
    if (!(amount > 0)) continue;
    const category = String(flow.category || "");
    if (category === "tax" && flow.direction === "out") {
      months[monthKey] = {
        ...prev,
        withdrawn: (prev.withdrawn || 0) + amount,
        taxWithdrawn: (prev.taxWithdrawn || 0) + amount,
      };
      continue;
    }
    if (category === "margin_interest" && flow.direction === "out") {
      const ref = String(flow.broker_ref ?? "").trim();
      const marginMonth = /^\d{4}-\d{2}$/.test(ref) ? ref : monthKey;
      if (marginMonth >= factThrough) continue;
      months[monthKey] = { ...prev, marginCost: (prev.marginCost || 0) + amount };
      continue;
    }
    if (category === "stock_lending_interest" && flow.direction === "in") {
      months[monthKey] = { ...prev, stockLendingIncome: (prev.stockLendingIncome || 0) + amount };
      continue;
    }
    if (category === "transfer_in" || category === "transfer_out") continue;
    if (flow.direction === "in") {
      months[monthKey] = { ...prev, cashAdded: (prev.cashAdded || 0) + amount };
    } else {
      months[monthKey] = { ...prev, withdrawn: (prev.withdrawn || 0) + amount };
    }
  }
  return months;
}

/** Monthly trading income for the Tax tab — own ledger fact + target-ROC forecast. */
export async function loadTradingIncomeForTaxYear(year: number): Promise<TradingIncomeTaxYearData> {
  const [loadedTxns, ledger] = await Promise.all([
    loadOwnLedgerTransactionSnapshot(),
    fetchOwnLedgerCapitalLedgerForTax(),
  ]);
  const txns = loadedTxns.filter(analyticsTxnInScope);
  const { months } = buildIncomeCalendar(txns, year);
  const { months: allMonths } = buildIncomeCalendar(txns, null);
  const monthsByKey = new Map(months.map((month) => [month.month, month]));
  const allMonthsByKey = new Map(allMonths.map((month) => [month.month, month]));
  // Do not refresh predicted tax withdrawals here — loadForecastOwnCapitalTaxPlan
  // calls this helper and would recurse. Income projection only needs target ROC.
  const ownCapitalByMonth = computeOwnCapitalTimeline(allMonths, ledger);
  const projectedTimeline = ensureProjectedOwnCapitalTimeline(
    ownCapitalByMonth,
    ledger,
    `${year}-12`,
  );
  const nowYear = currentCalendarYear();
  const nowMonthIndex = new Date().getMonth();
  const fifoFactMonths = fifoIncomeFromReplay(txns, year);

  const income = Array.from({ length: 12 }, (_, monthIndex) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
    const isForecast = year > nowYear || (year === nowYear && monthIndex > nowMonthIndex);

    if (isForecast) {
      return projectedTimeline.get(monthKey)?.income ?? 0;
    }

    const bucket = monthsByKey.get(monthKey) ?? allMonthsByKey.get(monthKey);
    return bucket ? cashIncome(bucket) : 0;
  });

  const fifo = Array.from({ length: 12 }, (_, monthIndex) => {
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
    const isForecast = year > nowYear || (year === nowYear && monthIndex > nowMonthIndex);

    if (isForecast) {
      return projectedTimeline.get(monthKey)?.income ?? 0;
    }

    return fifoFactMonths[monthIndex] ?? 0;
  });

  return { income, fifo };
}

/** Mobile-friendly Analytics+ snapshot built from the same logic as the desktop panel. */
